/**
 * SchedulePage.tsx
 *
 * Resource-based weekly/daily/monthly/overview appointment calendar for medical practice staff.
 * Replaces the legacy schedule.php view.
 *
 * Features:
 *  - 7-column weekly grid (Mon-Sun) with configurable time slots
 *  - Day-view mode (single column) - default on mobile
 *  - Month-view mode (standard calendar month grid)
 *  - Overview mode (appointments grouped by status)
 *  - Prev/Next/Today navigation
 *  - Left sidebar with mini calendar, display settings, and filters
 *  - Appointment cards with status colour coding and an inline check-in button
 *  - Click card -> AppointmentDetailModal
 *  - Click empty slot -> AppointmentCreateModal (start time pre-filled)
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock, Check, SlidersHorizontal, Plus, Filter, X, Search as SearchIcon, Printer, ChevronDown, ChevronUp, Phone, Cake, Hash, Languages, Stethoscope, MapPin, UserCog, StickyNote, NotebookText, Users, Pill, Package, PackageOpen, Award, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import type { MultiSelectOption } from '@/components/ui/MultiSelectDropdown';
import { TagIconBadge } from '@/components/tags/TagIconBadge';
import type { SelectOption } from '@/types/common.types';

import { AppointmentCreateModal } from '@/pages/appointments/AppointmentCreateModal';
import { AppointmentDetailModal } from '@/pages/appointments/AppointmentDetailModal';
import BlockTimeModal from '@/pages/plans/BlockTimeModal';
import { MedalDisc } from '@/components/scheduling/MedalDisc';

import {
  getCalendarAppointments,
  getWaitlistAppointments,
  checkInAppointment,
  checkOutAppointment,
  checkBackAppointment,
  confirmAppointment,
  getCalendarSettings as getSchedulerCalendarSettings,
  getPreviousDayCheck,
  printCalendarRange,
  printWaitlist,
  getSchedulerPrintDocumentOptions,
  printDocumentPacket,
  type AppointmentResource,
  type AppointmentInfoPopup,
  type BlockInfoPopup,
  type PopupMedalTier,
  type PopupPatientTag,
  type PopupMembership,
  type CalendarAppointment,
  type CalendarStatusKey,
  type AppointmentType,
  type CalendarSettings as SchedulerCalendarSettings,
  type PreviousDayCheckResponse,
} from '@/services/appointments.service';
import {
  appointmentsService,
  type Appointment,
  type Resource,
  type BlockToAppointmentSeed,
  type ConflictInfo,
} from '@/services/appointments.service';
import { promptAndSendBookedSmsUpdate } from '@/pages/appointments/promptBookedSmsUpdate';
import {
  buildConflictHeading,
  categorizeConflicts,
  formatResourceConflictLine,
} from '@/lib/appointmentConflicts';
import { listFacilities, getCalendarSettings as getAdminCalendarSettings, type Facility, type CalendarSettings as AdminCalendarSettings, type CalendarInfoFlags } from '@/services/admin.service';
import { useEffectiveFacilityId, useIsFacilityLocked } from '@/hooks/useGlobalFacility';
import {
  applySchedulerPreset,
  createSchedulerPreset,
  deleteSchedulerPreset,
  getSchedulerPreference,
  listSchedulerPresets,
  updateSchedulerPreference,
  updateSchedulerPreset,
  type SchedulerPreference,
  type SchedulerPreferencePayload,
  type SchedulerPrintOptions,
} from '@/services/scheduler-preferences.service';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { useAuth } from '@/hooks/useAuth';
import { usePatientPhoto } from '@/hooks/usePatientPhoto';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import {
  dateInTz,
  minutesSinceMidnightInTz,
  startOfDayInTzIso,
  timeInTz,
  toLocaleDateStringInTz,
  toLocaleTimeStringInTz,
} from '@/lib/facilityTimezone';
import {
  applyMoveToAppointments,
  computeVisibleMinutes,
  dayGridTemplate,
  layoutEvents,
  parseScheduleView,
  popupWidth,
  scheduleDateLabelText,
  withScheduleView,
  type PositionedCalendarItem,
  type ScheduleViewMode,
} from '@/pages/scheduleShared';
import { ScheduleDateLabel } from '@/pages/ScheduleDateLabel';
import { OffCanvasPanel } from '@/components/layout/OffCanvasPanel';
import { useIsBelow } from '@/hooks/useBreakpoint';

/**
 * What `getQueriesData` hands back for the calendar query: one `[key, data]` pair
 * per cached range/facility/resource combination. Held across an optimistic
 * drag so the exact prior state can be restored, not re-derived.
 */
type ScheduleCacheSnapshot = [QueryKey, CalendarAppointment[] | undefined][];
import { useAppointmentDragResize } from '@/hooks/useAppointmentDragResize';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/ui/DatePicker';

// --- Constants ---------------------------------------------------------------

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Vertical density of each scheduler time slot, expressed in rem. Lower
 * values produce a denser grid closer to the legacy `calendar-test.php`
 * Day view. Slot labels, event cards, drag ghost, and the now-line all
 * use percentage positioning inside the column so they scale to whatever
 * value is set here without any further math.
 */
const SLOT_ROW_HEIGHT_REM = 1.5;

interface PresetModalState {
  mode: 'create' | 'edit';
  presetId: number | null;
}

function resourceGroupLabel(resourceType: Resource['resourceType']): string {
  switch (resourceType) {
    case 'doctor':
      return 'Doctors';
    case 'nurse':
      return 'Nurses';
    case 'coordinator':
      return 'Coordinators';
    case 'room':
      return 'Rooms';
    default:
      return 'Equipment / Other';
  }
}

// Fallback labels when no aa_role override is configured. Keys are the singular
// `resources.resourceType` stored in `aa_resources`. Mirrors the map in
// `UnifiedAppointmentModal.tsx` (RESOURCE_TYPE_LABELS).
const RESOURCE_TYPE_LABELS: Record<Resource['resourceType'], string> = {
  doctor: 'Doctors',
  coordinator: 'Coordinators',
  nurse: 'Nurses',
  room: 'Rooms',
  equipment: 'Equipment',
  other: 'Other',
};

// Legacy `calendar-test.php` Schedules sidebar order: Doctors -> Coordinators
// -> Nurses -> Rooms -> Equipment -> Other (lines 7344..7600+).
const SCHEDULE_TYPE_ORDER: ReadonlyArray<Resource['resourceType']> = [
  'doctor',
  'coordinator',
  'nurse',
  'room',
  'equipment',
  'other',
];

function eventResources(appt: CalendarAppointment): AppointmentResource[] {
  if (Array.isArray(appt.resources) && appt.resources.length > 0) {
    return appt.resources;
  }

  if (appt.resourceID == null) {
    return [];
  }

  return [{
    resourceId: appt.resourceID,
    resourceName: appt.resourceName ?? 'Resource',
    resourceType: 'equipment',
  }];
}

function eventHasResource(appt: CalendarAppointment, resourceId: number): boolean {
  return eventResources(appt).some((resource) => resource.resourceId === resourceId);
}

/**
 * Block-vs-column visibility per legacy `scheduler-display-week-cell.php`
 * 542-595: a block with empty resources renders in EVERY column (legacy
 * `getResources() == ""` short-circuits `displayRecord = 1`); a block that
 * carries one or more resources renders ONLY in columns whose resourceID
 * matches one of those resources. Non-block events return false here and
 * are handled by `eventHasResource` at the call site.
 */
function isBlockVisibleInColumn(appt: CalendarAppointment, columnResourceId: number): boolean {
  if (!appt.isBlock) return false;
  const resources = eventResources(appt);
  if (resources.length === 0) return true;
  return resources.some((resource) => resource.resourceId === columnResourceId);
}

/**
 * Inline totals strip. Legacy week/day headers show appt count, hours, and
 * (for user_level > 2) a quote sum under each column. Each piece is
 * independently gated by its preference toggle. See
 * scheduler-display-week-cell.php 1358-1366.
 */
function TotalsStrip({
  totals,
  showCount,
  showHours,
  showQuote,
  compact,
}: {
  totals: { count: number; hours: number; quote: number };
  showCount: boolean;
  showHours: boolean;
  showQuote: boolean;
  compact?: boolean;
}) {
  if (!showCount && !showHours && !showQuote) return null;
  if (totals.count === 0 && totals.hours === 0 && totals.quote === 0) return null;
  const base = compact ? 'text-[9px]' : 'text-[10px]';
  return (
    <div className={cn(base, 'flex justify-center gap-1 text-slate-500 leading-tight')}>
      {showCount && (
        <span title="Appointment count">{totals.count}</span>
      )}
      {showHours && (
        <span title="Total hours">{totals.hours.toFixed(2)}h</span>
      )}
      {showQuote && (
        <span title="Total quote">${Math.round(totals.quote).toLocaleString()}</span>
      )}
    </div>
  );
}

/**
 * Compute legacy per-column totals (count / hours / quote) for an array of
 * appointments. Blocks are excluded to mirror
 * scheduler-display-week-cell.php 908-910 which only tallies real
 * appointments.
 */
function computeColumnTotals(appointments: CalendarAppointment[]): {
  count: number;
  hours: number;
  quote: number;
} {
  let count = 0;
  let minutes = 0;
  let quote = 0;
  for (const appt of appointments) {
    if (appt.isBlock) continue;
    count += 1;
    if (typeof appt.durationMinutes === 'number' && appt.durationMinutes > 0) {
      minutes += appt.durationMinutes;
    }
    if (typeof appt.quote === 'number' && Number.isFinite(appt.quote)) {
      quote += appt.quote;
    }
  }
  return { count, hours: Math.round((minutes / 60) * 100) / 100, quote };
}

function preferencePayloadFromState(state: {
  viewMode: 'week' | 'day' | 'month' | 'overview';
  displayMode: string;
  showHours: number;
  startHour: number;
  slotMinutes: number;
  showWeekends: boolean;
  showCancelled: boolean;
  showNoShows: boolean;
  showNoResource: boolean;
  showInfoPopups: boolean;
  showBlockInfoPopups: boolean;
  privacyMode: boolean;
  blockDisplayStyle: 'overlay' | 'side-by-side';
  selectedScheduleIds: number[];
  selectedFilterResourceIds: number[];
  selectedFacilityIds: number[];
  selectedTypeIds: number[];
  printOptions?: SchedulerPrintOptions;
  apptCountToggle?: boolean;
  apptHoursToggle?: boolean;
  apptQuoteToggle?: boolean;
  overviewOpenState?: {
    scheduled: boolean; contactAttempted: boolean; confirmed: boolean; checkedIn: boolean;
    checkedBack: boolean; checkedOut: boolean; cancelled: boolean; noShow: boolean; waitList: boolean;
  };
}): SchedulerPreferencePayload {
  return {
    viewMode: state.viewMode,
    displayMode: state.displayMode === 'appts' || state.displayMode === 'blocks' ? state.displayMode : 'both',
    showHours: state.showHours,
    startHour: state.startHour,
    slotMinutes: state.slotMinutes,
    showWeekends: state.showWeekends,
    showCancelled: state.showCancelled,
    showNoShows: state.showNoShows,
    showNoResource: state.showNoResource,
    showInfoPopups: state.showInfoPopups,
    showBlockInfoPopups: state.showBlockInfoPopups,
    privacyMode: state.privacyMode,
    blockDisplayStyle: state.blockDisplayStyle,
    scheduleResourceIds: state.selectedScheduleIds,
    filterResourceIds: state.selectedFilterResourceIds,
    facilityIds: state.selectedFacilityIds,
    appointmentTypeIds: state.selectedTypeIds,
    printOptions: state.printOptions,
    apptCountToggle: state.apptCountToggle,
    apptHoursToggle: state.apptHoursToggle,
    apptQuoteToggle: state.apptQuoteToggle,
    overviewScheduledOpen: state.overviewOpenState?.scheduled,
    overviewContactAttemptedOpen: state.overviewOpenState?.contactAttempted,
    overviewConfirmedOpen: state.overviewOpenState?.confirmed,
    overviewCheckedInOpen: state.overviewOpenState?.checkedIn,
    overviewCheckedBackOpen: state.overviewOpenState?.checkedBack,
    overviewCheckedOutOpen: state.overviewOpenState?.checkedOut,
    overviewCanceledOpen: state.overviewOpenState?.cancelled,
    overviewNoShowOpen: state.overviewOpenState?.noShow,
    overviewWaitingListOpen: state.overviewOpenState?.waitList,
  };
}

function preferencePayloadFromPreference(preference: SchedulerPreference): SchedulerPreferencePayload {
  return {
    name: preference.name,
    shared: preference.shared,
    viewMode: preference.viewMode,
    displayMode: preference.displayMode,
    showHours: preference.showHours,
    startHour: preference.startHour,
    slotMinutes: preference.slotMinutes,
    showWeekends: preference.showWeekends,
    showCancelled: preference.showCancelled,
    showNoShows: preference.showNoShows,
    showNoResource: preference.showNoResource,
    showInfoPopups: preference.showInfoPopups,
    showBlockInfoPopups: preference.showBlockInfoPopups,
    privacyMode: preference.privacyMode,
    blockDisplayStyle: preference.blockDisplayStyle,
    scheduleResourceIds: preference.scheduleResourceIds,
    filterResourceIds: preference.filterResourceIds,
    facilityIds: preference.facilityIds,
    appointmentTypeIds: preference.appointmentTypeIds,
    overviewScheduledOpen: preference.overviewScheduledOpen,
    overviewContactAttemptedOpen: preference.overviewContactAttemptedOpen,
    overviewConfirmedOpen: preference.overviewConfirmedOpen,
    overviewCheckedInOpen: preference.overviewCheckedInOpen,
    overviewCheckedBackOpen: preference.overviewCheckedBackOpen,
    overviewCheckedOutOpen: preference.overviewCheckedOutOpen,
    overviewCanceledOpen: preference.overviewCanceledOpen,
    overviewNoShowOpen: preference.overviewNoShowOpen,
    overviewWaitingListOpen: preference.overviewWaitingListOpen,
    printOptions: preference.printOptions,
    apptCountToggle: preference.apptCountToggle,
    apptHoursToggle: preference.apptHoursToggle,
    apptQuoteToggle: preference.apptQuoteToggle,
  };
}

// --- Display Settings Option Lists -------------------------------------------

const DISPLAY_MODE_OPTIONS: SelectOption<string>[] = [
  { value: 'both', label: 'Appointments & Blocks' },
  { value: 'appts', label: 'Appointments Only' },
  { value: 'blocks', label: 'Blocks Only' },
];

const SHOW_HOURS_OPTIONS: SelectOption<number>[] = [
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: 'Rest of day' },
];

const START_HOUR_OPTIONS: SelectOption<number>[] = Array.from({ length: 8 }, (_, i) => {
  const h = i + 5; // 5 AM to 12 PM
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return { value: h, label: `${hour12}:00 ${period}` };
});

const SLOT_MINUTES_OPTIONS: SelectOption<number>[] = [
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '60 min' },
];

const BLOCK_DISPLAY_STYLE_OPTIONS: SelectOption<'overlay' | 'side-by-side'>[] = [
  { value: 'overlay', label: 'Overlay Blocks' },
  { value: 'side-by-side', label: 'Side-by-Side Blocks' },
];

/**
 * Legacy print-option metadata — 21 independent toggles from
 * `calendar-test.php` 6054-6165. Each `printOptionN` is a boolean flag with a
 * named DOM id; they combine freely and are NOT a layout enum. `group`
 * controls the legend we render them under.
 */
interface PrintOptionMeta {
  index: number;
  key: keyof SchedulerPrintOptions;
  legacyId: string;
  label: string;
  hint?: string;
  group: 'identity' | 'appointment' | 'medical' | 'layout';
}

const PRINT_OPTION_META: PrintOptionMeta[] = [
  { index: 1,  key: 'printOption1',  legacyId: 'patientNameSelect',          label: 'Patient Name',         group: 'identity' },
  { index: 2,  key: 'printOption2',  legacyId: 'patientIDSelect',            label: 'Patient ID',           group: 'identity' },
  { index: 3,  key: 'printOption3',  legacyId: 'chartIDSelect',              label: 'Chart ID',             group: 'identity' },
  { index: 4,  key: 'printOption4',  legacyId: 'planIDSelect',               label: 'Plan ID',              group: 'identity' },
  { index: 5,  key: 'printOption5',  legacyId: 'phoneSelect',                label: 'Phone',                group: 'identity' },
  { index: 11, key: 'printOption11', legacyId: 'birthDateSelect',            label: 'Birth Date',           group: 'identity' },
  { index: 6,  key: 'printOption6',  legacyId: 'appointmentNameSelect',      label: 'Appointment Name',     group: 'appointment' },
  { index: 7,  key: 'printOption7',  legacyId: 'appointmentTypeSelect',      label: 'Appointment Type',     group: 'appointment' },
  { index: 8,  key: 'printOption8',  legacyId: 'facilityNameSelect',         label: 'Facility Name',        group: 'appointment' },
  { index: 9,  key: 'printOption9',  legacyId: 'apptNotesSelect',            label: 'Appt Notes',           group: 'appointment' },
  { index: 10, key: 'printOption10', legacyId: 'resourceDisplayListSelect',  label: 'Resources',            group: 'appointment' },
  { index: 12, key: 'printOption12', legacyId: 'heightWeightSelect',         label: 'Height / Weight',      group: 'medical' },
  { index: 13, key: 'printOption13', legacyId: 'bmiSelect',                  label: 'BMI',                  group: 'medical' },
  { index: 17, key: 'printOption17', legacyId: 'allergiesSelect',            label: 'Allergies',            group: 'medical' },
  { index: 18, key: 'printOption18', legacyId: 'languageSelect',             label: 'Language',             group: 'medical' },
  { index: 19, key: 'printOption19', legacyId: 'proceduresSelect',           label: 'Procedures',           group: 'medical' },
  { index: 20, key: 'printOption20', legacyId: 'implantsSelect',             label: 'Implants',             group: 'medical' },
  { index: 21, key: 'printOption21', legacyId: 'labChecklistSelect',         label: 'Lab Checklist',        group: 'medical' },
  { index: 14, key: 'printOption14', legacyId: 'appliedFiltersSelect',       label: 'Applied Filters',      hint: 'Print the filter summary on the first page.', group: 'layout' },
  { index: 15, key: 'printOption15', legacyId: 'patientDataPages',           label: 'Patient Data Pages',   hint: 'Append per-patient data pages after the schedule.', group: 'layout' },
  { index: 16, key: 'printOption16', legacyId: 'printOrientation',           label: 'Landscape Orientation', hint: 'Legacy printOption16 == 1 when printOrientation=1.', group: 'layout' },
];

const PRINT_OPTION_GROUP_LABELS: Record<PrintOptionMeta['group'], string> = {
  identity: 'Patient Identity',
  appointment: 'Appointment Detail',
  medical: 'Medical / Clinical',
  layout: 'Layout',
};

// --- Date helpers ------------------------------------------------------------

/** Returns the Monday of the week containing `date`. */
function getWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Add `days` to a Date, returning a new Date. */
function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Format a Date as YYYY-MM-DD */
function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Format a time string like "07:30" as "7:30 AM" */
function formatTimeLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

/** Get all days in a month as an array, padded with nulls for the calendar grid.
 *  CT-07 (P2): weekStart configurable (0=Sunday, 1=Monday). Legacy mini
 *  calendar in `calendar-test.php` starts weeks on Sunday, so default to 0. */
function getMonthGrid(year: number, month: number, weekStart: 0 | 1 = 1): Array<Date | null> {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay(); // 0=Sun
  const startOffset = weekStart === 0
    ? startDow
    : (startDow === 0 ? 6 : startDow - 1);

  const cells: Array<Date | null> = [];

  for (let i = 0; i < startOffset; i++) cells.push(null);

  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, month, d));
  }

  while (cells.length % 7 !== 0) cells.push(null);

  return cells;
}

/**
 * Resolve the canonical status key. Prefers the server-provided `statusKey`
 * from AppointmentPresenterService::status() so the UI and the API agree on
 * legacy precedence (cancelled > no_show > completed > checked_back >
 * waiting_list > confirmed > contact_attempted > scheduled). Falls back to
 * local derivation only if the API hasn't been upgraded.
 */
function resolveStatusKey(appt: CalendarAppointment): CalendarStatusKey {
  if (appt.statusKey) return appt.statusKey;
  if (appt.cancelled) return 'cancelled';
  if (appt.noShow) return 'no_show';
  if (appt.checkOutTime) return 'completed';
  if (appt.checkBackTime) return 'checked_back';
  if (appt.waitList) return 'waiting_list';
  if (appt.confirmed) return 'confirmed';
  if (appt.contactAttempted || appt.contactAttemptedAt) return 'contact_attempted';
  return 'scheduled';
}

const STATUS_LABELS: Record<CalendarStatusKey | 'checked_in', string> = {
  cancelled: 'Cancelled',
  no_show: 'No Show',
  completed: 'Checked Out',
  checked_back: 'Checked Back',
  waiting_list: 'Waiting List',
  confirmed: 'Confirmed',
  contact_attempted: 'Contact Attempted',
  scheduled: 'Scheduled',
  checked_in: 'Checked In',
};

/**
 * Legacy grid used a separate "Checked In" bucket between Confirmed and
 * Checked Back; the API's canonical `status()` collapses it into either
 * `checked_back` or `completed`. Re-derive "Checked In" from the flag so the
 * overview and grid copy match legacy exactly.
 */
function getApptStatus(appt: CalendarAppointment): string {
  const key = resolveStatusKey(appt);
  if ((key === 'scheduled' || key === 'contact_attempted') && appt.checkInTime && !appt.checkBackTime && !appt.checkOutTime) {
    return STATUS_LABELS.checked_in;
  }
  if (key === 'confirmed' && appt.checkInTime) {
    return STATUS_LABELS.checked_in;
  }
  return STATUS_LABELS[key];
}

// --- Card colour helpers -----------------------------------------------------

/**
 * Halve each RGB channel of a 6-digit hex color. Direct port of legacy
 * `darkerHtmlColor()` in `scheduler_display_week_cell_tz.php` 263-282
 * (and twin copies in `scheduler-display-week-cellorg.php`,
 * `scheduler_display_week_cell_tz2.php`, etc.). Used by the cell gradient:
 * top = darker variant of the type color, bottom = the type color itself.
 * Falls back to the input string when the input isn't a parseable hex.
 */
function darkenHexColor(hex: string | null | undefined): string {
  if (!hex) return '#666666';
  const cleaned = hex.replace(/^#/, '');
  if (cleaned.length !== 6) return hex;
  const out: string[] = ['', '', ''];
  for (let i = 0; i < 3; i++) {
    const piece = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(piece)) return hex;
    out[i] = Math.floor(piece / 2).toString(16).padStart(2, '0');
  }
  return `#${out.join('')}`;
}

function cardBgClass(appt: CalendarAppointment): string {
  const key = resolveStatusKey(appt);
  if (key === 'cancelled') return 'bg-slate-50 border-slate-200 opacity-60';
  if (key === 'no_show') return 'bg-red-50 border-red-200';
  if (key === 'completed') return 'bg-slate-50 border-slate-200';
  if (key === 'checked_back') return 'bg-blue-50 border-blue-200';
  if (appt.checkInTime) return 'bg-yellow-50 border-yellow-200';
  if (key === 'confirmed') return 'bg-green-50 border-green-200';
  if (key === 'contact_attempted') return 'bg-amber-50 border-amber-200';
  return 'bg-white border-slate-200';
}

function statusBadge(appt: CalendarAppointment): { label: string; cls: string } | null {
  const key = resolveStatusKey(appt);
  switch (key) {
    case 'no_show':         return { label: 'No Show',          cls: 'bg-red-100 text-red-700' };
    case 'cancelled':       return { label: 'Cancelled',        cls: 'bg-slate-200 text-slate-600' };
    case 'completed':       return { label: 'Checked Out',      cls: 'bg-slate-200 text-slate-600' };
    case 'checked_back':    return { label: 'Checked Back',     cls: 'bg-blue-100 text-blue-700' };
    case 'waiting_list':    return { label: 'Wait List',        cls: 'bg-slate-100 text-slate-600' };
    case 'contact_attempted': return { label: 'Contact Attempted', cls: 'bg-amber-100 text-amber-700' };
    case 'confirmed':       return appt.checkInTime
      ? { label: 'Checked In', cls: 'bg-yellow-100 text-yellow-700' }
      : { label: 'Confirmed',  cls: 'bg-green-100 text-green-700' };
    case 'scheduled':
      if (appt.checkInTime) return { label: 'Checked In', cls: 'bg-yellow-100 text-yellow-700' };
      return null;
  }
  return null;
}

// --- Info popover helpers ----------------------------------------------------

/**
 * Format a wait-time minute count as "Xm" or "Xh Ym" so the popup matches
 * legacy `scheduler-appt-info-popup.php` `Checked In Wait` / `Checked Back
 * Wait` row labels.
 */
function formatWait(minutes: number | null | undefined): string | null {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return null;
  const m = Math.round(minutes);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/**
 * Compact key/value row used inside the popup body. Renders nothing when
 * the value is null / empty so the legacy "section is invisible when its
 * `aa_accounts_calendar.infoXxx` flag is off" behavior is preserved at the
 * UI layer too.
 */
function PopupRow({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <div className="flex gap-2">
      <span className="text-slate-400 shrink-0 min-w-[6.5rem]">{label}</span>
      <span className="text-slate-700 break-words">{children}</span>
    </div>
  );
}

/**
 * Custom patient tag badge. Streams the icon image through the v5
 * `GET /tags-custom/{id}/icon` proxy (which now reads from the EFS
 * `tag_icons` mount) via `useTagIcon`, with a hover tooltip showing
 * the tag title and per-patient description. Mirrors the legacy
 * scheduler-appt-info-popup.php icon + qTip behavior.
 */
function CustomTagBadge({ tag }: { tag: PopupPatientTag }) {
  return (
    <TagIconBadge
      variant="icon"
      tag={{
        tagsCustomID: tag.id,
        title: tag.label,
        description: tag.tooltip ?? '',
        icon: tag.iconUrl ?? '',
        createDate: tag.createDate ?? null,
        addedByName: tag.addedByName ?? null,
      }}
    />
  );
}

/**
 * Popover header: avatar (or fallback monogram) on the left, patient
 * name + medal + status pill on the right, with a small subtitle for
 * the appointment type / duration. The dollar quote badge rides the
 * top-right corner.
 */
function PopupHeader({
  name,
  photoUrl,
  statusLabel,
  apptType,
  durationMinutes,
  quote,
  medalTier,
  medalTooltip,
}: {
  name: string;
  photoUrl: string | null;
  statusLabel: string | null;
  apptType: string | null;
  durationMinutes: number | null;
  quote: number | null;
  medalTier: PopupMedalTier | null;
  medalTooltip: string | null;
}) {
  const monogram = (name || '?').trim().charAt(0).toUpperCase() || '?';
  const subtitleBits: string[] = [];
  if (apptType) subtitleBits.push(apptType);
  if (typeof durationMinutes === 'number' && durationMinutes > 0) subtitleBits.push(`${durationMinutes} min`);
  const subtitle = subtitleBits.join(' · ');
  return (
    <div className="flex items-start gap-2.5">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
          loading="lazy"
        />
      ) : (
        <span
          className="h-9 w-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[14px] font-semibold shrink-0"
          aria-hidden="true"
        >
          {monogram}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-semibold text-[13px] text-slate-900 truncate">{name}</span>
          {medalTier && <MedalDisc tier={medalTier} tooltip={medalTooltip} />}
          {statusLabel && (
            <span className="inline-block rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">
              {statusLabel}
            </span>
          )}
          {typeof quote === 'number' && quote > 0 && (
            <span className="ml-auto inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
              ${quote.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          )}
        </div>
        {subtitle && <div className="text-[11px] text-slate-500 truncate mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

/**
 * Compact icon-prefixed row. Auto-hides when `value` is empty so the
 * same null-guard as {@link PopupRow} applies. `label` is optional —
 * omit for "iconified value" rows (e.g. the patient identity strip),
 * include for label/value rows (e.g. the plan block).
 */
function PopupIconRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label?: string;
  // `boolean` is in the union because the guard below handles `false` and the
  // legacy payload sends it for an unset flag. The type said string | number
  // | null, which made `value === false` a comparison with no overlap —
  // tsc was right that one of the two was wrong, and it is the type.
  value?: string | number | boolean | null;
}) {
  if (value == null || value === '' || value === false) return null;
  return (
    <div className="flex items-center gap-2 text-slate-700">
      <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      {label && <span className="text-slate-400 text-[11px] shrink-0">{label}</span>}
      <span className="break-words truncate">{value}</span>
    </div>
  );
}

/**
 * Section header — small uppercase label with an icon. Used to lead
 * Resources / Procedures / Products and similar lists.
 */
function PopupSectionHeader({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 text-slate-500 uppercase tracking-wide text-[10px] font-semibold mb-1">
      <Icon className="h-3 w-3" />
      <span>{children}</span>
    </div>
  );
}

/**
 * Card for a single membership / package. Header bar with kind icon
 * and title; body lists active procedures and products. Declined
 * memberships flip to a soft-red palette and surface a "Payment
 * Declined" pill (legacy `(Payment Declined)` text variant).
 */
function MembershipPanel({
  kind,
  icon: Icon,
  data,
}: {
  kind: 'Membership' | 'Package';
  icon: React.ComponentType<{ className?: string }>;
  data: PopupMembership;
}) {
  return (
    <div
      className={cn(
        'rounded-md border p-2',
        data.declined ? 'border-rose-200 bg-rose-50/50' : 'border-indigo-200 bg-indigo-50/40',
      )}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', data.declined ? 'text-rose-600' : 'text-indigo-600')} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                'text-[10px] uppercase tracking-wide font-semibold',
                data.declined ? 'text-rose-700' : 'text-indigo-700',
              )}
            >
              {kind}
            </span>
            {data.declined && (
              <span className="inline-block rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold text-rose-700 uppercase tracking-wide">
                Payment Declined
              </span>
            )}
          </div>
          <div className="text-[12px] font-medium text-slate-900 truncate">{data.title || 'Untitled'}</div>
        </div>
      </div>
      {data.procedures.length > 0 && (
        <ul className="pl-1 space-y-0.5 mb-1">
          {data.procedures.map((p, i) => (
            <li key={`mp-proc-${i}`} className="text-slate-700 text-[11px]">
              <span className="text-slate-400">·</span> {p.name}
            </li>
          ))}
        </ul>
      )}
      {data.products.length > 0 && (
        <ul className="pl-1 space-y-0.5">
          {data.products.map((p, i) => (
            <li key={`mp-prod-${i}`} className="text-slate-700 text-[11px]">
              <span className="text-slate-400">·</span> {p.name}
              {p.quantity != null && p.quantity > 1 ? ` (X ${p.quantity})` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ApptInfoPopoverProps {
  /** Either an appointment popup payload or a block popup payload. */
  data: AppointmentInfoPopup | BlockInfoPopup | null;
  loading: boolean;
  /** True when the API fetch errored. Renders an inline failure note so
   *  the user knows the load didn't succeed (legacy qTip silently stuck
   *  at "Loading..."). */
  error?: boolean;
  isBlock: boolean;
  privacyMode: boolean;
  /** Logged-in user level — gates the dollar-quote badge to the legacy
   *  `user_level > 2` rule (scheduler-appt-info-popup.php quote section). */
  userLevel: number;
  /** Display title for the popover header (patient name, block name). */
  headerTitle: string;
  /** Baseline info already on the calendar row. Rendered when the
   *  `/info-popup` payload is missing or stripped (e.g. permissions, API
   *  error) so the popup is never just a lone title. The legacy popup
   *  always had at least Status / Type / Facility — this guarantees
   *  parity even when the rich payload doesn't load. */
  fallback?: {
    status?: string | null;
    appointmentTypeName?: string | null;
    facilityName?: string | null;
    resourceNames?: string[] | null;
    notes?: string | null;
    durationMinutes?: number | null;
    startLabel?: string | null;
    endLabel?: string | null;
  };
}

/**
 * Structured rich-popup body for the appointment / block hover. Replaces
 * the old generic `Object.entries(popupData).slice(0, 14)` dump with
 * legacy-parity sections (header strip, patient identity, status / wait,
 * appointment info, notes, people, procedures), conditional on each
 * field's presence on the typed payload.
 *
 * Privacy-mode masks patient-derived rows (photo, dob, phone, chartId,
 * language, patientName, patientNotes, tags) — mirrors legacy
 * `scheduler-appt-info-popup.php` `if ($privacyModeToggle == "1")`
 * branches at lines 576 / 736 / 962. Non-patient rows render normally.
 */
function ApptInfoPopover({ data, loading, error, isBlock, privacyMode, userLevel, headerTitle, fallback }: ApptInfoPopoverProps) {
  // Patient photo: API sends `photoUrl` as a relative `/patients/{id}/photo` route. Plain
  // `<img src>` can't carry the auth token, so we pipe the patient id through
  // `usePatientPhoto` (the canonical hook used by PatientAvatar) which authenticates the
  // request and returns a cached blob URL.
  //
  // Called here, above every early return, with a possibly-null id. It used to sit further
  // down beside its only consumer, where the claim that "the hook is always called" was
  // simply untrue: the loading and block branches return before reaching it. Since it is a
  // useQuery, the same popover instance that renders `loading` and then re-renders with
  // data would gain a hook mid-life and throw React #310.
  const apptForPhoto = !isBlock && data ? (data as AppointmentInfoPopup) : null;
  const patientPhotoSrc = usePatientPhoto(
    apptForPhoto?.patientId ?? null,
    apptForPhoto?.photoUrl ?? null,
  );

  if (loading) {
    return (
      <div className="space-y-1">
        <div className="font-semibold text-sm text-slate-900 mb-1">{headerTitle}</div>
        <div className="text-slate-400">Loading…</div>
      </div>
    );
  }
  // Empty state: render baseline info from the calendar row so the popup
  // is never just a lone title. Mirrors legacy popup which always had at
  // least Status / Type / Facility lines visible.
  if (!data) {
    const fb = fallback ?? {};
    const hasFallback = !!(fb.status || fb.appointmentTypeName || fb.facilityName
      || (fb.resourceNames && fb.resourceNames.length > 0) || fb.notes || fb.startLabel);
    const fbStartEnd = (fb.startLabel && fb.endLabel) ? `${fb.startLabel} - ${fb.endLabel}` : null;
    const fbDuration = typeof fb.durationMinutes === 'number' && fb.durationMinutes > 0
      ? `${fb.durationMinutes} min`
      : null;
    return (
      <div className="space-y-3 text-[12px] leading-snug">
        <PopupHeader
          name={headerTitle}
          photoUrl={null}
          statusLabel={fb.status ?? null}
          apptType={fb.appointmentTypeName ?? null}
          durationMinutes={fb.durationMinutes ?? null}
          quote={null}
          medalTier={null}
          medalTooltip={null}
        />
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700 text-[10px]">
            Couldn't load full details. Showing basic info below.
          </div>
        )}
        {hasFallback ? (
          <>
            {fbStartEnd && (
              <div className="flex items-center gap-2 text-slate-700">
                <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span>{fbStartEnd}</span>
                {fbDuration && <span className="text-slate-400">· {fbDuration}</span>}
              </div>
            )}
            {(fb.appointmentTypeName || fb.facilityName) && (
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-1">
                <PopupIconRow icon={Sparkles} label="Type" value={fb.appointmentTypeName} />
                <PopupIconRow icon={MapPin} label="Facility" value={fb.facilityName} />
              </div>
            )}
            {fb.resourceNames && fb.resourceNames.length > 0 && (
              <div>
                <PopupSectionHeader icon={Users}>
                  People / Resources ({fb.resourceNames.length})
                </PopupSectionHeader>
                <ul className="pl-1 space-y-0.5">
                  {fb.resourceNames.map((name, idx) => (
                    <li key={`fb-res-${idx}`} className="text-slate-700">
                      <span className="text-slate-400">·</span> {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fb.notes && (
              <div className="flex gap-2">
                <StickyNote className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-slate-700 italic break-words">{fb.notes}</div>
              </div>
            )}
          </>
        ) : (
          !error && <div className="text-slate-400 text-[10px]">No additional details.</div>
        )}
      </div>
    );
  }

  if (isBlock) {
    const block = data as BlockInfoPopup;
    const showName = block.name && block.name.trim() !== '' && block.name.trim() !== 'Untitled Appointment';
    return (
      <div className="space-y-1">
        <div className="font-semibold text-sm text-slate-900 mb-1">
          {showName ? block.name : headerTitle}
        </div>
        <div className="flex flex-wrap gap-1 mb-1">
          <span
            className={cn(
              'inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-none',
              block.available ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700',
            )}
          >
            {block.available ? 'Available' : 'Unavailable'}
          </span>
          <span
            className={cn(
              'inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-none',
              block.required ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600',
            )}
          >
            {block.required ? 'Required' : 'Optional'}
          </span>
        </div>
        <PopupRow label="Type">{block.appointmentType}</PopupRow>
        <PopupRow label="Facility">{block.facility}</PopupRow>
        <PopupRow label="Notes">{block.notes}</PopupRow>
        {block.resources && block.resources.length > 0 && (
          <div>
            <div className="text-slate-400 mb-0.5">People / Resources ({block.resources.length})</div>
            <ul className="pl-3 list-disc">
              {block.resources.map((r) => (
                <li key={`block-res-${r.resourceId}`} className="text-slate-700 italic">
                  {r.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const apptData = data as AppointmentInfoPopup;
  const fb = fallback ?? {};
  // `patientPhotoSrc` comes from the hook at the top of this component — see the note
  // there for why it cannot live down here next to its consumer.
  // Coalesce API fields with the calendar-row fallback so the popup body
  // is never sparse — even if `aa_accounts_calendar` flags suppress some
  // info-popup fields (e.g. an account turned off `infoApptType`), the
  // basic Type / Facility / Status lines always render from the row.
  const status = apptData.status || fb.status || null;
  const apptTypeName = apptData.apptType || fb.appointmentTypeName || null;
  const facilityName = apptData.facility || fb.facilityName || null;
  const apptNotes = apptData.apptNotes || fb.notes || null;
  const apptName = apptData.name && apptData.name.trim() !== '' && apptData.name.trim() !== 'Untitled Appointment'
    ? apptData.name : null;
  const resourceLines: { name: string; key: string }[] = (apptData.resources && apptData.resources.length > 0)
    ? apptData.resources.map((r, i) => ({ name: r.name, key: `api-${r.resourceId}-${i}` }))
    : (fb.resourceNames ?? []).map((n, i) => ({ name: n, key: `fb-${i}` }));
  const startEnd = (fb.startLabel && fb.endLabel) ? `${fb.startLabel} - ${fb.endLabel}` : null;
  const durationMinutes = (typeof apptData.duration === 'number' && apptData.duration > 0)
    ? apptData.duration
    : (fb.durationMinutes ?? null);

  // Legacy `scheduler-appt-info-popup.php`: quote badge gated to
  // `user_level > 2`; duration always; tags only when not in privacy mode.
  const showQuote = userLevel > 2 && typeof apptData.quote === 'number' && apptData.quote > 0;
  const allTags = !privacyMode ? apptData.tags ?? [] : [];
  const medalTag = allTags.find((t) => t.medalTier) ?? null;
  const customTags = allTags.filter((t) => !t.medalTier);
  const displayName = apptData.patientName?.trim() || headerTitle;
  const checkedInWait = formatWait(apptData.checkedInWaitMinutes);
  const checkedBackWait = formatWait(apptData.checkedBackWaitMinutes);
  const showPlanCard = !!(apptData.planStageName || apptData.doctorName || apptData.coordinatorName || facilityName);
  const showNotesBlock = !!(apptNotes || (!privacyMode && apptData.patientNotes));
  const showIdentityRow = !privacyMode
    && !!(apptData.dob || apptData.phone || apptData.chartId || apptData.language);

  return (
    <div className="space-y-3 text-[12px] leading-snug">
      <PopupHeader
        name={displayName}
        photoUrl={!privacyMode ? patientPhotoSrc ?? null : null}
        statusLabel={status}
        apptType={apptTypeName}
        durationMinutes={durationMinutes}
        quote={showQuote ? apptData.quote ?? null : null}
        medalTier={medalTag?.medalTier ?? null}
        medalTooltip={medalTag?.tooltip ?? null}
      />

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700 text-[10px]">
          Couldn't load full details. Showing basic info below.
        </div>
      )}

      {status && (
        <div className="flex items-center gap-2 text-slate-700">
          <span className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full bg-slate-400 shrink-0">
            <Check className="h-2.5 w-2.5 text-white" />
          </span>
          <span className="font-medium">{status}</span>
        </div>
      )}

      {startEnd && (
        <div className="flex items-center gap-2 text-slate-700">
          <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span>{startEnd}</span>
        </div>
      )}

      {showIdentityRow && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <PopupIconRow icon={Cake} value={apptData.dob} />
          <PopupIconRow icon={Phone} value={apptData.phone} />
          <PopupIconRow icon={Hash} value={apptData.chartId} />
          <PopupIconRow icon={Languages} value={apptData.language} />
        </div>
      )}

      {customTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {customTags.map((tag) => (
            <CustomTagBadge key={`tag-${tag.id}`} tag={tag} />
          ))}
        </div>
      )}

      {(checkedInWait || checkedBackWait) && (
        <div className="flex flex-wrap gap-1.5">
          {checkedInWait && (
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">
              <Clock className="h-3 w-3" />
              Checked in {checkedInWait} ago
            </span>
          )}
          {checkedBackWait && (
            <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">
              <Clock className="h-3 w-3" />
              Checked back {checkedBackWait} ago
            </span>
          )}
        </div>
      )}

      {showPlanCard && (
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-1">
          <PopupIconRow icon={Sparkles} label="Stage" value={apptData.planStageName} />
          <PopupIconRow icon={Stethoscope} label="Doctor" value={apptData.doctorName} />
          <PopupIconRow icon={UserCog} label="Coordinator" value={apptData.coordinatorName} />
          <PopupIconRow icon={MapPin} label="Facility" value={facilityName} />
        </div>
      )}

      {apptName && <PopupIconRow icon={Sparkles} label="Name" value={apptName} />}

      {showNotesBlock && (
        <div className="space-y-1.5">
          {apptNotes && (
            <div className="flex gap-2">
              <StickyNote className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-slate-700 italic break-words">{apptNotes}</div>
            </div>
          )}
          {!privacyMode && apptData.patientNotes && (
            <div className="flex gap-2">
              <NotebookText className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
              <div className="text-slate-600 italic break-words">{apptData.patientNotes}</div>
            </div>
          )}
        </div>
      )}

      {resourceLines.length > 0 && (
        <div>
          <PopupSectionHeader icon={Users}>
            People / Resources ({resourceLines.length})
          </PopupSectionHeader>
          <ul className="pl-1 space-y-0.5">
            {resourceLines.map((r) => (
              <li key={r.key} className="text-slate-700">
                <span className="text-slate-400">·</span> {r.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {apptData.procedures && apptData.procedures.length > 0 && (
        <div>
          <PopupSectionHeader icon={Pill}>Procedures</PopupSectionHeader>
          <div className="flex flex-wrap gap-1">
            {apptData.procedures.map((p, i) => (
              <span
                key={`proc-${i}`}
                className="inline-block rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-700"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {apptData.products && apptData.products.length > 0 && (
        <div>
          <PopupSectionHeader icon={Package}>
            Products ({apptData.products.reduce((s, p) => s + p.totalQuantity, 0)})
          </PopupSectionHeader>
          <ul className="pl-1 space-y-0.5">
            {apptData.products.map((p, i) => (
              <React.Fragment key={`prod-${i}`}>
                {p.requiredQuantity > 0 && (
                  <li className="text-slate-700">
                    <span className="text-emerald-600 font-semibold">+{p.requiredQuantity}</span>{' '}
                    {p.productName}
                  </li>
                )}
                {p.addonQuantity > 0 && (
                  <li className="text-slate-700">
                    <span className="text-amber-600 font-semibold">+{p.addonQuantity}</span>{' '}
                    {p.productName} <span className="text-slate-400">(Addon)</span>
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      )}

      {apptData.memberships && apptData.memberships.length > 0 && (
        <div className="space-y-2">
          {apptData.memberships.map((m, i) => (
            <MembershipPanel key={`mem-${i}`} kind="Membership" icon={Award} data={m} />
          ))}
        </div>
      )}

      {apptData.packages && apptData.packages.length > 0 && (
        <div className="space-y-2">
          {apptData.packages.map((p, i) => (
            <MembershipPanel key={`pkg-${i}`} kind="Package" icon={PackageOpen} data={p} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Appointment Card --------------------------------------------------------

interface ApptCardProps {
  appt: CalendarAppointment;
  onOpen: (appt: CalendarAppointment) => void;
  onCheckIn: (appt: CalendarAppointment) => void;
  checkingIn: boolean;
  privacyMode?: boolean;
  showInfoPopups?: boolean;
  /** Legacy blockInfoPopUpToggle — distinct from apptInfoPopUpToggle. When the
   *  card wraps a block row this flag wins; otherwise showInfoPopups applies. */
  showBlockInfoPopups?: boolean;
  className?: string;
  /** Account-level field visibility from aa_accounts_calendar / CalendarSettingsAdmin. */
  cardSettings?: AdminCalendarSettings | null;
  /** Account-level palette + gate from aa_accounts (statusColors / statusColorXxx). */
  schedulerSettings?: SchedulerCalendarSettings | null;
  /** IANA tz the active staff user expects times rendered in (resolved
   *  from `users_timezones` -> `aa_accounts.timezone`). */
  userTz: string;
}

function ApptCard({ appt, onOpen,  privacyMode, showInfoPopups, showBlockInfoPopups, className, cardSettings, schedulerSettings, userTz }: ApptCardProps) {
  // Legacy: apptInfoPopUpToggle drives appointment popups; blockInfoPopUpToggle
  // drives block popups. Pick the effective flag for this row.
  const effectiveShowInfoPopups = appt.isBlock
    ? (showBlockInfoPopups ?? showInfoPopups)
    : showInfoPopups;
  // Fall back to the cached react-query result if the parent didn't provide settings.
  const cachedSettings = useQuery<AdminCalendarSettings>({
    queryKey: ['admin-calendar-settings'],
    queryFn: getAdminCalendarSettings,
    staleTime: 10 * 60 * 1000,
    enabled: !cardSettings,
  }).data;
  const cachedSchedulerSettings = useQuery<SchedulerCalendarSettings>({
    queryKey: ['scheduler-calendar-settings'],
    queryFn: getSchedulerCalendarSettings,
    staleTime: 10 * 60 * 1000,
    enabled: !schedulerSettings,
  }).data;
  const effectiveSettings = cardSettings ?? cachedSettings ?? null;
  const effectiveSchedulerSettings = schedulerSettings ?? cachedSchedulerSettings ?? null;
  // Legacy aa_accounts_calendar flags: 1 == show, 0 == hide. Default-on if settings unavailable.
  const show = (key: keyof CalendarInfoFlags): boolean =>
    effectiveSettings ? Number(effectiveSettings[key]) === 1 : true;
  const badge = statusBadge(appt);

  // Legacy "Checked In" intermediate state — `CalendarStatusKey` collapses
  // it under `scheduled`/`confirmed`, but legacy palette has a separate
  // `checkedIn` slot (scheduler-display-week-cell.php 977-979) and a
  // distinct visual. Derive it from `checkInTime` once and use everywhere.
  const isCheckedIn = !!appt.checkInTime
    && !appt.checkBackTime
    && !appt.checkOutTime
    && !appt.cancelled
    && !appt.noShow;

  // Legacy parity (scheduler-display-week-cell.php 960-985): when
  // `aa_accounts.statusColors == 1`, the card border follows the canonical
  // status() precedence and pulls colors from the per-account palette. We
  // reuse resolveStatusKey as the single source of truth, with a derived
  // `checkedIn` short-circuit since it isn't in `CalendarStatusKey`.
  const statusBorderColor = React.useMemo((): string | null => {
    if (!effectiveSchedulerSettings?.statusColors) return null;
    const palette = effectiveSchedulerSettings.statusColorPalette;
    if (!palette) return null;
    const normalize = (raw: string | null | undefined): string | null => {
      if (!raw) return null;
      return raw.startsWith('#') ? raw : `#${raw}`;
    };
    if (isCheckedIn) {
      const checkedIn = normalize(palette.checkedIn);
      if (checkedIn) return checkedIn;
    }
    const key = resolveStatusKey(appt);
    const map: Record<CalendarStatusKey, string | null | undefined> = {
      cancelled: palette.cancelled,
      no_show: palette.noShow,
      completed: palette.checkedOut,
      checked_back: palette.checkedBack,
      waiting_list: palette.waitList,
      confirmed: palette.confirmed,
      contact_attempted: palette.contactAttempted,
      scheduled: palette.scheduled,
    };
    return normalize(map[key]);
  }, [effectiveSchedulerSettings, appt, isCheckedIn]);

  // Legacy fill rule (scheduler-display-week-cell.php 849-899, 1327-1344):
  // - Comp appointments: forced gray `#888888` fill, white text, regardless
  //   of `statusColors`.
  // - First appointments: prefer `firstAppointmentTypeColor`; fall back to
  //   `appointmentTypeColor`.
  // - Otherwise (statusColors on): use `appointmentTypeColor`.
  // Comp/First overrides apply unconditionally so the visual semantic isn't
  // lost when an account hasn't opted into the status palette.
  const effectiveFillColor = React.useMemo((): string | null => {
    if (appt.compAppointment) return '#888888';
    const normalize = (raw: string | null | undefined): string | null => {
      if (!raw) return null;
      return raw.startsWith('#') ? raw : `#${raw}`;
    };
    if (appt.firstAppointment) {
      return normalize(appt.firstAppointmentTypeColor) ?? normalize(appt.appointmentTypeColor);
    }
    if (!effectiveSchedulerSettings?.statusColors) return null;
    return normalize(appt.appointmentTypeColor);
  }, [
    effectiveSchedulerSettings,
    appt.appointmentTypeColor,
    appt.firstAppointmentTypeColor,
    appt.firstAppointment,
    appt.compAppointment,
  ]);

  // Legacy font color precedence (scheduler-display-week-cell.php
  // 849-899, 1298-1314):
  //   1. Comp -> #FFFFFF (forced white over the gray comp fill)
  //   2. First -> firstAppointmentTypeFontColor, then #FFFFFF default
  //      when a first-appt color is set without a font color override
  //   3. Default -> appointmentTypeFontColor when defined; otherwise
  //      legacy applies #FFFFFF whenever there's a colored fill (legacy
  //      `if ($fontColor == "") { $fontColor = "#FFFFFF"; }` at 1313).
  //      Without this default, the modern UI fell back to dark
  //      `text-slate-*` Tailwind classes which were unreadable on the
  //      saturated type-color fills the user saw in the screenshot.
  // Returning null means "no override" — the card keeps the existing
  // Tailwind text classes (only when there's no fill at all).
  const effectiveFontColor = React.useMemo((): string | null => {
    const normalize = (raw: string | null | undefined): string | null => {
      if (!raw) return null;
      const trimmed = String(raw).trim();
      if (trimmed === '') return null;
      return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    };
    if (appt.compAppointment) return '#ffffff';
    if (appt.firstAppointment) {
      return normalize(appt.firstAppointmentTypeFontColor) ?? '#ffffff';
    }
    const explicit = normalize(appt.appointmentTypeFontColor);
    if (explicit) return explicit;
    // Legacy fallback: any cell with a colored fill defaults to white text.
    if (appt.appointmentTypeColor) return '#ffffff';
    return null;
  }, [
    appt.compAppointment,
    appt.firstAppointment,
    appt.firstAppointmentTypeFontColor,
    appt.appointmentTypeFontColor,
    appt.appointmentTypeColor,
  ]);

  // Reusable inline-style override used on inner text spans so the legacy
  // font color wins over the default `text-slate-*` Tailwind classes.
  const textColorStyle = effectiveFontColor ? { color: effectiveFontColor } : undefined;

  const borderColor = statusBorderColor ?? appt.appointmentTypeColor ?? '#3b82f6';
  // Legacy `borderColorToggle` (scheduler-display-week-cell.php 1327-1330):
  // 2px when `statusColors == 1`, 1px otherwise. Modern previously used a
  // fixed 3px left stripe regardless of the toggle.
  const accentBorderWidth = effectiveSchedulerSettings?.statusColors ? 2 : 1;

  // Render time labels in the user's preferred TZ. Mirrors legacy behavior
  // where each user can pick a TZ in their profile (users_timezones) and
  // see all calendar times in that zone regardless of where they're
  // browsing from.
  const startLabel = toLocaleTimeStringInTz(appt.start, undefined, {
    hour: '2-digit', minute: '2-digit', timeZone: userTz,
  });

  // WC-07 (P2 polish): legacy privacy-mode shows an empty name cell, not
  // literal "Patient". Match the legacy glyph.
  const patientDisplay = privacyMode
    ? '—'
    : (appt.patientName?.trim() || appt.title || 'No patient');

  const tooltipText = effectiveShowInfoPopups
    ? `${patientDisplay} - ${appt.appointmentTypeName ?? 'N/A'} at ${startLabel}${appt.resourceName ? ` with ${appt.resourceName}` : ''}`
    : undefined;

  // Time labels for the secondary "(09:00 AM - 10:30 AM)" line on block
  // cards. Mirrors legacy scheduler-display-week-cell.php 1139.
  const endLabel = toLocaleTimeStringInTz(appt.end, undefined, {
    hour: '2-digit', minute: '2-digit', timeZone: userTz,
  });

  // qtip-style rich hover popup — loads lazily via GET /appointments/{id}/info-popup or
  // /appointment-blocks/{id}/info-popup (legacy scheduler_appt_info_popup.php / scheduler_block_info_popup.php).
  // Storage is typed: appointments use AppointmentInfoPopup, blocks use
  // BlockInfoPopup. The structured ApptInfoPopover renders each section in
  // legacy order from the typed payload.
  //
  // Rendering: portaled to document.body so the card's `overflow-hidden`
  // doesn't clip the popup (legacy qTip lives at body level too). We track
  // viewport-relative coords from the card's bounding rect at open time
  // and flip above when there isn't enough room below.
  const [hoverOpen, setHoverOpen] = React.useState(false);
  // `top` is set when placing below the card; `bottom` is set when placing
  // above so the popup grows upward from just above the card and never
  // floats off to the top of the viewport (the prior approach pinned the
  // popup to top:8 for mid-screen cells).
  const [popupCoords, setPopupCoords] = React.useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
    placement: 'below' | 'above';
  } | null>(null);
  const [popupData, setPopupData] = React.useState<AppointmentInfoPopup | BlockInfoPopup | null>(null);
  const [popupLoading, setPopupLoading] = React.useState(false);
  const [popupError, setPopupError] = React.useState(false);
  const hoverTimerRef = React.useRef<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  // Popup viewport width. The popup's height adapts to the available space
  // via `maxHeight` (computed at open time) and scrolls internally when
  // content exceeds it — so we don't need a fixed height estimate.
  // Clamped to the viewport (SCRUM-1448). A hard 400px panel cannot fit a 375px screen however
  // it is positioned, so it overlapped the appointment columns and wrapped one or two words per
  // line. Clamping once, here, keeps the rendered `width` below and the left-edge clamp in
  // `computeCoords` in agreement — two independent caps would park the panel off its anchor.
  const POPUP_WIDTH = popupWidth(400, window.innerWidth);
  const POPUP_MIN_HEIGHT = 160;

  // Logged-in user level — gates the dollar quote badge in the popup
  // header strip per legacy `user_level > 2` check.
  const { user } = useAuth();
  const userLevel = user?.userLevel ?? 0;

  // Position the popup relative to the card. Picks the side (below /
  // above) with more room and caps the popup's max-height to fit; the
  // popup body scrolls internally when content overflows. Falls back
  // gracefully when both sides are tight rather than clamping to top:8
  // and floating away from the cell (as the prior naive math did for
  // mid-viewport cells).
  const computeCoords = React.useCallback((): {
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
    placement: 'below' | 'above';
  } | null => {
    const el = cardRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const margin = 6;
    const edge = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom - edge);
    const spaceAbove = Math.max(0, rect.top - edge);
    // Prefer below when there's reasonable room; flip only if "above"
    // gives meaningfully more space.
    const placeBelow = spaceBelow >= POPUP_MIN_HEIGHT || spaceBelow >= spaceAbove;
    const placement: 'below' | 'above' = placeBelow ? 'below' : 'above';
    const maxHeight = Math.max(
      POPUP_MIN_HEIGHT,
      placement === 'below' ? spaceBelow - margin : spaceAbove - margin,
    );
    let left = rect.left;
    if (left + POPUP_WIDTH > viewportWidth - edge) {
      left = Math.max(edge, viewportWidth - POPUP_WIDTH - edge);
    }
    if (left < edge) left = edge;
    if (placement === 'below') {
      return { top: rect.bottom + margin, left, maxHeight, placement };
    }
    // Anchor the popup's BOTTOM at `rect.top - margin` so it grows upward
    // from just above the cell. With CSS `bottom`, when content fits within
    // `maxHeight` the popup is right above the cell instead of pinned at
    // top:8 (legacy qTip behavior — tooltip hugs the trigger element).
    return {
      bottom: viewportHeight - rect.top + margin,
      left,
      maxHeight,
      placement,
    };
  }, []);

  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openPopup = React.useCallback(() => {
    if (!effectiveShowInfoPopups) return;
    cancelClose();
    if (hoverTimerRef.current !== null) return;
    hoverTimerRef.current = window.setTimeout(async () => {
      hoverTimerRef.current = null;
      const coords = computeCoords();
      if (coords) setPopupCoords(coords);
      setHoverOpen(true);
      if (!popupData && !popupLoading) {
        setPopupLoading(true);
        setPopupError(false);
        try {
          const svc = await import('@/services/appointments.service');
          const data = appt.isBlock
            ? await svc.getBlockInfoPopup(appt.id)
            : await svc.appointmentsService.getAppointmentInfoPopup(appt.id);
          setPopupData(data ?? null);
        } catch (err) {
          // Surface the failure in the popup body so the user knows the
          // hover lookup didn't succeed (legacy qTip silently shows
          // "Loading..." forever; we prefer an explicit message).
          // eslint-disable-next-line no-console
          console.warn('[SchedulePage] info-popup fetch failed', err);
          setPopupError(true);
          setPopupData(null);
        } finally {
          setPopupLoading(false);
        }
      }
    }, 500);
  }, [effectiveShowInfoPopups, popupData, popupLoading, appt.id, appt.isBlock, computeCoords, cancelClose]);

  const closePopup = React.useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Small grace period so users can move the cursor from the card into
    // the portaled popup without the popup vanishing en-route.
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setHoverOpen(false);
    }, 120);
  }, []);

  React.useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current);
  }, []);

  // Block branch — legacy `scheduler-display-week-cell.php` 1113-1141 wraps
  // block-time rows in a separate visual: solid color background + dotted
  // (Available) / cross-hatch (Unavailable) overlay, the block name and
  // Required/Optional annotation, no patient name / status badges / check-in
  // button. We branch early so the appointment-card layout stays focused on
  // patient-driven rendering.
  if (appt.isBlock) {
    // Block colors come from `scheduled_appointments.backgroundColor` /
    // `.fontColor` (legacy `scheduler-display-week-cell.php` 607-610). The
    // API's `calendarBlockOccurrence` overrides `appointmentTypeColor` /
    // `appointmentTypeFontColor` with these per-block values so each block
    // renders in the user's chosen palette instead of all defaulting to
    // gray. Falls back to `#666666` / `#FFFFFF` when unset (legacy
    // `scheduler_block_details.php` L109-L110 default).
    const blockBg = appt.appointmentTypeColor
      ? (appt.appointmentTypeColor.startsWith('#') ? appt.appointmentTypeColor : `#${appt.appointmentTypeColor}`)
      : '#666666';
    const blockFg = appt.appointmentTypeFontColor && appt.appointmentTypeFontColor.trim() !== ''
      ? (appt.appointmentTypeFontColor.startsWith('#') ? appt.appointmentTypeFontColor : `#${appt.appointmentTypeFontColor}`)
      : '#ffffff';
    const isAvailable = appt.blockType === 'available' || appt.blockAvailability === 1;
    const blockLabel = isAvailable ? 'Available' : 'Unavailable';
    // Inline SVG repeating patterns mirror the legacy `dotpattern.png` /
    // `crosshatch.png` overlays (line 1115-1118). Keeping them inline keeps
    // the bundle a one-file change and renders correctly over any block
    // color without needing image assets.
    const dotPattern =
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='6' height='6' viewBox='0 0 6 6'><circle cx='1.5' cy='1.5' r='0.85' fill='rgba(255,255,255,0.18)'/></svg>\")";
    const crossHatch =
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'><path d='M0 8 L8 0 M-2 2 L2 -2 M6 10 L10 6' stroke='rgba(255,255,255,0.18)' stroke-width='1'/></svg>\")";
    const overlayPattern = isAvailable ? dotPattern : crossHatch;
    const blockTitle = appt.title?.trim() || (isAvailable ? 'Available block' : 'Blocked time');
    // When rich popups are enabled the portaled popover replaces the
    // browser's native title; otherwise leave the simple summary as a
    // fallback so the user still sees something on hover.
    const blockNativeTitle = effectiveShowInfoPopups
      ? undefined
      : `${blockTitle} — ${blockLabel} (${startLabel} - ${endLabel})`;

    return (
      <div
        ref={cardRef}
        role="button"
        tabIndex={0}
        onClick={() => onOpen(appt)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(appt); }}
        onMouseEnter={openPopup}
        onMouseLeave={closePopup}
        onFocus={openPopup}
        onBlur={closePopup}
        title={blockNativeTitle}
        className={cn(
          'relative w-full text-left rounded border border-amber-200 cursor-pointer',
          'hover:shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          'overflow-hidden',
          className,
        )}
        style={{
          backgroundColor: blockBg,
          color: blockFg,
        }}
      >
        {/* Pattern overlay — sits above the solid color but below the text. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: overlayPattern }}
        />
        <div className="relative px-1.5 py-1 leading-tight">
          <p className="text-[11px] font-semibold drop-shadow-sm">
            {blockTitle}
            {' — '}
            <span className={cn('font-normal', isAvailable ? 'text-emerald-100' : 'text-rose-100')}>
              {blockLabel}
            </span>
          </p>
          <p className="text-[10px] text-white/90">
            {startLabel} – {endLabel}
            {appt.blockRequired === true && (
              <span className="ml-1 italic">Required</span>
            )}
            {appt.blockRequired === false && (
              <span className="ml-1 italic text-white/70">Optional</span>
            )}
          </p>
          {appt.resourceName && (
            <p className="text-[10px] text-white/80">
              {appt.resourceName}
            </p>
          )}
        </div>

        {/* Block hover popup — portaled to document.body so the card's
            `overflow-hidden` doesn't clip it; mirrors legacy qTip which
            renders globally rather than as a card child. */}
        {hoverOpen && popupCoords && createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              ...(popupCoords.top != null ? { top: popupCoords.top } : {}),
              ...(popupCoords.bottom != null ? { bottom: popupCoords.bottom } : {}),
              left: popupCoords.left,
              zIndex: 60,
              width: POPUP_WIDTH,
              maxHeight: popupCoords.maxHeight,
              overflowY: 'auto',
            }}
            className="rounded-md border border-slate-200 bg-white p-3 shadow-xl text-[11px] text-slate-700"
            onMouseEnter={cancelClose}
            onMouseLeave={closePopup}
            onClick={(e) => e.stopPropagation()}
          >
            <ApptInfoPopover
              data={popupData}
              loading={popupLoading}
              error={popupError}
              isBlock
              privacyMode={!!privacyMode}
              userLevel={userLevel}
              headerTitle={blockTitle}
              fallback={{
                status: isAvailable ? 'Available' : 'Unavailable',
                appointmentTypeName: appt.appointmentTypeName,
                facilityName: appt.facilityName,
                resourceNames: appt.resourceNames?.length
                  ? appt.resourceNames
                  : (appt.resourceName ? [appt.resourceName] : []),
                notes: appt.drNotes ?? appt.staffNotes ?? null,
                durationMinutes: appt.durationMinutes,
                startLabel,
                endLabel,
              }}
            />
          </div>,
          document.body,
        )}
      </div>
    );
  }

  // When rich popups are enabled the portaled popover replaces the
  // browser's native title (the native tooltip pre-empted hover and
  // duplicated info). Keep the simple summary as a fallback only when
  // popups are disabled.
  const apptNativeTitle = effectiveShowInfoPopups ? undefined : tooltipText;

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(appt)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(appt); }}
      onMouseEnter={openPopup}
      onMouseLeave={closePopup}
      onFocus={openPopup}
      onBlur={closePopup}
      title={apptNativeTitle}
      className={cn(
        'relative w-full text-left rounded border px-0.5 py-px cursor-pointer',
        // SCRUM-1448: "text is cut vertically, one or two letters per line". The card had no
        // minimum width and no wrapping rule, so in a ~40px column the paragraph below broke
        // per character. `max-lg:` matches the tier where the filters move into the drawer and
        // the grid takes a 9rem column minimum — the two halves of the same fix.
        'max-lg:break-words',
        'hover:shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        // When an inline fill is in play (comp / first / status palette),
        // defer to inline style; otherwise fall back to the Tailwind class.
        effectiveFillColor ? '' : cardBgClass(appt),
        // WC-11 (P2): legacy renders cancelled appointments with a line-through
        // on the full card content.
        resolveStatusKey(appt) === 'cancelled' ? 'line-through' : '',
        className,
      )}
      style={{
        borderLeftColor: borderColor,
        borderLeftWidth: accentBorderWidth,
        // Legacy parity: when status colors are on, the full outline of the
        // card follows the status palette (scheduler-display-week-cell.php
        // 960-985), not just the left stripe.
        ...(statusBorderColor
          ? { borderColor: statusBorderColor, borderWidth: accentBorderWidth }
          : {}),
        ...(effectiveFillColor
          ? (effectiveSchedulerSettings?.schedulerGradients
              // Legacy parity (scheduler-display-week-cell.php 1343-1347):
              // gradient runs from `darkerHtmlColor($color)` at the top to
              // `$color` at the bottom — same hue, varying brightness. The
              // earlier modern impl faded to white which lost the type-color
              // cue at the cell's lower half.
              ? { background: `linear-gradient(180deg, ${darkenHexColor(effectiveFillColor)} 0%, ${effectiveFillColor} 100%)` }
              : { backgroundColor: effectiveFillColor })
          : {}),
        ...(effectiveFontColor ? { color: effectiveFontColor } : {}),
      }}
    >
      {/* Legacy parity (scheduler-display-week-cell.php 1274-1294):
            `**RESCHEDULE PENDING** [First Appt] [Comp]` prefix; we render the
            ribbon on its own line so long patient names don't push it offscreen. */}
      {appt.reschedulePending && (
        <p className="text-[10px] font-bold tracking-wide text-rose-700 leading-none mb-0.5">
          ** RESCHEDULE PENDING **
        </p>
      )}

      {/* AR-5 / WC-14: per-resource color strip. `aa_resources
          .resourceBackgroundColor` backs the dot fill. We suppress the strip
          when status colors own the border (effectiveSchedulerSettings.statusColors),
          otherwise the colors fight for visual attention. */}
      {appt.resources
        && appt.resources.length > 0
        && appt.resources.some((r) => r.resourceColor)
        && effectiveSchedulerSettings?.statusColors !== true && (
        <div className="flex items-center gap-0.5 mb-0.5" aria-hidden="true">
          {appt.resources
            .filter((r) => r.resourceColor)
            .map((r) => (
              <span
                key={`rc-${r.resourceId}`}
                className="h-1.5 w-1.5 rounded-full inline-block"
                style={{ backgroundColor: r.resourceColor ?? undefined }}
                title={r.resourceName}
              />
            ))}
        </div>
      )}

      {/* Inline flowing text matching legacy layout: all info wraps as a
          single paragraph so short cells remain readable. */}
      <p className="text-[11px] leading-tight max-lg:min-w-0" style={textColorStyle}>
        {appt.checkOutTime && !appt.cancelled && !appt.noShow && (
          <span className="inline-flex items-center justify-center h-3 w-3 rounded-full bg-green-600 mr-0.5 align-middle" aria-label="Checked out">
            <Check className="h-2 w-2 text-white" />
          </span>
        )}
        {appt.checkInTime && !appt.checkOutTime && !appt.cancelled && !appt.noShow && (
          <span className="inline-flex items-center justify-center h-3 w-3 rounded-full bg-orange-500 mr-0.5 align-middle" aria-label="Checked in">
            <Check className="h-2 w-2 text-white" />
          </span>
        )}
        <span className="font-semibold">{patientDisplay}</span>
        {' '}
        <span className="text-[10px]">({startLabel} - {endLabel})</span>
        {appt.appointmentTypeName && show('infoApptType') && (
          <span className="text-[10px]"> - {appt.appointmentTypeName}</span>
        )}
        {(() => {
          const trimmedTitle = appt.title?.trim() ?? '';
          const showTitle = trimmedTitle !== '' && trimmedTitle !== 'Untitled Appointment';
          const addonSummary = appt.addonSummary?.trim() ?? '';
          if (!showTitle && !addonSummary) return null;
          return (
            <span className="text-[10px] italic">
              {' '}{showTitle ? trimmedTitle : ''}{addonSummary ? ` (${addonSummary})` : ''}
            </span>
          );
        })()}
        {appt.firstAppointment && (
          <span className="ml-1 align-middle rounded bg-indigo-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-indigo-700">
            First
          </span>
        )}
        {appt.compAppointment && (
          <span className="ml-1 align-middle rounded bg-amber-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700">
            Comp
          </span>
        )}
        {(() => {
          const names = appt.resourceNames?.length
            ? appt.resourceNames
            : (appt.resourceName ? [appt.resourceName] : []);
          if (names.length === 0) return null;
          if (!show('infoResources')) return null;
          if (effectiveSchedulerSettings?.schedulerResources === false) return null;
          return <span className="text-[10px]"> - {names.join(', ')}</span>;
        })()}
        {appt.facilityName && show('infoFacility') && (
          <span className="text-[10px]"> - {appt.facilityName}</span>
        )}
      </p>

      {/* Status badge */}
      {badge && show('infoStatus') && (
        <span className={cn('inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none', badge.cls)}>
          {badge.label}
        </span>
      )}

      {/* Check-in button — hidden (kept for future use) */}

      {/* Rich hover popup — portaled to document.body so the card's
          `overflow-hidden` parent doesn't clip it; mirrors legacy qTip
          which renders globally. */}
      {hoverOpen && popupCoords && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            ...(popupCoords.top != null ? { top: popupCoords.top } : {}),
            ...(popupCoords.bottom != null ? { bottom: popupCoords.bottom } : {}),
            left: popupCoords.left,
            zIndex: 60,
            width: POPUP_WIDTH,
            maxHeight: popupCoords.maxHeight,
            overflowY: 'auto',
          }}
          className="rounded-md border border-slate-200 bg-white p-3 shadow-xl text-[11px] text-slate-700"
          onMouseEnter={cancelClose}
          onMouseLeave={closePopup}
          onClick={(e) => e.stopPropagation()}
        >
          <ApptInfoPopover
            data={popupData}
            loading={popupLoading}
            error={popupError}
            isBlock={false}
            privacyMode={!!privacyMode}
            userLevel={userLevel}
            headerTitle={patientDisplay}
            // The popover coalesces its API payload with these row-derived
            // values so the body is never sparse. That would silently reinstate
            // any section the account turned off in Calendar Settings, because
            // the API omits a suppressed field and the fallback fills it back in
            // — so withhold the fallback for the fields the flags suppress.
            fallback={{
              status: show('infoStatus') ? getApptStatus(appt) : null,
              appointmentTypeName: show('infoApptType') ? appt.appointmentTypeName : null,
              facilityName: show('infoFacility') ? appt.facilityName : null,
              resourceNames: show('infoResources')
                ? (appt.resourceNames?.length
                    ? appt.resourceNames
                    : (appt.resourceName ? [appt.resourceName] : []))
                : [],
              notes: show('infoApptNotes')
                ? (appt.notesSnippet ?? appt.drNotes ?? appt.staffNotes ?? null)
                : null,
              durationMinutes: appt.durationMinutes,
              startLabel,
              endLabel,
            }}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
/**
 * Width (percent) of the blocks lane in side-by-side mode. The remainder
 * is reserved for appointments. Legacy used a fixed split (`scheduler-
 * display-week-cell.php` 652-664) — the modern UI mirrors the same ratio.
 */
const SIDE_BY_SIDE_BLOCKS_WIDTH_PCT = 32;

function buildPositionedEvents(
  events: CalendarAppointment[],
  startHour: number,
  hourEnd: number,
  blockDisplayStyle: 'overlay' | 'side-by-side',
  dateStr?: string,
  userTz?: string,
  slotMinutes?: number,
): PositionedCalendarItem[] {
  const blocks = events.filter((event) => event.isBlock);
  const appointments = events.filter((event) => !event.isBlock);

  if (blockDisplayStyle === 'overlay') {
    // Legacy parity (scheduler-display-week-cell-tz.php 463-621 / 647-962 /
    // 1067-1216): in overlay mode blocks and appointments each compute their
    // own conflict lanes against same-kind events only, both span the full
    // column (0-100%), and appointments are emitted AFTER blocks so DOM
    // order paints them on top — a fully-scheduled block disappears behind
    // its appointments instead of stealing lane width from them.
    return [
      ...layoutEvents(blocks, startHour, hourEnd, 0, 100, dateStr, userTz, slotMinutes),
      ...layoutEvents(appointments, startHour, hourEnd, 0, 100, dateStr, userTz, slotMinutes),
    ];
  }

  const blocksWidth = blocks.length > 0 && appointments.length > 0
    ? SIDE_BY_SIDE_BLOCKS_WIDTH_PCT
    : 0;
  return [
    ...layoutEvents(blocks, startHour, hourEnd, 0, blocks.length > 0 ? (blocksWidth || 100) : 0, dateStr, userTz, slotMinutes),
    ...layoutEvents(appointments, startHour, hourEnd, blocksWidth, 100 - blocksWidth, dateStr, userTz, slotMinutes),
  ];
}

interface OverlayDayColumnProps {
  dateStr: string;
  events: CalendarAppointment[];
  timeSlots: string[];
  slotMinutes: number;
  startHour: number;
  hourEnd: number;
  isToday: boolean;
  scheduleResourceId?: number;
  onOpenAppt: (appt: CalendarAppointment) => void;
  onOpenCreate: (dateStr: string, time: string, resourceIds?: number[]) => void;
  onCheckIn: (appt: CalendarAppointment) => void;
  checkingInId: number | null;
  privacyMode: boolean;
  showInfoPopups: boolean;
  showBlockInfoPopups: boolean;
  blockDisplayStyle: 'overlay' | 'side-by-side';
  /** Current tick (ms) used to render the now-line; re-evaluated every 60 seconds. */
  nowTick?: number;
  /** CT-06 (P2): when true, suppress the now-line (legacy aa_accounts.timezone==10). */
  hideNowLine?: boolean;
  /** Active staff user's IANA TZ (used for the now-line + event lane math). */
  userTz: string;
}

function OverlayDayColumn({
  dateStr,
  events,
  timeSlots,
  slotMinutes,
  startHour,
  hourEnd,
  isToday,
  scheduleResourceId,
  onOpenAppt,
  onOpenCreate,
  onCheckIn,
  checkingInId,
  privacyMode,
  showInfoPopups,
  showBlockInfoPopups,
  blockDisplayStyle,
  nowTick,
  hideNowLine,
  userTz,
}: OverlayDayColumnProps) {
  // Ref to the absolutely-positioned cards layer — passed to every
  // useAppointmentDragResize hook so pointer-y deltas can be measured
  // against a stable column height.
  const columnInnerRef = useRef<HTMLDivElement | null>(null);

  // Pull the cached scheduler settings (filled by ApptCard's own useQuery
  // and shared via react-query's cache). We only need `draggableAppts` here.
  const schedulerSettings = useQuery<SchedulerCalendarSettings>({
    queryKey: ['scheduler-calendar-settings'],
    queryFn: getSchedulerCalendarSettings,
    staleTime: 10 * 60 * 1000,
  }).data;
  const draggableApptsEnabled = schedulerSettings?.draggableAppts === true;

  const qc = useQueryClient();
  const toast = useToast();

  // Pending drag/resize awaiting user confirmation when scheduler_check_blocks
  // returned conflicts (legacy `dragConflictCheck` opened `popup_name` with
  // the same Cancel / Save-Anyway choice at calendar-test.php L5747-L5779).
  const [pendingDrag, setPendingDrag] = useState<{
    apptId: number;
    newStart: string;
    newEnd: string;
    conflicts: ConflictInfo[];
    warnings: ConflictInfo[];
    canSaveAnyway: boolean;
    // The pre-drag cache, carried here because the conflict dialog can sit open
    // indefinitely and its Cancel path has no request to hang a rollback off.
    snapshot: ScheduleCacheSnapshot;
  } | null>(null);
  const [savingDrag, setSavingDrag] = useState(false);

  /**
   * Move the card NOW, before any request goes out, and hand back the previous
   * cache so it can be put back if the save never lands.
   *
   * Prefix-matched on purpose: the full key is
   * `['schedule', rangeStart, rangeEnd, serverFacilityId, resourceIds]` and this
   * column has none of those in scope. Safe because the only `useQuery` in the
   * client using a `['schedule', …]` key is the calendar one — every other
   * reference to that key is an invalidation.
   */
  const applyOptimisticMove = useCallback(
    async (apptId: number, newStart: string, newEnd: string): Promise<ScheduleCacheSnapshot> => {
      // Stop any in-flight calendar fetch from landing on top of the write.
      await qc.cancelQueries({ queryKey: ['schedule'] });

      const snapshot = qc.getQueriesData<CalendarAppointment[]>({ queryKey: ['schedule'] });
      qc.setQueriesData<CalendarAppointment[]>(
        { queryKey: ['schedule'] },
        (list) => applyMoveToAppointments(list, apptId, newStart, newEnd),
      );

      return snapshot;
    },
    [qc],
  );

  const revertOptimisticMove = useCallback(
    (snapshot: ScheduleCacheSnapshot) => {
      for (const [key, data] of snapshot) {
        qc.setQueryData(key, data);
      }
    },
    [qc],
  );

  const persistReschedule = useCallback(
    async (apptId: number, newStart: string, newEnd: string, snapshot: ScheduleCacheSnapshot) => {
      setSavingDrag(true);

      // Collected inside the try, acted on after it: the "tell the patient?"
      // dialog is blocking, so raising it before the finally would leave the
      // drag spinner turning behind an open prompt.
      let smsPromptApptId: number | null = null;

      try {
        const result = await appointmentsService.updateAppointment(apptId, {
          startTime: newStart,
          endTime: newEnd,
        });
        toast.success('Appointment rescheduled');

        // Whether the patient is worth texting about the move is decided
        // server-side (start time actually changed, account has "text on staff
        // bookings" on) and arrives as meta.workflow.promptBookedSmsUpdate.
        //
        // result.data.appointmentId, NOT apptId: the save is audit-versioned,
        // so it inserted a NEW row and apptId now names a superseded version.
        const workflowMeta = result.meta?.workflow as Record<string, unknown> | undefined;
        const savedApptId = result.data?.appointmentId;
        if (workflowMeta?.promptBookedSmsUpdate === true && savedApptId) {
          smsPromptApptId = savedApptId;
        }

        // The card is already where the user dropped it. This reconciles that
        // optimistic value against the authoritative server response — a
        // correction step, no longer the thing that makes the card move.
        await qc.invalidateQueries({ queryKey: ['schedule'] });
      } catch {
        // Put the card back where it came from; the user needs to see that the
        // move did not take.
        revertOptimisticMove(snapshot);
        toast.error('Failed to reschedule appointment');
      } finally {
        setSavingDrag(false);
      }

      // Strictly after the move is saved and settled: the reschedule stands on
      // its own, and declining the text must not undo it.
      if (smsPromptApptId !== null) {
        await promptAndSendBookedSmsUpdate(smsPromptApptId, toast);
      }
    },
    [qc, toast, revertOptimisticMove],
  );

  const handleApptCommit = useCallback(
    async (apptId: number, newStart: string, newEnd: string) => {
      // Move the card first. The conflict check and the save are two more
      // round-trips behind this, and waiting for both before repainting is what
      // made a drop feel unresponsive.
      const snapshot = await applyOptimisticMove(apptId, newStart, newEnd);

      // Mirror legacy `dragConflictCheck` (calendar-test.php L5747-L5779):
      // run the conflict check first and only show the modal when blocks
      // are involved; otherwise persist immediately.
      let check: Awaited<ReturnType<typeof appointmentsService.dragCheck>>;
      try {
        check = await appointmentsService.dragCheck({
          appointmentId: apptId,
          newStart,
          newEnd,
        });
      } catch {
        // dragCheck failure should not orphan the user — fall back to a save
        // attempt with no conflict preview (matches legacy which would proceed
        // to plan_appt_processor_tz.php even if the check request failed).
        await persistReschedule(apptId, newStart, newEnd, snapshot);
        return;
      }

      // Drop conflict entries that flag pre-existing data on the appointment
      // itself (no patient / no plan when the type requires them). A grid
      // drag only changes the time, so it can't introduce these violations —
      // surfacing them here just blocks the user from rescheduling rows that
      // were already persisted in that state. The backend writer is the
      // authority that prevents NEW violations from being saved. Block
      // conflicts and resource double-bookings continue to gate the save.
      const PRE_EXISTING_DATA_CONFLICTS = new Set([
        'requires_patient',
        'requires_patient_plan',
      ]);
      const conflicts = (check.conflicts ?? []).filter(
        (c) => !(c.conflictType && PRE_EXISTING_DATA_CONFLICTS.has(c.conflictType)),
      );
      if (conflicts.length === 0) {
        await persistReschedule(apptId, newStart, newEnd, snapshot);
        return;
      }

      // Recompute canSaveAnyway against the filtered set so pre-existing
      // data violations don't gate the dialog button, and so resource
      // overlaps that came back as `severity: 'warning'` (within the
      // appointment type's maxConflicts threshold) still allow Save Anyway.
      const hasErrorAfterFilter = conflicts.some((c) => (c.severity ?? 'error') === 'error');
      setPendingDrag({
        apptId,
        newStart,
        newEnd,
        conflicts,
        warnings: check.warnings ?? [],
        canSaveAnyway: !hasErrorAfterFilter,
        snapshot,
      });
    },
    [applyOptimisticMove, persistReschedule],
  );
  const nowLinePct = useMemo(() => {
    if (!isToday || hideNowLine) return null;
    // Now-line is positioned in the user's TZ so it lines up with the
    // event rows (which also use user-TZ wall-clock for vertical math).
    const minutes = minutesSinceMidnightInTz(
      new Date(nowTick ?? Date.now()).toISOString(),
      userTz,
    );
    const startMin = startHour * 60;
    const endMin = hourEnd * 60;
    if (minutes < startMin || minutes > endMin) return null;
    // P1-10: scale the now-line by the real wall-clock duration so on DST
    // transition days the marker still aligns with the event rows.
    const visible = computeVisibleMinutes(startHour, hourEnd, dateStr, userTz);
    return ((minutes - startMin) / Math.max(1, visible)) * 100;
  }, [isToday, nowTick, startHour, hourEnd, dateStr, hideNowLine, userTz]);
  const positioned = useMemo(
    () => buildPositionedEvents(events, startHour, hourEnd, blockDisplayStyle, dateStr, userTz, slotMinutes),
    [events, startHour, hourEnd, blockDisplayStyle, dateStr, userTz, slotMinutes],
  );

  // Side-by-side mode: render a tinted background behind the LEFT blocks
  // lane to mirror the legacy yellow lane (scheduler-display-week-cell.php
  // 652-664 + the legacy CSS `.blockColumn` style). Only show when both
  // blocks and appointments exist for the day so a blocks-only day stays
  // full-width without a redundant tint.
  const sideBySideBlocksLaneVisible = useMemo(() => {
    if (blockDisplayStyle !== 'side-by-side') return false;
    let hasBlocks = false;
    let hasAppts = false;
    for (const e of events) {
      if (e.isBlock) hasBlocks = true;
      else hasAppts = true;
      if (hasBlocks && hasAppts) return true;
    }
    return false;
  }, [events, blockDisplayStyle]);

  const handleColumnClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const offsetY = Math.max(0, Math.min(bounds.height, event.clientY - bounds.top));
    // P1-10: use the DST-aware day duration so click-to-create snaps to the
    // correct wall-clock slot on spring-forward / fall-back days.
    const totalMinutes = Math.max(1, computeVisibleMinutes(startHour, hourEnd, dateStr, userTz));
    const clickedMinutes = startHour * 60 + (offsetY / bounds.height) * totalMinutes;
    const roundedMinutes = Math.floor(clickedMinutes / slotMinutes) * slotMinutes;
    const hh = Math.floor(roundedMinutes / 60);
    const mm = roundedMinutes % 60;
    onOpenCreate(
      dateStr,
      `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`,
      scheduleResourceId && scheduleResourceId > 0 ? [scheduleResourceId] : undefined,
    );
  }, [dateStr, hourEnd, onOpenCreate, scheduleResourceId, slotMinutes, startHour, userTz]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleColumnClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onOpenCreate(
            dateStr,
            `${String(startHour).padStart(2, '0')}:00`,
            scheduleResourceId && scheduleResourceId > 0 ? [scheduleResourceId] : undefined,
          );
        }
      }}
      className={cn('relative border-r border-slate-300 min-h-full cursor-pointer', isToday && 'bg-blue-50/30')}
    >
      <div className="absolute inset-0">
        {timeSlots.map((slot, index) => {
          const top = `${(index / timeSlots.length) * 100}%`;
          const isHourBoundary = slot.endsWith(':00');
          return (
            <div
              key={`${dateStr}-${slot}`}
              className={cn(
                'absolute left-0 right-0 border-t',
                isHourBoundary ? 'border-slate-400' : 'border-slate-300',
              )}
              style={{ top }}
            />
          );
        })}
      </div>

      {/* Side-by-side blocks lane background — legacy `scheduler-display-
          week-cell.php` 652-664 paints the blocks column with a light
          yellow tint (`#FFFFCC`-ish) so the user can visually separate
          blocks from appointments at a glance. */}
      {sideBySideBlocksLaneVisible && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0"
          style={{
            left: 0,
            width: `${SIDE_BY_SIDE_BLOCKS_WIDTH_PCT}%`,
            backgroundColor: '#fffce5',
            borderRight: '1px solid #f5e9a0',
          }}
        />
      )}

      <div ref={columnInnerRef} className="absolute inset-0">
        {positioned.map((item) => (
          <DraggableApptItem
            // Recurring blocks share `id` across every occurrence (legacy DOM
            // ids prefixed with $fullDate to disambiguate; see
            // scheduler-display-week-cell.php 1139), so the key needs more than
            // `id` to keep each per-day occurrence as its own element.
            //
            // It must NOT include `start`: the optimistic drag/resize update
            // rewrites `start` in the cache, and a key change there would unmount
            // this component between `pointerup` and the browser's synthesized
            // `click` — discarding the click guard that stops a drop from also
            // opening the detail modal. `dateStr` is this column's own day, so it
            // is stable across a within-day move; a cross-midnight drag lands in a
            // different column and remounts, which is correct. The block flag
            // keeps a block and an appointment that share an `id` distinct in
            // side-by-side mode.
            key={`${item.appt.isBlock ? 'b' : 'a'}-${item.appt.id}-${dateStr}`}
            item={item}
            columnRef={columnInnerRef}
            dateStr={dateStr}
            userTz={userTz}
            startHour={startHour}
            hourEnd={hourEnd}
            draggableEnabled={draggableApptsEnabled}
            onOpenAppt={onOpenAppt}
            onCheckIn={onCheckIn}
            checkingInId={checkingInId}
            privacyMode={privacyMode}
            showInfoPopups={showInfoPopups}
            showBlockInfoPopups={showBlockInfoPopups}
            onCommit={handleApptCommit}
          />
        ))}
      </div>

      {/* Current-time line (legacy scheduler_display_tz.php setInterval). */}
      {nowLinePct !== null && (
        <div
          className="pointer-events-none absolute left-0 right-0 z-20 flex items-center"
          style={{ top: `${nowLinePct}%` }}
        >
          <div className="h-2 w-2 rounded-full bg-red-500 -translate-x-1" />
          <div className="flex-1 h-px bg-red-500" />
        </div>
      )}

      <DragConflictDialog
        pending={pendingDrag}
        displayTz={userTz}
        saving={savingDrag}
        // Cancel — and the hard-conflict case where Cancel is the only button —
        // abandons the move, so the optimistic position has to be undone here.
        // There is no failed request to roll this back for us.
        onCancel={() => {
          if (pendingDrag) revertOptimisticMove(pendingDrag.snapshot);
          setPendingDrag(null);
        }}
        onConfirm={async () => {
          if (!pendingDrag) return;
          const { apptId, newStart, newEnd, snapshot } = pendingDrag;
          setPendingDrag(null);
          await persistReschedule(apptId, newStart, newEnd, snapshot);
        }}
      />
    </div>
  );
}

// --- Draggable Appointment Wrapper ------------------------------------------
//
// Per-card wrapper that wires `useAppointmentDragResize` for the absolutely-
// positioned card slot. When the account flag is off (`draggableEnabled =
// false`) the wrapper renders the legacy non-interactive layout, preserving
// existing behavior for accounts that never opted into draggable scheduling.

interface DraggableApptItemProps {
  item: PositionedCalendarItem;
  columnRef: React.RefObject<HTMLDivElement | null>;
  dateStr: string;
  userTz: string;
  startHour: number;
  hourEnd: number;
  draggableEnabled: boolean;
  onOpenAppt: (appt: CalendarAppointment) => void;
  onCheckIn: (appt: CalendarAppointment) => void;
  checkingInId: number | null;
  privacyMode: boolean;
  showInfoPopups: boolean;
  showBlockInfoPopups: boolean;
  onCommit: (apptId: number, newStart: string, newEnd: string) => void | Promise<void>;
}

function DraggableApptItem({
  item,
  columnRef,
  dateStr,
  userTz,
  startHour,
  hourEnd,
  draggableEnabled,
  onOpenAppt,
  onCheckIn,
  checkingInId,
  privacyMode,
  showInfoPopups,
  showBlockInfoPopups,
  onCommit,
}: DraggableApptItemProps) {
  const { appt } = item;

  // Legacy `makeApptDraggable` (calendar-test.php L2729) implicitly skipped
  // cells without a real appointment row — blocks, cancelled, no-shows and
  // checked-out cards are excluded from drag because rescheduling them is
  // either nonsensical or would silently overwrite legacy audit timestamps.
  const isInteractive =
    draggableEnabled
    && !appt.isBlock
    && !appt.cancelled
    && !appt.noShow
    && !appt.checkOutTime;

  const drag = useAppointmentDragResize({
    enabled: isInteractive,
    currentStartIso: appt.start,
    currentEndIso: appt.end,
    dateStr,
    userTz,
    startHour,
    hourEnd,
    columnRef,
    onCommit: (newStart, newEnd) => onCommit(appt.id, newStart, newEnd),
  });

  // Native capture-phase listener: only swallow the click when the drag
  // guard is active (mirror of legacy `wasDragged` at calendar-test.php
  // L2727 / L2839). Going through the DOM bypasses React's synthetic
  // dispatch entirely, so when the guard is NOT active the click reaches
  // ApptCard's internal onClick normally and opens the detail modal.
  // Using React's onClickCapture for this instead breaks every click
  // because stopPropagation() in React's capture phase suppresses the
  // child's onClick too.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handler = (event: MouseEvent) => {
      if (drag.consumeClickGuard()) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    };
    el.addEventListener('click', handler, true);
    return () => el.removeEventListener('click', handler, true);
  }, [drag]);

  const wrapperCursor = !isInteractive
    ? ''
    : drag.isDragging
      ? 'cursor-grabbing'
      : 'cursor-grab';

  return (
    <div
      ref={wrapperRef}
      className={`absolute pb-1 group/appt ${wrapperCursor}`}
      style={{
        top: `${item.topPct}%`,
        left: `${item.leftPct}%`,
        width: `${item.widthPct}%`,
        height: `${item.heightPct}%`,
        minHeight: 14,
      }}
      // Bubble-phase onClick: ApptCard's own onClick has already fired by
      // the time this runs, so opening the detail modal still works. We
      // stop propagation here to keep the column-level click-to-create
      // (handleColumnClick) from firing on the same click — matching the
      // original wrapper's `onClick={(e) => e.stopPropagation()}` behavior.
      onClick={(event) => event.stopPropagation()}
      onPointerDown={isInteractive ? drag.cardHandlers.onPointerDown : undefined}
    >
      <ApptCard
        appt={appt}
        onOpen={onOpenAppt}
        onCheckIn={onCheckIn}
        checkingIn={checkingInId === appt.id}
        privacyMode={privacyMode}
        showInfoPopups={showInfoPopups}
        showBlockInfoPopups={showBlockInfoPopups}
        userTz={userTz}
        className="h-full overflow-hidden text-[11px]"
      />

      {/* Bottom-edge resize handle — only when draggable is on. Hover-only
          to keep the resting card visually unchanged from the non-draggable
          layout (legacy jQuery-UI `resizable()` painted a similar 5px grip
          along the bottom edge; see calendar-test.php L2743). */}
      {isInteractive && (
        <div
          className="absolute bottom-1 left-0 right-0 h-[6px] cursor-ns-resize opacity-0 group-hover/appt:opacity-100 transition-opacity"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.18)' }}
          onPointerDown={drag.resizeHandlers.onPointerDown}
          aria-hidden="true"
        />
      )}

      {/* Translucent ghost while dragging — replaces the legacy
          `$("#" + cellID + "_times").html(...)` text update at L2791/L2942
          with a real preview rectangle inside the column. Portaled into the
          column's cards layer so its top/height percentages resolve against
          the same containing block the card lanes use. */}
      {drag.ghost && columnRef.current && createPortal(
        <div
          className="pointer-events-none absolute z-30 rounded border border-blue-500 bg-blue-200/60 text-[10px] font-semibold text-blue-900 px-1 py-0.5"
          style={{
            top: `${drag.ghost.topPct}%`,
            height: `${drag.ghost.heightPct}%`,
            left: `${item.leftPct}%`,
            width: `${item.widthPct}%`,
          }}
        >
          {drag.ghost.startLabel} – {drag.ghost.endLabel}
        </div>,
        columnRef.current,
      )}
    </div>
  );
}

// --- Drag-conflict confirmation modal ---------------------------------------
//
// Surfaces the same Cancel / Save-Anyway choice the legacy `dragConflictCheck`
// popup did (calendar-test.php L5747-L5779). Only opens when
// /appointments/availability/drag-check returns at least one conflict.

interface DragConflictDialogProps {
  pending: {
    apptId: number;
    newStart: string;
    newEnd: string;
    conflicts: ConflictInfo[];
    warnings: ConflictInfo[];
    canSaveAnyway: boolean;
  } | null;
  saving: boolean;
  /** Facility/user IANA zone the quoted conflict times are rendered in. */
  displayTz: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

function DragConflictDialog({ pending, saving, displayTz, onCancel, onConfirm }: DragConflictDialogProps) {
  const open = pending !== null;
  const cat = pending ? categorizeConflicts(pending.conflicts) : null;
  const heading = cat ? buildConflictHeading(cat) : '';
  const resourceErrors = cat?.resource.filter((c) => c.severity === 'error') ?? [];
  const resourceWarnings = cat?.resource.filter((c) => c.severity !== 'error') ?? [];
  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      title="Reschedule Conflict"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {pending?.canSaveAnyway && (
            <Button onClick={onConfirm} loading={saving}>
              Save Anyway
            </Button>
          )}
        </div>
      }
    >
      {pending && cat && (
        <div className="space-y-3 text-sm text-slate-700">
          <p>{heading}</p>

          {cat.block.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
              <p className="font-semibold">Block conflicts</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {cat.block.map((c, idx) => (
                  <li key={`block-${idx}`}>{c.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {resourceErrors.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
              <p className="font-semibold">Resource conflicts</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {resourceErrors.map((c, idx) => (
                  <li key={`re-${idx}`}>
                    <span className="font-medium">{c.resourceName ?? 'Resource'}:</span> {formatResourceConflictLine(c, displayTz)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {resourceWarnings.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              <p className="font-semibold">Resource overlaps</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {resourceWarnings.map((c, idx) => (
                  <li key={`rw-${idx}`}>
                    <span className="font-medium">{c.resourceName ?? 'Resource'}:</span> {formatResourceConflictLine(c, displayTz)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {cat.other.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
              <p className="font-semibold">Other issues</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {cat.other.map((c, idx) => (
                  <li key={`oi-${idx}`}>{c.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {pending.warnings.length > 0 && (
            <div className="mt-3">
              <p className="font-semibold text-amber-700">Warnings:</p>
              <ul className="list-disc pl-5 space-y-1 text-amber-700">
                {pending.warnings.map((w, idx) => (
                  <li key={`warn-${idx}`}>{w.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {!pending.canSaveAnyway && (
            <p className="mt-3 text-red-600">
              These conflicts must be resolved before the appointment can be moved.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// --- Mini Calendar -----------------------------------------------------------

interface MiniCalendarProps {
  currentDate: Date;
  onDateClick: (date: Date) => void;
  /** CT-07 (P2): 0=Sunday (legacy default), 1=Monday. */
  weekStart?: 0 | 1;
}

function MiniCalendar({ currentDate, onDateClick, weekStart = 0 }: MiniCalendarProps) {
  const [displayMonth, setDisplayMonth] = useState<Date>(
    () => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  );

  const todayStr = toDateStr(new Date());
  const selectedStr = toDateStr(currentDate);

  const grid = useMemo(
    () => getMonthGrid(displayMonth.getFullYear(), displayMonth.getMonth(), weekStart),
    [displayMonth, weekStart],
  );

  const dowLabels = weekStart === 0
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  const monthLabel = displayMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="px-3 py-2 border-b border-slate-200">
      {/* Month header with arrows */}
      <div className="flex items-center justify-between mb-1">
        <button
          type="button"
          onClick={() => setDisplayMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          className="p-0.5 rounded hover:bg-slate-200 text-slate-500"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <span className="text-[11px] font-semibold text-slate-700">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setDisplayMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          className="p-0.5 rounded hover:bg-slate-200 text-slate-500"
          aria-label="Next month"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-px mb-0.5">
        {dowLabels.map((d, i) => (
          <div key={i} className="text-center text-[9px] font-medium text-slate-400">{d}</div>
        ))}
      </div>

      {/* Date cells */}
      <div className="grid grid-cols-7 gap-px">
        {grid.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} className="h-5" />;
          const ds = toDateStr(day);
          const isToday = ds === todayStr;
          const isSelected = ds === selectedStr;
          return (
            <button
              key={ds}
              type="button"
              onClick={() => onDateClick(day)}
              className={cn(
                'h-5 w-full text-[10px] rounded transition-colors',
                isToday && !isSelected && 'bg-primary/10 text-primary font-bold',
                isSelected && 'bg-primary text-white font-bold',
                !isToday && !isSelected && 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Overview Section --------------------------------------------------------

interface OverviewSectionProps {
  title: string;
  appointments: CalendarAppointment[];
  colorClass: string;
  onOpenAppt: (appt: CalendarAppointment) => void;
  privacyMode: boolean;
  // Inline workflow actions (legacy progressAppointment(id, action))
  onConfirm: (id: number) => void;
  onCheckIn: (id: number) => void;
  onCheckBack: (id: number) => void;
  onCheckOut: (id: number) => void;
  pendingId: number | null;
  /** Controlled collapsed flag persisted via users_filters_sched ov*Open. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** P1-11: aa_accounts_calendar field toggles; reused from ApptCard so the
   *  overview shows the same columns the grid card does. */
  cardSettings?: AdminCalendarSettings | null;
  /** Active staff user's IANA TZ for time-column rendering. */
  userTz: string;
  /** Show the date alongside the time in the Start column. Set for the
   *  Waiting List section, whose rows are not scoped to the viewed day, so a
   *  bare clock time would not say WHEN the patient is currently booked. */
  showStartDate?: boolean;
}

// Columns mirror legacy scheduler_display_tz.php 1388-1459:
//   Start · Patient · Phone · Plan · Notes · Facility · Type · Resources · Dur · Action
function OverviewSection({
  title, appointments, colorClass, onOpenAppt, privacyMode,
  onConfirm, onCheckIn, onCheckBack, onCheckOut, pendingId,
  collapsed, onToggleCollapsed, cardSettings, userTz, showStartDate = false,
}: OverviewSectionProps) {
  // Legacy aa_accounts_calendar flags: 1 == show, 0 == hide. Default-on if
  // settings unavailable. Kept local to the component so ApptCard's copy stays
  // the single definition (duplicated here to avoid a cross-cutting util).
  const show = (key: keyof CalendarInfoFlags): boolean =>
    cardSettings ? Number(cardSettings[key]) === 1 : true;
  const showPhone = show('infoPhone');
  const showPlan = show('infoPlanStage');
  const showApptNotes = show('infoApptNotes');
  const showFacility = show('infoFacility');
  const showApptType = show('infoApptType');
  const showResources = show('infoResources');

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
      <button
        type="button"
        onClick={onToggleCollapsed}
        className={cn(
          'w-full flex items-center justify-between px-4 py-2 text-left font-semibold text-sm',
          colorClass,
        )}
      >
        <span>{title} ({appointments.length})</span>
        {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
      </button>
      {!collapsed && appointments.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                <th className="text-left px-3 py-1.5 whitespace-nowrap">Start</th>
                {/* Headerless member marker, matching the appointments list. */}
                <th className="w-6 px-1 py-1.5" aria-label="Member" />
                <th className="text-left px-3 py-1.5">Patient</th>
                {showPhone && <th className="text-left px-3 py-1.5">Phone</th>}
                {showPlan && <th className="text-left px-3 py-1.5">Plan</th>}
                {showApptNotes && <th className="text-left px-3 py-1.5">Notes</th>}
                {showFacility && <th className="text-left px-3 py-1.5">Facility</th>}
                {showApptType && <th className="text-left px-3 py-1.5">Type</th>}
                {showResources && <th className="text-left px-3 py-1.5">Resources</th>}
                <th className="text-right px-3 py-1.5 whitespace-nowrap">Dur.</th>
                <th className="text-right px-3 py-1.5 whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {appointments.map((appt) => {
                const startTime = toLocaleTimeStringInTz(appt.start, undefined, {
                  hour: '2-digit', minute: '2-digit', timeZone: userTz,
                });
                const startDate = showStartDate
                  ? toLocaleDateStringInTz(appt.start, undefined, {
                    month: 'numeric', day: 'numeric', timeZone: userTz,
                  })
                  : null;
                const durationMins = appt.end
                  ? Math.round((new Date(appt.end).getTime() - new Date(appt.start).getTime()) / 60000)
                  : (appt.durationMinutes ?? 0);
                const resourceDisplay = (appt.resourceNames && appt.resourceNames.length > 0)
                  ? appt.resourceNames.join(', ')
                  : (appt.resourceName ?? '-');
                const planLabel = appt.planDisplayId ?? (appt.planId ? `#${appt.planId}` : '-');
                const isPending = pendingId === appt.id;

                // Legacy progressAppointment() button visibility:
                //  - confirm: when not yet confirmed and not cancelled/no-show
                //  - checkin: when not yet checked in
                //  - checkback: after check-in, before check-out
                //  - checkout: after check-in, before check-out
                const canConfirm = !appt.confirmed && !appt.cancelled && !appt.noShow;
                const canCheckIn = !appt.checkInTime && !appt.cancelled && !appt.noShow;
                const canCheckBack = !!appt.checkInTime && !appt.checkBackTime && !appt.checkOutTime;
                const canCheckOut = !!appt.checkInTime && !appt.checkOutTime;

                return (
                  <tr
                    // Block occurrences reuse `id` across days; suffix with
                    // `start` so the list-view stays unique per row.
                    key={`${appt.id}-${appt.start}`}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <td
                      className="px-3 py-2 text-slate-500 whitespace-nowrap cursor-pointer"
                      onClick={() => onOpenAppt(appt)}
                    >
                      {startDate ? `${startDate} ${startTime}` : startTime}
                    </td>
                    {/* Sits outside the name cell so it never gets caught by
                        that cell's truncation. Hidden under privacy mode along
                        with the name and phone. */}
                    <td className="px-1 py-2 text-center">
                      {!privacyMode && appt.patientIsMember && (
                        <span
                          title="Member"
                          aria-label="Member"
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-[11px] font-semibold lowercase text-green-700 ring-1 ring-green-200"
                        >
                          m
                        </span>
                      )}
                    </td>
                    <td
                      className="px-3 py-2 font-medium text-slate-700 cursor-pointer max-w-[14ch] truncate"
                      onClick={() => onOpenAppt(appt)}
                    >
                      {/* WC-07: legacy privacy-mode blanks the patient name cell
                          rather than showing a "Patient" literal. */}
                      {privacyMode ? '—' : (appt.patientName?.trim() || 'No patient')}
                    </td>
                    {showPhone && (
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                        {privacyMode ? '—' : (appt.patientPhone ?? '-')}
                      </td>
                    )}
                    {showPlan && (
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{planLabel}</td>
                    )}
                    {showApptNotes && (
                      <td className="px-3 py-2 text-slate-500 max-w-[18ch] truncate" title={appt.notesSnippet ?? ''}>
                        {appt.notesSnippet ?? '-'}
                      </td>
                    )}
                    {showFacility && (
                      <td className="px-3 py-2 text-slate-500 max-w-[14ch] truncate">{appt.facilityName ?? '-'}</td>
                    )}
                    {showApptType && (
                      <td className="px-3 py-2 text-slate-500 max-w-[14ch] truncate">{appt.appointmentTypeName ?? '-'}</td>
                    )}
                    {showResources && (
                      <td className="px-3 py-2 text-slate-500 max-w-[18ch] truncate" title={resourceDisplay}>
                        {resourceDisplay}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right text-slate-400 whitespace-nowrap">
                      {durationMins > 0 ? `${durationMins}m` : '-'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        {canConfirm && (
                          <button
                            type="button"
                            className="rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                            onClick={(e) => { e.stopPropagation(); onConfirm(appt.id); }}
                            disabled={isPending}
                            title="Confirm"
                          >
                            Conf
                          </button>
                        )}
                        {canCheckIn && (
                          <button
                            type="button"
                            className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            onClick={(e) => { e.stopPropagation(); onCheckIn(appt.id); }}
                            disabled={isPending}
                            title="Check In"
                          >
                            In
                          </button>
                        )}
                        {canCheckBack && (
                          <button
                            type="button"
                            className="rounded border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                            onClick={(e) => { e.stopPropagation(); onCheckBack(appt.id); }}
                            disabled={isPending}
                            title="Send Back"
                          >
                            Back
                          </button>
                        )}
                        {canCheckOut && (
                          <button
                            type="button"
                            className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                            onClick={(e) => { e.stopPropagation(); onCheckOut(appt.id); }}
                            disabled={isPending}
                            title="Check Out"
                          >
                            Out
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!collapsed && appointments.length === 0 && (
        <p className="px-4 py-3 text-xs text-slate-400 italic">No appointments</p>
      )}
    </div>
  );
}

// --- Schedules per-type section (legacy parity) ------------------------------
//
// Legacy `calendar-test.php` (L7327-L7600+) renders the Schedules sidebar as
// six independent panels — Doctors / Coordinators / Nurses / Rooms / Equipment
// / Other — each with its own header, count badge, check-all/uncheck-all
// controls, and scrollable checkbox list. This component restores that layout
// in v5 (replacing the single MultiSelectDropdown that was lumping every
// resource type into one long list).
//
// `selectedSchedules` remains an ordered `number[]` (legacy dual-axis: the
// schedule list also drives column ordering — see line 2169 below). Toggling a
// resource on appends to the end; check-all-of-type appends only the missing
// ids; clear-of-type filters them out without disturbing the order of the rest.

interface ScheduleTypeOption {
  id: number;
  label: string;
}

interface ScheduleTypeSectionProps {
  resourceType: Resource['resourceType'];
  title: string;
  options: ScheduleTypeOption[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  /**
   * Receives the ids currently on screen when a search term is narrowing the
   * list, and `undefined` when it is not — so "Select all" after a search
   * takes only the matches, merged into the existing picks.
   */
  onSelectAll: (resourceType: Resource['resourceType'], visibleIds?: number[]) => void;
  onClearAll: (resourceType: Resource['resourceType']) => void;
}

function ScheduleTypeSection({
  resourceType,
  title,
  options,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
}: ScheduleTypeSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const selectedCount = useMemo(
    () => options.reduce((acc, opt) => acc + (selectedIds.has(opt.id) ? 1 : 0), 0),
    [options, selectedIds],
  );
  const searchable = options.length > 6;
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, search]);

  const [sortSnapshot, setSortSnapshot] = React.useState<Set<number>>(() => new Set());
  const prevExpandedRef = React.useRef(expanded);
  useEffect(() => {
    const justExpanded = expanded && !prevExpandedRef.current;
    prevExpandedRef.current = expanded;
    if (justExpanded || !expanded) {
      setSortSnapshot(new Set(selectedIds));
    }
  }, [expanded, selectedIds]);

  const handleSectionBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setSortSnapshot(new Set(selectedIds));
    }
  }, [selectedIds]);

  const displayOptions = useMemo(() => {
    const sorted = [...filteredOptions].sort((a, b) => a.label.localeCompare(b.label));
    const top = sorted.filter((opt) => sortSnapshot.has(opt.id));
    const bottom = sorted.filter((opt) => !sortSnapshot.has(opt.id));
    return [...top, ...bottom];
  }, [filteredOptions, sortSnapshot]);

  const isSearching = search.trim().length > 0;
  const visibleIds = useMemo(() => displayOptions.map((opt) => opt.id), [displayOptions]);

  return (
    <div className="rounded-md border border-slate-200 bg-white" onBlur={handleSectionBlur}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left"
        aria-expanded={expanded}
        aria-controls={`sched-type-${resourceType}`}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          <ChevronDown
            className={cn(
              'h-3 w-3 text-slate-400 transition-transform',
              !expanded && '-rotate-90',
            )}
          />
          {title}
        </span>
        {selectedCount > 0 ? (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold tabular-nums">
            {selectedCount}
          </span>
        ) : (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-slate-400 text-[10px] font-semibold tabular-nums">
            {selectedCount}
          </span>
        )}
      </button>
      {expanded && (
        <>
          <div className="flex items-center justify-end gap-2 border-t border-b border-slate-100 px-2 py-1.5 text-[11px] text-slate-600">
            <button
              type="button"
              onClick={() => onSelectAll(resourceType, isSearching ? visibleIds : undefined)}
              disabled={isSearching && visibleIds.length === 0}
              title={
                isSearching
                  ? `Add the ${visibleIds.length} matching option${visibleIds.length === 1 ? '' : 's'} to the selection`
                  : 'Select every option'
              }
              className="font-medium hover:text-blue-600 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {isSearching ? `Select ${visibleIds.length}` : 'Select all'}
            </button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={() => onClearAll(resourceType)} className="font-medium hover:text-slate-800">Clear</button>
          </div>
          {searchable && (
            <div className="px-2 pb-1 pt-2">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full rounded border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}
          <div
            id={`sched-type-${resourceType}`}
            className="max-h-[170px] overflow-y-auto px-2 py-1"
          >
            {displayOptions.length === 0 ? (
              <p className="px-2 py-2 text-center text-[11px] text-slate-400">
                {options.length === 0 ? 'No options available' : 'No matches'}
              </p>
            ) : (
              displayOptions.map((opt) => {
                const checked = selectedIds.has(opt.id);
                return (
                  <label
                    key={opt.id}
                    title={opt.label}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs text-slate-700 hover:bg-slate-50',
                      checked && 'text-slate-900',
                    )}
                  >
                    <Checkbox checked={checked} onChange={() => onToggle(opt.id)} />
                    <span className="truncate">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}


// --- Generic collapsible filter section ----------------------------------------

interface FilterSectionProps {
  title: string;
  options: MultiSelectOption[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  /**
   * Receives the ids currently on screen when a search term is narrowing the
   * list, and `undefined` when it is not — so "Select all" after a search
   * takes only the matches, merged into the existing picks.
   */
  onSelectAll: (visibleIds?: number[]) => void;
  onClearAll: () => void;
  disabled?: boolean;
  badge?: string;
}

function FilterSection({
  title,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  disabled,
  badge,
}: FilterSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const selectedCount = useMemo(
    () => options.reduce((acc, opt) => acc + (selected.has(opt.id) ? 1 : 0), 0),
    [options, selected],
  );
  const searchable = options.length > 6;
  const cleanOptions = useMemo(() => options.filter((opt) => !/----/.test(opt.label)), [options]);
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cleanOptions;
    return cleanOptions.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [cleanOptions, search]);

  const [sortSnapshot, setSortSnapshot] = React.useState<Set<number>>(() => new Set());
  const prevExpandedRef = React.useRef(expanded);
  useEffect(() => {
    const justExpanded = expanded && !prevExpandedRef.current;
    prevExpandedRef.current = expanded;
    if (justExpanded || !expanded) {
      setSortSnapshot(new Set(selected));
    }
  }, [expanded, selected]);

  const handleSectionBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setSortSnapshot(new Set(selected));
    }
  }, [selected]);

  const displayOptions = useMemo(() => {
    const sorted = [...filteredOptions].sort((a, b) => a.label.localeCompare(b.label));
    const top = sorted.filter((opt) => sortSnapshot.has(opt.id));
    const bottom = sorted.filter((opt) => !sortSnapshot.has(opt.id));
    return [...top, ...bottom];
  }, [filteredOptions, sortSnapshot]);

  const isSearching = search.trim().length > 0;
  const visibleIds = useMemo(() => displayOptions.map((opt) => opt.id), [displayOptions]);

  return (
    <div className="rounded-md border border-slate-200 bg-white" onBlur={handleSectionBlur}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          <ChevronDown
            className={cn(
              'h-3 w-3 text-slate-400 transition-transform',
              !expanded && '-rotate-90',
            )}
          />
          {title}
        </span>
        {badge ? (
          <span className="text-[10px] font-medium text-blue-600">{badge}</span>
        ) : selectedCount > 0 ? (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold tabular-nums">
            {selectedCount}
          </span>
        ) : (
          <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-slate-400 text-[10px] font-semibold tabular-nums">
            {selectedCount}
          </span>
        )}
      </button>
      {expanded && (
        <>
          <div className="flex items-center justify-end gap-2 border-t border-b border-slate-100 px-2 py-1.5 text-[11px] text-slate-600">
            <button
              type="button"
              onClick={() => onSelectAll(isSearching ? visibleIds : undefined)}
              disabled={isSearching && visibleIds.length === 0}
              title={
                isSearching
                  ? `Add the ${visibleIds.length} matching option${visibleIds.length === 1 ? '' : 's'} to the selection`
                  : 'Select every option'
              }
              className="font-medium hover:text-blue-600 disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {isSearching ? `Select ${visibleIds.length}` : 'Select all'}
            </button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={onClearAll} className="font-medium hover:text-slate-800">Clear</button>
          </div>
          {searchable && (
            <div className="px-2 pb-1 pt-2">
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full rounded border border-slate-200 bg-white py-1 pl-7 pr-2 text-xs outline-none focus:border-blue-400"
                />
              </div>
            </div>
          )}
          <div className={cn(
            'max-h-[170px] overflow-y-auto px-2 py-1',
            disabled && 'pointer-events-none select-none opacity-60',
          )}>
            {displayOptions.length === 0 ? (
              <p className="px-2 py-2 text-center text-[11px] text-slate-400">
                {options.length === 0 ? 'No options available' : 'No matches'}
              </p>
            ) : (
              displayOptions.map((opt) => {
                const checked = selected.has(opt.id);
                return (
                  <label
                    key={opt.id}
                    title={opt.label}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs text-slate-700 hover:bg-slate-50',
                      checked && 'text-slate-900',
                    )}
                  >
                    <Checkbox checked={checked} onChange={() => onToggle(opt.id)} />
                    <span className="truncate">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

// --- Sidebar Filter ----------------------------------------------------------

interface SidebarFiltersProps {
  facilityOptions: MultiSelectOption[];
  selectedFacilities: Set<number>;
  onToggleFacility: (id: number) => void;
  onSelectAllFacilities: (visibleIds?: number[]) => void;
  onClearAllFacilities: () => void;
  isFacilityLocked?: boolean;
  scheduleSectionsByType: Record<Resource['resourceType'], ScheduleTypeOption[]>;
  scheduleSectionTitle: (resourceType: Resource['resourceType']) => string;
  selectedSchedules: number[];
  onToggleSchedule: (id: number) => void;
  onSelectAllSchedulesOfType: (
    resourceType: Resource['resourceType'],
    visibleIds?: number[],
  ) => void;
  onClearSchedulesOfType: (resourceType: Resource['resourceType']) => void;
  showCancelled: boolean;
  onToggleCancelled: () => void;
  showNoShows: boolean;
  onToggleNoShows: () => void;
  showWeekends: boolean;
  onToggleWeekends: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  // Display settings
  displayMode: string;
  onDisplayModeChange: (v: string | null) => void;
  blockDisplayStyle: 'overlay' | 'side-by-side';
  onBlockDisplayStyleChange: (v: 'overlay' | 'side-by-side' | null) => void;
  showHours: number;
  onShowHoursChange: (v: number | null) => void;
  startHour: number;
  onStartHourChange: (v: number | null) => void;
  slotMinutes: number;
  onSlotMinutesChange: (v: number | null) => void;
  showNoResource: boolean;
  onToggleNoResource: () => void;
  showInfoPopups: boolean;
  onToggleInfoPopups: () => void;
  showBlockInfoPopups: boolean;
  onToggleBlockInfoPopups: () => void;
  privacyMode: boolean;
  onTogglePrivacyMode: () => void;
  // Legacy apptCountToggle / apptHoursToggle / apptQuoteToggle
  apptCountToggle: boolean;
  onToggleApptCount: () => void;
  apptHoursToggle: boolean;
  onToggleApptHours: () => void;
  apptQuoteToggle: boolean;
  onToggleApptQuote: () => void;
  canSeeQuoteTotals: boolean;
  // Mini calendar
  currentDate: Date;
  onMiniCalendarDateClick: (date: Date) => void;
}

function SidebarFilters({
  facilityOptions,
  selectedFacilities,
  onToggleFacility,
  onSelectAllFacilities,
  onClearAllFacilities,
  isFacilityLocked = false,
  scheduleSectionsByType,
  scheduleSectionTitle,
  selectedSchedules,
  onToggleSchedule,
  onSelectAllSchedulesOfType,
  onClearSchedulesOfType,
  showCancelled,
  onToggleCancelled,
  showNoShows,
  onToggleNoShows,
  showWeekends,
  onToggleWeekends,
  collapsed,
  onToggleCollapse,
  // Display settings
  displayMode,
  onDisplayModeChange,
  blockDisplayStyle,
  onBlockDisplayStyleChange,
  showHours,
  onShowHoursChange,
  startHour,
  onStartHourChange,
  slotMinutes,
  onSlotMinutesChange,
  showNoResource,
  onToggleNoResource,
  showInfoPopups,
  onToggleInfoPopups,
  showBlockInfoPopups,
  onToggleBlockInfoPopups,
  privacyMode,
  onTogglePrivacyMode,
  apptCountToggle,
  onToggleApptCount,
  apptHoursToggle,
  onToggleApptHours,
  apptQuoteToggle,
  onToggleApptQuote,
  canSeeQuoteTotals,
  // Mini calendar
  currentDate,
  onMiniCalendarDateClick,
}: SidebarFiltersProps) {
  // Build the selected-id Set once per render so each per-type section can
  // share the same instance (avoids re-creating it inside the .map below).
  const selectedScheduleSet = useMemo(() => new Set(selectedSchedules), [selectedSchedules]);

  if (collapsed) {
    return (
      <div className="flex-shrink-0 w-10 border-r border-slate-200 bg-slate-50 flex flex-col items-center pt-3">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-500 transition-colors"
          title="Show filters"
          aria-label="Show filters"
        >
          <Filter className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 w-56 border-r border-slate-200 bg-slate-50 overflow-y-auto py-6">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Filters</span>
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-0.5 rounded hover:bg-slate-200 text-slate-400 transition-colors"
          title="Hide filters"
          aria-label="Hide filters"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Mini Calendar */}
      <MiniCalendar currentDate={currentDate} onDateClick={onMiniCalendarDateClick} />

      {/* Display Settings */}
      <div className="px-3 py-2 border-b border-slate-200">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Display</p>

        <div className="space-y-1.5">
          <Select<string>
            options={DISPLAY_MODE_OPTIONS}
            value={displayMode}
            onChange={onDisplayModeChange}
            placeholder="Show..."
            className="!py-1 !px-2 !text-xs"
          />

          <Select<'overlay' | 'side-by-side'>
            options={BLOCK_DISPLAY_STYLE_OPTIONS}
            value={blockDisplayStyle}
            onChange={onBlockDisplayStyleChange}
            placeholder="Block layout"
            className="!py-1 !px-2 !text-xs"
          />

          <div className="grid grid-cols-2 gap-1.5">
            <Select<number>
              options={SHOW_HOURS_OPTIONS}
              value={showHours}
              onChange={onShowHoursChange}
              placeholder="Hours"
              className="!py-1 !px-2 !text-xs"
            />
            <Select<number>
              options={START_HOUR_OPTIONS}
              value={startHour}
              onChange={onStartHourChange}
              placeholder="From"
              className="!py-1 !px-2 !text-xs"
            />
          </div>

          <Select<number>
            options={SLOT_MINUTES_OPTIONS}
            value={slotMinutes}
            onChange={onSlotMinutesChange}
            placeholder="Increment"
            className="!py-1 !px-2 !text-xs"
          />
        </div>
      </div>

      {/* Facility filter */}
      {facilityOptions.length > 0 && (
        <div className="px-3 py-2 border-b border-slate-200">
          <FilterSection
            title="Facilities"
            options={facilityOptions}
            selected={selectedFacilities}
            onToggle={onToggleFacility}
            onSelectAll={onSelectAllFacilities}
            onClearAll={onClearAllFacilities}
            disabled={isFacilityLocked}
            badge={isFacilityLocked ? 'Global' : undefined}
          />
        </div>
      )}

      {SCHEDULE_TYPE_ORDER.some((rt) => (scheduleSectionsByType[rt]?.length ?? 0) > 0) && (
        <div className="px-3 py-2 border-b border-slate-200">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Schedules</p>
          <div className="space-y-1.5">
            {SCHEDULE_TYPE_ORDER.map((rt) => {
              const opts = scheduleSectionsByType[rt] ?? [];
              if (opts.length === 0) return null;
              return (
                <ScheduleTypeSection
                  key={rt}
                  resourceType={rt}
                  title={scheduleSectionTitle(rt)}
                  options={opts}
                  selectedIds={selectedScheduleSet}
                  onToggle={onToggleSchedule}
                  onSelectAll={onSelectAllSchedulesOfType}
                  onClearAll={onClearSchedulesOfType}
                />
              );
            })}
          </div>
        </div>
      )}


      {/* Toggle options */}
      <div className="px-3 py-2 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showCancelled} onChange={onToggleCancelled} />
          <span>Show Cancelled</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showNoShows} onChange={onToggleNoShows} />
          <span>Show No Shows</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showWeekends} onChange={onToggleWeekends} />
          <span>Show Weekends</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showNoResource} onChange={onToggleNoResource} />
          <span>Appts with No Resources</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showInfoPopups} onChange={onToggleInfoPopups} />
          <span>Appt Info Pop-ups</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={showBlockInfoPopups} onChange={onToggleBlockInfoPopups} />
          <span>Block Info Pop-ups</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
          <Checkbox checked={privacyMode} onChange={onTogglePrivacyMode} />
          <span>Privacy Mode</span>
        </label>
        {/* Legacy totals toggles (users_filters_sched: apptCountToggle,
            apptHoursToggle, apptQuoteToggle). Quote is gated behind
            user_level > 2 just like the legacy header emit. */}
        <div className="pt-2 mt-2 border-t border-slate-200">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
            Column Totals
          </p>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
            <Checkbox checked={apptCountToggle} onChange={onToggleApptCount} />
            <span>Appt Count</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
            <Checkbox checked={apptHoursToggle} onChange={onToggleApptHours} />
            <span>Hours</span>
          </label>
          {canSeeQuoteTotals && (
            <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 hover:text-slate-900">
              <Checkbox checked={apptQuoteToggle} onChange={onToggleApptQuote} />
              <span>Quote Total</span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Main page ---------------------------------------------------------------

export default function SchedulePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { isAdmin, hasPermission } = useAuth();
  // Resolved IANA timezone for the active staff user. Source order
  // (server-side, mirrors legacy `legacyOffsetHours`):
  //   1) `users_timezones` row for this user when active=1 + timezoneID > 0
  //   2) `aa_accounts.timezone` (i.e. "Account default" or no row)
  //   3) Browser-resolved zone as a last-resort fallback.
  // Every appointment time label, day-bucket key, visible-window filter,
  // and now-line in this page renders against this zone.
  const userTz = useUserTimezone();
  const canCreateSharedPreset = hasPermission(22);
  const [searchParams, setSearchParams] = useSearchParams();
  const updateAIContext = usePageContext('Schedule');
  // Legacy `$roleTitleDoctor`/`$roleTitleCoordinator`/`$roleTitleNurse` —
  // configured per account via aa_role. Used as the per-type Schedules section
  // headers below (e.g. "Physician" / "ECC" / "Provider").
  const { roles } = useRoleTitles();
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null);
  const [presetModal, setPresetModal] = useState<PresetModalState | null>(null);
  const [presetDraftName, setPresetDraftName] = useState('');
  const [presetDraftShared, setPresetDraftShared] = useState(false);
  const preferenceHydratedRef = React.useRef(false);
  const lastSavedPreferenceRef = React.useRef<string | null>(null);
  const autoSaveSeenInitialRef = React.useRef(false);

  const hydrateFromPreference = useCallback((preference: SchedulerPreference) => {
    setViewMode(preference.viewMode);
    setSelectedSchedules(preference.scheduleResourceIds);
    setSelectedFacilities(new Set(preference.facilityIds));
    setShowCancelled(preference.showCancelled);
    setShowNoShows(preference.showNoShows ?? false);
    setShowWeekends(preference.showWeekends);
    setDisplayMode(preference.displayMode);
    setShowHours(preference.showHours);
    setStartHour(preference.startHour);
    setSlotMinutes(preference.slotMinutes);
    setShowNoResource(preference.showNoResource);
    setShowInfoPopups(preference.showInfoPopups);
    setShowBlockInfoPopups(preference.showBlockInfoPopups);
    setPrivacyMode(preference.privacyMode);
    setBlockDisplayStyle(preference.blockDisplayStyle);
    if (typeof preference.apptCountToggle === 'boolean') setApptCountToggle(preference.apptCountToggle);
    if (typeof preference.apptHoursToggle === 'boolean') setApptHoursToggle(preference.apptHoursToggle);
    if (typeof preference.apptQuoteToggle === 'boolean') setApptQuoteToggle(preference.apptQuoteToggle);
    setOverviewOpenState((prev) => ({
      scheduled: typeof preference.overviewScheduledOpen === 'boolean' ? preference.overviewScheduledOpen : prev.scheduled,
      contactAttempted: typeof preference.overviewContactAttemptedOpen === 'boolean' ? preference.overviewContactAttemptedOpen : prev.contactAttempted,
      confirmed: typeof preference.overviewConfirmedOpen === 'boolean' ? preference.overviewConfirmedOpen : prev.confirmed,
      checkedIn: typeof preference.overviewCheckedInOpen === 'boolean' ? preference.overviewCheckedInOpen : prev.checkedIn,
      checkedBack: typeof preference.overviewCheckedBackOpen === 'boolean' ? preference.overviewCheckedBackOpen : prev.checkedBack,
      checkedOut: typeof preference.overviewCheckedOutOpen === 'boolean' ? preference.overviewCheckedOutOpen : prev.checkedOut,
      cancelled: typeof preference.overviewCanceledOpen === 'boolean' ? preference.overviewCanceledOpen : prev.cancelled,
      noShow: typeof preference.overviewNoShowOpen === 'boolean' ? preference.overviewNoShowOpen : prev.noShow,
      waitList: typeof preference.overviewWaitingListOpen === 'boolean' ? preference.overviewWaitingListOpen : prev.waitList,
    }));
    if (preference.printOptions) {
      setPrintFlags((prev) => {
        const next = { ...prev };
        PRINT_OPTION_META.forEach((meta) => {
          // `unknown`, not Record<string, unknown>: the field is typed boolean but
          // the wire still sends legacy 1/0 and '1'/'0', which the branches below
          // handle. Widening the VALUE keeps those checks meaningful; asserting the
          // OBJECT into a Record was rejected outright.
          const raw: unknown = preference.printOptions[meta.key];
          if (typeof raw === 'boolean') next[meta.index] = raw;
          else if (raw === 1 || raw === '1') next[meta.index] = true;
          else if (raw === 0 || raw === '0') next[meta.index] = false;
        });
        return next;
      });
    }
    lastSavedPreferenceRef.current = JSON.stringify(preferencePayloadFromPreference(preference));
  }, []);

  // -- View state -------------------------------------------------------------
  // CT-13 (P2): deep-link `?date=YYYY-MM-DD` + `?view=`. If date is valid we
  // seed week/month/day anchors so `/schedule?view=day&date=2026-04-20` lands
  // on the right day.
  const urlDateSeed = React.useMemo(() => {
    const raw = searchParams.get('date');
    if (!raw) return null;
    const parsed = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [searchParams]);
  const [weekStart, setWeekStart]         = useState<Date>(() => getWeekMonday(urlDateSeed ?? new Date()));
  const [viewMode, setViewMode]           = useState<ScheduleViewMode>(
    () => parseScheduleView(searchParams.get('view')) ?? 'month',
  );
  const urlView = searchParams.get('view');
  useEffect(() => {
    const parsed = parseScheduleView(urlView);
    if (parsed) {
      setViewMode(parsed);
    } else if (urlView === null) {
      setViewMode('month');
    }
  }, [urlView]);
  // Every in-page view switch goes through here so `?view=` follows the view on screen. Without
  // it, arriving from the sidebar's Week View and then pressing Day left `?view=week` in the URL
  // and the Week View row lit. `replace` keeps view flips out of the back-button history.
  const showViewInUrl = useCallback((mode: ScheduleViewMode) => {
    const next = withScheduleView(searchParams, mode);
    if (next !== searchParams) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const selectViewMode = useCallback((mode: ScheduleViewMode) => {
    setViewMode(mode);
    showViewInUrl(mode);
  }, [showViewInUrl]);
  const [dayOffset, setDayOffset]         = useState<number>(() => {
    // When URL specifies a date, anchor to that day's offset within the
    // computed Monday-anchored week.
    if (urlDateSeed) {
      const monday = getWeekMonday(urlDateSeed);
      const days = Math.round((urlDateSeed.getTime() - monday.getTime()) / (24 * 60 * 60 * 1000));
      return Math.max(0, Math.min(6, days));
    }
    // Default: today's offset within the current week. Bug fix: previously
    // returned 0 (Monday) so first-load + refresh always landed on Monday
    // instead of today, which the user reported as "Today thinks it's
    // yesterday" (legacy `calendar-test.php` initial seed used `today()`).
    const now = new Date();
    const dow = now.getDay(); // 0 = Sun
    return dow === 0 ? 6 : dow - 1;
  }); // for day-view
  const [monthDate, setMonthDate]         = useState<Date>(() => urlDateSeed ?? new Date()); // for month-view

  // -- Filter state -----------------------------------------------------------
  const [selectedSchedules, setSelectedSchedules]   = useState<number[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<Set<number>>(new Set());
  const [showCancelled, setShowCancelled]           = useState(false);
  const [showNoShows, setShowNoShows]               = useState(false);
  const [showWeekends, setShowWeekends]             = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed]     = useState(false);

  // -- Display settings state -------------------------------------------------
  const [displayMode, setDisplayMode]     = useState<string>('both');
  const [showHours, setShowHours]         = useState<number>(12);
  const [startHour, setStartHour]         = useState<number>(7);
  const [slotMinutes, setSlotMinutes]     = useState<number>(30);
  const [showNoResource, setShowNoResource] = useState(true);
  const [showInfoPopups, setShowInfoPopups] = useState(true);
  // Legacy blockInfoPopUpToggle — separate from apptInfoPopUpToggle so users
  // can suppress block-time popups independently (calendar-test.php).
  const [showBlockInfoPopups, setShowBlockInfoPopups] = useState(true);
  const [privacyMode, setPrivacyMode]       = useState(false);
  const [blockDisplayStyle, setBlockDisplayStyle] = useState<'overlay' | 'side-by-side'>('overlay');

  // -- Modal state ------------------------------------------------------------
  const [detailAppt, setDetailAppt]       = useState<CalendarAppointment | null>(null);
  const [createDate, setCreateDate]       = useState<string | null>(null);
  const [createTime, setCreateTime]       = useState<string | null>(null);
  const [createResourceIds, setCreateResourceIds] = useState<number[]>([]);
  const [createOpen, setCreateOpen]       = useState(false);
  const [checkingInId, setCheckingInId]   = useState<number | null>(null);
  const [findAvailableOpen, setFindAvailableOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockRange, setBlockRange] = useState<{ start?: string; end?: string }>({});
  const [editBlockId, setEditBlockId] = useState<number | null>(null);
  const [editBlockOccurrenceDate, setEditBlockOccurrenceDate] = useState<string | undefined>(undefined);
  // Convert-Block-to-Appointment seed (legacy `convertBlockToAppt`,
  // calendar-test.php L6714-L6733). When set, the create modal opens
  // with these defaults pre-filled instead of an empty form.
  const [convertSeed, setConvertSeed] = useState<BlockToAppointmentSeed['seed'] | null>(null);

  // Print modal — mirrors legacy scheduler_print_options_*.php.
  // Legacy form carries 21 independent checkboxes + orientation + waitlist + document packet.
  // Each `printOptionN` is an independent boolean — NOT a layout enum.
  // See calendar-test.php 6054-6165 for the exact legacy mapping.
  const [printOpen, setPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState<'range' | 'waitlist' | 'documents'>('range');
  // P1-12: legacy documentIDSelect — value is {id}|packet or {id}|document.
  const [printDocumentValue, setPrintDocumentValue] = useState<string>('');
  const [printStartDate, setPrintStartDate] = useState<string>(() => toDateStr(new Date()));
  const [printEndDate, setPrintEndDate] = useState<string>(() => toDateStr(new Date()));
  const [printIncludeCancelled, setPrintIncludeCancelled] = useState<boolean>(false);
  const [printIncludeBlocks, setPrintIncludeBlocks] = useState<boolean>(true);
  // 21 independent legacy flags. Indexes 1..21 correspond to legacy field names
  // captured in PRINT_OPTION_META below.
  const [printFlags, setPrintFlags] = useState<Record<number, boolean>>(() => ({
    1: true, 2: false, 3: false, 4: false, 5: true,
    6: true, 7: true, 8: true, 9: true, 10: true,
    11: false, 12: false, 13: false, 14: false, 15: false,
    16: false, 17: false, 18: false, 19: false, 20: false,
    21: false,
  }));
  const [findAvailableTypeId, setFindAvailableTypeId] = useState<number | undefined>();
  const [findAvailableResourceId, setFindAvailableResourceId] = useState<number | undefined>();
  const [findAvailableResourceIds, setFindAvailableResourceIds] = useState<number[]>([]);
  const [findAvailableFacilityId, setFindAvailableFacilityId] = useState<number | undefined>();
  const [findAvailableDuration, setFindAvailableDuration] = useState<number>(30);
  const [findAvailableDateFrom, setFindAvailableDateFrom] = useState<string>('');
  const [findAvailableDateTo, setFindAvailableDateTo] = useState<string>('');
  const [findAvailableTimeStart, setFindAvailableTimeStart] = useState<string>('');
  const [findAvailableTimeEnd, setFindAvailableTimeEnd] = useState<string>('');
  const [findAvailableIncludeWeekends, setFindAvailableIncludeWeekends] = useState<boolean>(false);
  const [findAvailableApptTypeGroupId, setFindAvailableApptTypeGroupId] = useState<number | undefined>();
  // Legacy blocksOnlySelect toggle — false means search ALL availability (blocks
  // + empty gaps); true restricts to blockTime windows only (calendar-test.php).
  const [findAvailableBlocksOnly, setFindAvailableBlocksOnly] = useState<boolean>(false);

  // Legacy header/month totals toggles (users_filters_sched apptCountToggle /
  // apptHoursToggle / apptQuoteToggle). See P1-4.
  const [apptCountToggle, setApptCountToggle] = useState<boolean>(true);
  const [apptHoursToggle, setApptHoursToggle] = useState<boolean>(true);
  const [apptQuoteToggle, setApptQuoteToggle] = useState<boolean>(false);

  // Legacy overview collapsed flags (users_filters_sched ov*Open). Each key
  // corresponds to a group title emitted by overviewGroups. Persisted so the
  // user's collapsed state survives page reloads (P1-5). `contactAttempted` is
  // v5-only: legacy had no such bucket, so its ovContactAttemptedOpen column is
  // added by the FTP migration runners rather than inherited from legacy.
  const [overviewOpenState, setOverviewOpenState] = useState<{
    scheduled: boolean; contactAttempted: boolean; confirmed: boolean; checkedIn: boolean;
    checkedBack: boolean; checkedOut: boolean; cancelled: boolean; noShow: boolean; waitList: boolean;
  }>({
    scheduled: true, contactAttempted: true, confirmed: true, checkedIn: true, checkedBack: true,
    checkedOut: true, cancelled: true, noShow: true, waitList: true,
  });

  // Current-time line tick (60s) — matches legacy `setInterval` in scheduler_display_tz.php.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => window.clearInterval(t);
  }, []);

  // Previous-day banner — mirrors performYesterdayCheck / scheduler_previous_day_check.php.
  // Legacy parity (calendar-test.php 1098-1123): auto-runs only when
  //   (a) aa_accounts_calendar.displayDailyAlert === 1 AND
  //   (b) users_filters_sched.yesterdayCheckDate != today
  // Dismiss writes `yesterdayCheckDate` into scheduler preferences (NOT
  // localStorage) so the suppression is per-user and survives new devices.
  const todayKey = useMemo(() => toDateStr(new Date(nowTick)), [nowTick]);
  // Cached calendar settings carry the displayDailyAlert account flag.
  const dailyAlertAccountFlagQuery = useQuery<SchedulerCalendarSettings>({
    queryKey: ['scheduler-calendar-settings'],
    queryFn: getSchedulerCalendarSettings,
    staleTime: Infinity,
  });
  const dailyAlertEnabled = dailyAlertAccountFlagQuery.data?.displayDailyAlert === true;
  // CT-06 (P2): legacy calendar-test.php L2428 — `timezone==10` sentinel
  // suppresses the current-time marker. Account-wide.
  const hideNowLine = dailyAlertAccountFlagQuery.data?.accountTimezoneId === 10;

  const preferenceQuery = useQuery({
    queryKey: ['scheduler-preference'],
    queryFn: getSchedulerPreference,
    staleTime: 0,
  });

  // P1-6: derive "checked today already?" from the persisted
  // yesterdayCheckDate preference instead of localStorage.
  const yesterdayCheckedDate = preferenceQuery.data?.yesterdayCheckDate ?? null;
  const previousDayChecked = yesterdayCheckedDate !== null && yesterdayCheckedDate === todayKey;
  const previousDayCheckQuery = useQuery<PreviousDayCheckResponse>({
    queryKey: ['scheduler-previous-day-check', todayKey],
    queryFn: () => getPreviousDayCheck(),
    enabled: dailyAlertEnabled && !previousDayChecked,
    staleTime: 5 * 60 * 1000,
  });
  const previousDayBanner = previousDayCheckQuery.data;
  const previousDayVisible =
    dailyAlertEnabled
    && !previousDayChecked
    && previousDayBanner !== undefined
    && previousDayBanner.totalCount > 0;

  const presetsQuery = useQuery({
    queryKey: ['scheduler-presets'],
    queryFn: listSchedulerPresets,
    staleTime: 0,
  });

  const updatePreferenceMutation = useMutation({
    mutationFn: updateSchedulerPreference,
    onSuccess: (preference) => {
      qc.setQueryData(['scheduler-preference'], preference);
      lastSavedPreferenceRef.current = JSON.stringify(preferencePayloadFromPreference(preference));
    },
    onError: (error) => {
      toast.error(
        'Failed to save scheduler settings',
        error instanceof Error ? error.message : 'The scheduler preferences could not be saved.',
      );
    },
  });

  const applyPresetMutation = useMutation({
    mutationFn: applySchedulerPreset,
    onSuccess: (preference, presetId) => {
      qc.setQueryData(['scheduler-preference'], preference);
      void qc.invalidateQueries({ queryKey: ['scheduler-presets'] });
      hydrateFromPreference(preference);
      showViewInUrl(preference.viewMode);
      autoSaveSeenInitialRef.current = false;
      setPresetModal(null);
      setPresetDraftName('');
      setPresetDraftShared(false);
      toast.success('Preset applied');
      setSelectedPresetId(presetId);
      void presetsQuery.refetch();
    },
    onError: (error) => {
      toast.error(
        'Failed to apply preset',
        error instanceof Error ? error.message : 'The scheduler preset could not be applied.',
      );
    },
  });

  const createPresetMutation = useMutation({
    mutationFn: createSchedulerPreset,
    onSuccess: (preset) => {
      setPresetModal(null);
      setPresetDraftName('');
      setPresetDraftShared(false);
      toast.success('Preset saved');
      setSelectedPresetId(preset.schedulerPreferenceId);
      void qc.invalidateQueries({ queryKey: ['scheduler-presets'] });
      void presetsQuery.refetch();
    },
    onError: (error) => {
      toast.error(
        'Failed to save preset',
        error instanceof Error ? error.message : 'The scheduler preset could not be saved.',
      );
    },
  });

  const updatePresetMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SchedulerPreferencePayload }) =>
      updateSchedulerPreset(id, payload),
    onSuccess: () => {
      setPresetModal(null);
      setPresetDraftName('');
      setPresetDraftShared(false);
      toast.success('Preset updated');
      void qc.invalidateQueries({ queryKey: ['scheduler-presets'] });
    },
    onError: (error) => {
      toast.error(
        'Failed to update preset',
        error instanceof Error ? error.message : 'The scheduler preset could not be updated.',
      );
    },
  });

  const deletePresetMutation = useMutation({
    mutationFn: deleteSchedulerPreset,
    onSuccess: () => {
      toast.success('Preset deleted');
      void qc.invalidateQueries({ queryKey: ['scheduler-presets'] });
    },
    onError: (error) => {
      toast.error(
        'Failed to delete preset',
        error instanceof Error ? error.message : 'The scheduler preset could not be deleted.',
      );
    },
  });

  // -- Computed time slots from display settings ------------------------------
  const hourEnd = useMemo(() => {
    const computed = startHour + showHours;
    return Math.min(computed, 24);
  }, [startHour, showHours]);

  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let h = startHour; h < hourEnd; h++) {
      for (let m = 0; m < 60; m += slotMinutes) {
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return slots;
  }, [startHour, hourEnd, slotMinutes]);

  // -- Derived dates ----------------------------------------------------------
  const weekDays: Date[] = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const displayDays = useMemo(() => {
    if (viewMode === 'day' || viewMode === 'overview') return [weekDays[dayOffset] ?? weekDays[0]];
    if (viewMode === 'month') return []; // month view doesn't use displayDays
    if (showWeekends) return weekDays;
    return weekDays.filter((_, i) => i < 5); // Mon-Fri
  }, [viewMode, weekDays, dayOffset, showWeekends]);

  const todayStr  = toDateStr(new Date());

  // The current date depends on view mode
  const currentDate = useMemo(() => {
    if (viewMode === 'month') return monthDate;
    if (viewMode === 'day' || viewMode === 'overview') return weekDays[dayOffset] ?? weekDays[0];
    return weekDays[0];
  }, [viewMode, weekDays, dayOffset, monthDate]);

  // Range for the query -- depends on view mode. Sent as the viewer's local
  // midnights, not bare dates: the API reads a bare date as the Pacific day,
  // while `apptsByDay` buckets on the viewer's day, so a non-Pacific viewer lost
  // every appointment past their midnight (SCRUM-2046). Same window length, so
  // the server does the same work. Falls back to the bare date if the zone
  // can't be resolved.
  const rangeStart = useMemo(() => {
    let day: string;
    if (viewMode === 'month') {
      day = toDateStr(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
    } else if (viewMode === 'overview') {
      day = toDateStr(weekDays[dayOffset] ?? weekDays[0]);
    } else {
      day = toDateStr(weekStart);
    }
    return startOfDayInTzIso(day, userTz) ?? day;
  }, [viewMode, weekStart, monthDate, weekDays, dayOffset, userTz]);

  const rangeEnd = useMemo(() => {
    let day: string;
    if (viewMode === 'month') {
      day = toDateStr(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1));
    } else if (viewMode === 'overview') {
      day = toDateStr(addDays(weekDays[dayOffset] ?? weekDays[0], 1));
    } else {
      day = toDateStr(addDays(weekStart, 7));
    }
    return startOfDayInTzIso(day, userTz) ?? day;
  }, [viewMode, weekStart, monthDate, weekDays, dayOffset, userTz]);

  // -- Facilities for filter --------------------------------------------------
  const { data: facilities = [] } = useQuery<Facility[]>({
    queryKey: ['facilities'],
    queryFn:  listFacilities,
    staleTime: 5 * 60 * 1000,
  });

  // -- Appointment types for filter -------------------------------------------
  const { data: appointmentTypes = [] } = useQuery<AppointmentType[]>({
    queryKey: ['appointmentTypes'],
    queryFn:  () => appointmentsService.getAppointmentTypes(),
    staleTime: 5 * 60 * 1000,
  });

  // -- Resources for filter ---------------------------------------------------
  const { data: resources = [] } = useQuery<Resource[]>({
    queryKey: ['resources'],
    queryFn:  () => appointmentsService.getResources(),
    staleTime: 5 * 60 * 1000,
  });

  // aa_accounts_calendar field-visibility settings (legacy infoTags, infoPhoto, etc).
  const calendarSettingsQuery = useQuery<AdminCalendarSettings>({
    queryKey: ['admin-calendar-settings'],
    queryFn: getAdminCalendarSettings,
    staleTime: 10 * 60 * 1000,
  });
  const cardSettings = calendarSettingsQuery.data ?? null;

  // P1-12: document packet/document options for the print modal. Lazy-loaded
  // only when the modal is opened so we don't hit the DB on every page load.
  const printDocumentOptionsQuery = useQuery({
    queryKey: ['scheduler-print-documents'],
    queryFn: getSchedulerPrintDocumentOptions,
    enabled: printOpen && printScope === 'documents',
    staleTime: 5 * 60 * 1000,
  });

  // -- Global facility override -----------------------------------------------
  // When a global facility is selected in the nav, it overrides the user's
  // saved facility filter at runtime without mutating persisted state.
  const globalFacilityId = useEffectiveFacilityId(undefined);
  const isFacilityLocked = useIsFacilityLocked();
  const effectiveSelectedFacilities = useMemo<Set<number>>(() => {
    if (isFacilityLocked && globalFacilityId) return new Set<number>([globalFacilityId]);
    return selectedFacilities;
  }, [isFacilityLocked, globalFacilityId, selectedFacilities]);

  // -- Calendar appointments --------------------------------------------------
  const visibleSelectedSchedules = useMemo(() => {
    if (effectiveSelectedFacilities.size === 0) return selectedSchedules;
    return selectedSchedules.filter((id) => {
      const r = resources.find((res) => res.resourceId === id);
      if (!r) return false;
      if (r.facilityIds.length === 0) return true;
      return r.facilityIds.some((fid) => effectiveSelectedFacilities.has(fid));
    });
  }, [selectedSchedules, resources, effectiveSelectedFacilities]);

  const serverFacilityId = effectiveSelectedFacilities.size === 1
    ? Array.from(effectiveSelectedFacilities)[0]
    : undefined;

  const scheduleResourceIdsForQuery = visibleSelectedSchedules.length > 0 ? visibleSelectedSchedules : undefined;

  const { data: rawAppointments = [], isFetching, isError } = useQuery<CalendarAppointment[]>({
    queryKey: [
      'schedule',
      rangeStart,
      rangeEnd,
      serverFacilityId,
      scheduleResourceIdsForQuery?.join(',') ?? '',
    ],
    queryFn:  () => getCalendarAppointments(
      rangeStart,
      rangeEnd,
      serverFacilityId,
      {
        includeCancelled: true,
        scheduleResourceIds: scheduleResourceIdsForQuery,
      },
    ),
    // CT-05 (P2 polish): legacy grid views do NOT auto-refetch every 60s —
    // only the overview tab polls (180s from scheduler_display_overview.php).
    // The current-time line moves via the local `nowTick` interval, and React
    // Query already invalidates on window focus (refetchOnWindowFocus default),
    // so grid views rely on focus + user action for freshness.
    staleTime: 60 * 1000,
    refetchInterval: viewMode === 'overview' ? 180 * 1000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // The Overview's "Waiting List" panel is fed by its own query, NOT by the
  // day's appointments. A wait-list entry is a patient asking to come in
  // EARLIER than their booked slot, so the whole point is that it stays
  // visible on every day's overview until that slot passes — scoping it to
  // the viewed day would only surface it on the one day it is least useful.
  // Legacy did exactly this with a second `showWaitList = '1'` query
  // (scheduler_display_tz.php 1121-1122, 1262-1366).
  const { data: waitlistAppointments = [] } = useQuery<CalendarAppointment[]>({
    queryKey: [
      'schedule-waitlist',
      serverFacilityId,
      scheduleResourceIdsForQuery?.join(',') ?? '',
    ],
    queryFn: () => getWaitlistAppointments(serverFacilityId, {
      scheduleResourceIds: scheduleResourceIdsForQuery,
    }),
    enabled: viewMode === 'overview',
    staleTime: 60 * 1000,
    refetchInterval: viewMode === 'overview' ? 180 * 1000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const effectiveFindAvailableFacilityId = isFacilityLocked && globalFacilityId
    ? globalFacilityId
    : findAvailableFacilityId;

  const { data: availableSlots = [], isFetching: findingAvailability } = useQuery({
    queryKey: [
      'schedule-find-available',
      findAvailableOpen,
      currentDate.toISOString(),
      findAvailableTypeId,
      findAvailableApptTypeGroupId,
      findAvailableResourceId,
      findAvailableResourceIds.join(','),
      effectiveFindAvailableFacilityId,
      findAvailableDuration,
      findAvailableDateFrom,
      findAvailableDateTo,
      findAvailableTimeStart,
      findAvailableTimeEnd,
      findAvailableIncludeWeekends,
      findAvailableBlocksOnly,
    ],
    queryFn: () => appointmentsService.checkAvailability({
      date: findAvailableDateFrom || toDateStr(currentDate),
      dateFrom: findAvailableDateFrom || undefined,
      dateTo: findAvailableDateTo || undefined,
      typeId: findAvailableTypeId,
      appointmentTypeGroupId: findAvailableApptTypeGroupId,
      resourceId: findAvailableResourceId,
      resourceIds: findAvailableResourceIds.length > 0 ? findAvailableResourceIds : undefined,
      facilityId: effectiveFindAvailableFacilityId,
      duration: findAvailableDuration,
      timeStart: findAvailableTimeStart || undefined,
      timeEnd: findAvailableTimeEnd || undefined,
      includeWeekends: findAvailableIncludeWeekends || undefined,
      blocksOnly: findAvailableBlocksOnly,
    }),
    enabled: findAvailableOpen,
    staleTime: 30 * 1000,
  });

  // Client-side filtering
  const appointments = useMemo(() => {
    let filtered = rawAppointments;

    // Multi-facility filter (only if more than 1 facility selected). Blocks
    // are exempt: legacy `scheduler-display-week-cell.php` 542-595 / 829-833
    // never narrows blocks by facility — `subFiltersFacilities` only gates
    // the appointment loop. Filtering blocks here would silently hide them
    // whenever the user selects multiple facilities.
    if (effectiveSelectedFacilities.size > 1) {
      filtered = filtered.filter((a) => a.isBlock || a.facilityID == null || effectiveSelectedFacilities.has(a.facilityID));
    } else if (isFacilityLocked && globalFacilityId) {
      // Single global override: hard-filter to the overridden facility.
      filtered = filtered.filter((a) => a.isBlock || a.facilityID == null || a.facilityID === globalFacilityId);
    }

    // Hide appointments with no resource if toggle is off. Blocks are exempt:
    // legacy `scheduler-display-week-cell.php` 542-595 short-circuits empty-
    // resource blocks to `displayRecord = 1` regardless of `noResources`.
    if (!showNoResource) {
      filtered = filtered.filter((a) => a.isBlock || eventResources(a).length > 0);
    }

    if (!showCancelled) {
      filtered = filtered.filter((a) => !a.cancelled);
    }
    if (!showNoShows) {
      filtered = filtered.filter((a) => !a.noShow);
    }

    // Display mode filter (appointments vs blocks)
    // "blocks" are appointments where isBlock is true (if the field exists)
    if (displayMode === 'appts') {
      filtered = filtered.filter((a) => !(a as CalendarAppointment & { isBlock?: boolean }).isBlock);
    } else if (displayMode === 'blocks') {
      filtered = filtered.filter((a) => (a as CalendarAppointment & { isBlock?: boolean }).isBlock);
    }

    return filtered;
  }, [rawAppointments, effectiveSelectedFacilities, isFacilityLocked, globalFacilityId, showNoResource, showCancelled, showNoShows, displayMode]);

  // -- AI page context --------------------------------------------------------
  useEffect(() => {
    updateAIContext({
      filters: `View: ${viewMode}, Date: ${currentDate.toLocaleDateString()}`,
      dataSummary: `${appointments.length} appointments visible`,
    });
  }, [viewMode, currentDate, appointments.length, updateAIContext]);

  // -- Check-in mutation ------------------------------------------------------
  const checkInMutation = useMutation({
    mutationFn: (id: number) => checkInAppointment(id),
    onMutate: (id) => setCheckingInId(id),
    onSettled: () => setCheckingInId(null),
    onSuccess: () => {
      toast.success('Patient checked in.');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: () => {
      toast.error('Check-in failed. Please try again.');
    },
  });

  // -- Inline overview workflow mutations (confirm / check-back / check-out) -
  //    Mirrors legacy progressAppointment(id, 'confirm'|'checkback'|'checkout').
  const confirmMutation = useMutation({
    mutationFn: (id: number) => confirmAppointment(id),
    onSuccess: () => {
      toast.success('Appointment confirmed.');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: () => toast.error('Confirm failed. Please try again.'),
  });

  const checkBackMutation = useMutation({
    mutationFn: (id: number) => checkBackAppointment(id),
    onSuccess: () => {
      toast.success('Patient sent back.');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: () => toast.error('Check-back failed. Please try again.'),
  });

  const checkOutMutation = useMutation({
    mutationFn: (id: number) => checkOutAppointment(id),
    onSuccess: () => {
      toast.success('Patient checked out.');
      void qc.invalidateQueries({ queryKey: ['schedule'] });
    },
    onError: () => toast.error('Check-out failed. Please try again.'),
  });

  // -- Build MultiSelectOption arrays -----------------------------------------

  const facilityOptions: MultiSelectOption[] = useMemo(
    () => facilities.filter((f) => f.active).map((f) => ({ id: f.facilityId, label: f.facilityName })),
    [facilities],
  );

  const allTypeOptions: MultiSelectOption[] = useMemo(
    () =>
      appointmentTypes.map((t) => ({
        id: t.appointmentTypeId,
        label: t.appointmentTypeName,
        group: t.appointmentTypeGroupName ?? undefined,
        color: t.appointmentTypeColor ?? undefined,
      })),
    [appointmentTypes],
  );

  const typeOptions: MultiSelectOption[] = useMemo(() => {
    const seen = new Set<string>();
    return allTypeOptions.filter((t) => {
      const key = t.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allTypeOptions]);

  const resourceOptions: MultiSelectOption[] = useMemo(
    () =>
      resources
        .filter((r) => {
          if (!r.active) return false;
          if (effectiveSelectedFacilities.size > 0 && r.facilityIds.length > 0) {
            return r.facilityIds.some((fid) => effectiveSelectedFacilities.has(fid));
          }
          return true;
        })
        .map((r) => ({
          id: r.resourceId,
          label: r.resourceName,
          group: resourceGroupLabel(r.resourceType),
        })),
    [resources, effectiveSelectedFacilities],
  );

  // Per-type Schedules sections (legacy parity — see ScheduleTypeSection
  // above). Derived from the same active-resources list as `resourceOptions`,
  // but bucketed by `resourceType` so each section can render independently.
  const scheduleSectionsByType = useMemo<Record<Resource['resourceType'], ScheduleTypeOption[]>>(() => {
    const buckets: Record<Resource['resourceType'], ScheduleTypeOption[]> = {
      doctor: [],
      coordinator: [],
      nurse: [],
      room: [],
      equipment: [],
      other: [],
    };
    for (const r of resources) {
      if (!r.active) continue;
      if (effectiveSelectedFacilities.size > 0 && r.facilityIds.length > 0) {
        if (!r.facilityIds.some((fid) => effectiveSelectedFacilities.has(fid))) continue;
      }
      buckets[r.resourceType].push({ id: r.resourceId, label: r.resourceName });
    }
    return buckets;
  }, [resources, effectiveSelectedFacilities]);

  const scheduleSectionTitle = useCallback(
    (rt: Resource['resourceType']) => {
      const cap = rt.charAt(0).toUpperCase() + rt.slice(1);
      return roles.find((r) => r.roleType === cap)?.roleTitle || RESOURCE_TYPE_LABELS[rt];
    },
    [roles],
  );

  const selectedPreset = useMemo(
    () => (presetsQuery.data ?? []).find((preset) => preset.schedulerPreferenceId === selectedPresetId) ?? null,
    [presetsQuery.data, selectedPresetId],
  );
  const presetOptions = useMemo(
    () => (presetsQuery.data ?? []).map((preset) => ({
      value: preset.schedulerPreferenceId,
      label: preset.shared && !preset.ownedByCurrentUser ? `${preset.name} (shared)` : preset.name,
    })),
    [presetsQuery.data],
  );

  const printOptionsPayload = useMemo<SchedulerPrintOptions>(() =>
    PRINT_OPTION_META.reduce<SchedulerPrintOptions>((acc, meta) => {
      acc[meta.key] = !!printFlags[meta.index];
      return acc;
    }, {} as SchedulerPrintOptions),
  [printFlags]);

  const schedulerPayload = useMemo(
    () =>
      preferencePayloadFromState({
        viewMode,
        displayMode,
        showHours,
        startHour,
        slotMinutes,
        showWeekends,
        showCancelled,
        showNoShows,
        showNoResource,
        showInfoPopups,
        showBlockInfoPopups,
        privacyMode,
        blockDisplayStyle,
        selectedScheduleIds: selectedSchedules,
        selectedFilterResourceIds: [],
        selectedFacilityIds: Array.from(selectedFacilities),
        selectedTypeIds: [],
        printOptions: printOptionsPayload,
        apptCountToggle,
        apptHoursToggle,
        apptQuoteToggle,
        overviewOpenState,
      }),
    [
      viewMode,
      displayMode,
      showHours,
      startHour,
      slotMinutes,
      showWeekends,
      showCancelled,
      showNoShows,
      showNoResource,
      showInfoPopups,
      showBlockInfoPopups,
      privacyMode,
      blockDisplayStyle,
      selectedSchedules,
      selectedFacilities,
      printOptionsPayload,
      apptCountToggle,
      apptHoursToggle,
      apptQuoteToggle,
      overviewOpenState,
    ],
  );

  // Hydrate local state from the saved preference exactly once on load.
  // Subsequent refetches (window-focus, cache invalidation) are ignored
  // so they never overwrite in-progress user edits.
  useEffect(() => {
    if (preferenceHydratedRef.current) return;
    const preference = preferenceQuery.data;
    if (!preference) return;
    hydrateFromPreference(preference);
    // An explicit `?view=` (the Day View / Week View nav rows, a deep link, or a view picked
    // before the preference arrived) outranks the saved last-used view. Otherwise opening Week
    // View from the nav showed the saved Day view while the URL and nav still said Week.
    const explicitView = parseScheduleView(urlView);
    if (explicitView) setViewMode(explicitView);
    preferenceHydratedRef.current = true;
  }, [preferenceQuery.data, hydrateFromPreference, urlView]);

  // Set the selected preset dropdown to match the active preference name.
  // No auto-apply — just load what's saved and use it as-is.
  useEffect(() => {
    if (!preferenceQuery.data || !presetsQuery.data) return;
    const matchingPreset = presetsQuery.data.find((preset) => preset.name === preferenceQuery.data?.name);
    setSelectedPresetId(matchingPreset?.schedulerPreferenceId ?? null);
  }, [preferenceQuery.data, presetsQuery.data]);

  // The mutation object and preferenceQuery.data change identity on every render
  // (useMutation returns a fresh object, and onSuccess calls setQueryData which
  // produces a new data reference). Putting them in the effect's dep array caused
  // a render → save → setQueryData → render loop that spammed `/preferences`
  // every ~second. Hold them in refs so the effect only re-runs when the UI
  // state the user actually controls (`schedulerPayload`) changes.
  const updatePreferenceMutateRef = React.useRef(updatePreferenceMutation.mutate);
  updatePreferenceMutateRef.current = updatePreferenceMutation.mutate;
  const updatePreferencePendingRef = React.useRef(updatePreferenceMutation.isPending);
  updatePreferencePendingRef.current = updatePreferenceMutation.isPending;
  const preferenceDataRef = React.useRef(preferenceQuery.data);
  preferenceDataRef.current = preferenceQuery.data;

  useEffect(() => {
    if (!preferenceHydratedRef.current) return;

    // The very first run after hydration just records the baseline —
    // never saves. This is bulletproof regardless of React batching or
    // timing because we literally skip the first invocation.
    if (!autoSaveSeenInitialRef.current) {
      autoSaveSeenInitialRef.current = true;
      lastSavedPreferenceRef.current = JSON.stringify(
        preferenceDataRef.current
          ? { ...preferencePayloadFromPreference(preferenceDataRef.current), ...schedulerPayload }
          : schedulerPayload,
      );
      return;
    }

    const basePayload = preferenceDataRef.current
      ? preferencePayloadFromPreference(preferenceDataRef.current)
      : null;
    const mergedPayload: SchedulerPreferencePayload = basePayload
      ? { ...basePayload, ...schedulerPayload }
      : schedulerPayload;

    const serialized = JSON.stringify(mergedPayload);
    if (serialized === lastSavedPreferenceRef.current || updatePreferencePendingRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      updatePreferenceMutateRef.current(mergedPayload);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [schedulerPayload]);

  // -- Filter handlers --------------------------------------------------------

  const handleToggleFacility = useCallback((id: number) => {
    setSelectedFacilities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // `visibleIds` is only supplied when the section's search box is narrowing the
  // list; then we merge those in instead of replacing, so a search + "Select all"
  // does not wipe facilities the search happens to be hiding.
  const handleSelectAllFacilities = useCallback((visibleIds?: number[]) => {
    if (visibleIds) {
      setSelectedFacilities((prev) => new Set([...prev, ...visibleIds]));
      return;
    }
    setSelectedFacilities(new Set(facilities.filter((f) => f.active).map((f) => f.facilityId)));
  }, [facilities]);

  const handleClearAllFacilities = useCallback(() => {
    setSelectedFacilities(new Set());
  }, []);

  const handleToggleSchedule = useCallback((id: number) => {
    setSelectedSchedules((prev) => (
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
    ));
  }, []);

  // Per-type select/clear (legacy parity — each Schedules sub-section has its
  // own check-all/uncheck-all icons in calendar-test.php). The ordered
  // `selectedSchedules` array drives column ordering, so check-all-of-type
  // appends only the missing ids to the END (matching toggle-on behavior),
  // and clear-of-type filters them out without touching the other types.
  // `visibleIds` is only supplied when the section's search box is narrowing the
  // list, in which case only those are appended.
  const handleSelectAllSchedulesOfType = useCallback(
    (rt: Resource['resourceType'], visibleIds?: number[]) => {
      const idsForType = visibleIds ?? resources
        .filter((r) => r.active && r.resourceType === rt)
        .map((r) => r.resourceId);
      setSelectedSchedules((prev) => {
        const set = new Set(prev);
        const additions = idsForType.filter((id) => !set.has(id));
        return additions.length === 0 ? prev : [...prev, ...additions];
      });
    },
    [resources],
  );

  const handleClearSchedulesOfType = useCallback(
    (rt: Resource['resourceType']) => {
      const remove = new Set(
        resources.filter((r) => r.resourceType === rt).map((r) => r.resourceId),
      );
      setSelectedSchedules((prev) => prev.filter((id) => !remove.has(id)));
    },
    [resources],
  );

  // Column drag-and-drop reorder (mirrors legacy jQuery UI sortable on headers)
  const dragColumnIdRef = React.useRef<number | null>(null);

  const handleColumnDrop = useCallback((targetId: number) => {
    const draggedId = dragColumnIdRef.current;
    if (draggedId == null || draggedId === targetId) return;
    setSelectedSchedules((prev) => {
      const fromIdx = prev.indexOf(draggedId);
      const toIdx = prev.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedId);
      return next;
    });
    dragColumnIdRef.current = null;
  }, []);


  // -- Handlers ---------------------------------------------------------------

  const handlePrev = useCallback(() => {
    if (viewMode === 'month') {
      setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    } else if (viewMode === 'week') {
      setWeekStart((d) => addDays(d, -7));
    } else {
      // day or overview
      if (dayOffset > 0) {
        setDayOffset((n) => n - 1);
      } else {
        setWeekStart((d) => addDays(d, -7));
        setDayOffset(6);
      }
    }
  }, [viewMode, dayOffset]);

  const handleNext = useCallback(() => {
    if (viewMode === 'month') {
      setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    } else if (viewMode === 'week') {
      setWeekStart((d) => addDays(d, 7));
    } else {
      // day or overview
      if (dayOffset < 6) {
        setDayOffset((n) => n + 1);
      } else {
        setWeekStart((d) => addDays(d, 7));
        setDayOffset(0);
      }
    }
  }, [viewMode, dayOffset]);

  const handleToday = useCallback(() => {
    const now = new Date();
    const monday = getWeekMonday(now);
    setWeekStart(monday);
    setMonthDate(new Date(now.getFullYear(), now.getMonth(), 1));
    const todayDayOfWeek = now.getDay();
    const offset = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
    setDayOffset(offset);
  }, []);

  const handleOpenAppt = useCallback((appt: CalendarAppointment) => {
    if (appt.isBlock) {
      // Route block-time clicks to the dedicated block modal. The
      // occurrence date is derived from the rendered start so the modal
      // can offer the legacy "Delete this occurrence" /
      // "Convert to single-date block" actions for recurring series.
      // Use the user's local date (not the UTC-Z prefix) so a late-evening
      // block that crosses UTC midnight still picks the user's day.
      setEditBlockId(appt.id);
      setEditBlockOccurrenceDate(appt.start ? dateInTz(appt.start, userTz) : undefined);
      setBlockOpen(true);
      return;
    }
    setDetailAppt(appt);
  }, []);

  const handleOpenCreate = useCallback((dateStr: string, _time: string, resourceIds?: number[]) => {
    setCreateDate(dateStr);
    setCreateTime(_time);
    setCreateResourceIds(resourceIds ?? []);
    setCreateOpen(true);
  }, []);

  const handleCheckIn = useCallback((appt: CalendarAppointment) => {
    checkInMutation.mutate(appt.id);
  }, [checkInMutation]);

  const handleCreateSuccess = useCallback((_appointment: Appointment) => {
    setCreateOpen(false);
    setCreateDate(null);
    setCreateTime(null);
    setCreateResourceIds([]);
    void qc.invalidateQueries({ queryKey: ['schedule'] });
  }, [qc]);

  const handleDetailUpdated = useCallback(() => {
    setDetailAppt(null);
    void qc.invalidateQueries({ queryKey: ['schedule'] });
  }, [qc]);

  /** Navigate from month view or mini calendar to day view for a specific date */
  const handleMonthDayClick = useCallback((date: Date) => {
    const monday = getWeekMonday(date);
    setWeekStart(monday);
    const dow = date.getDay();
    const offset = dow === 0 ? 6 : dow - 1;
    setDayOffset(offset);
    selectViewMode('day');
  }, [selectViewMode]);

  /** Navigate from mini calendar - go to that date in day view */
  const handleMiniCalendarDateClick = useCallback((date: Date) => {
    const monday = getWeekMonday(date);
    setWeekStart(monday);
    const dow = date.getDay();
    const offset = dow === 0 ? 6 : dow - 1;
    setDayOffset(offset);
    if (viewMode === 'month') {
      selectViewMode('day');
    }
  }, [viewMode, selectViewMode]);

  const openCreatePresetModal = useCallback(() => {
    setPresetDraftName('');
    setPresetDraftShared(false);
    setPresetModal({ mode: 'create', presetId: null });
  }, []);

  const openEditPresetModal = useCallback(() => {
    if (!selectedPreset || !selectedPreset.ownedByCurrentUser) {
      toast.error('Select one of your presets to update');
      return;
    }

    setPresetDraftName(selectedPreset.name);
    setPresetDraftShared(selectedPreset.shared);
    setPresetModal({ mode: 'edit', presetId: selectedPreset.schedulerPreferenceId });
  }, [selectedPreset, toast]);

  const handleApplyPreset = useCallback(() => {
    if (!selectedPresetId) {
      toast.error('Choose a preset to apply');
      return;
    }

    applyPresetMutation.mutate(selectedPresetId);
  }, [applyPresetMutation, selectedPresetId, toast]);

  const handleDeletePreset = useCallback(() => {
    if (!selectedPreset || !selectedPreset.ownedByCurrentUser) {
      toast.error('Select one of your presets to delete');
      return;
    }

    if (!window.confirm(`Delete preset "${selectedPreset.name}"?`)) {
      return;
    }

    deletePresetMutation.mutate(selectedPreset.schedulerPreferenceId, {
      onSuccess: () => {
        if (selectedPresetId === selectedPreset.schedulerPreferenceId) {
          setSelectedPresetId(null);
        }
      },
    });
  }, [deletePresetMutation, selectedPreset, selectedPresetId, toast]);

  const handleSavePreset = useCallback(() => {
    const name = presetDraftName.trim();
    if (!name) {
      toast.error('Preset name is required');
      return;
    }

    // Legacy parity (calendar-test.php L6581-L6622): preset names are unique
    // per account. Legacy upserted on the same name; v5's modal has explicit
    // create/edit modes so we instead block the save with a clear error and
    // tell the user to either edit the existing preset or pick a new name.
    // Comparison is case-insensitive (after trim) to catch
    // "morning"/"Morning"/" Morning " collisions.
    const editingId =
      presetModal?.mode === 'edit' && presetModal.presetId ? presetModal.presetId : null;
    const normalizedName = name.toLowerCase();
    const conflict = (presetsQuery.data ?? []).find(
      (preset) =>
        preset.name.trim().toLowerCase() === normalizedName &&
        preset.schedulerPreferenceId !== editingId,
    );
    if (conflict) {
      toast.error(
        conflict.ownedByCurrentUser
          ? `A preset named "${conflict.name}" already exists. Edit it from the preset menu or pick a different name.`
          : `"${conflict.name}" is already used by a shared preset. Pick a different name.`,
      );
      return;
    }

    const payload: SchedulerPreferencePayload = {
      ...schedulerPayload,
      name,
      shared: presetDraftShared,
    };

    if (editingId) {
      updatePresetMutation.mutate({ id: editingId, payload });
      return;
    }

    createPresetMutation.mutate(payload);
  }, [
    createPresetMutation,
    presetDraftName,
    presetDraftShared,
    presetModal,
    presetsQuery.data,
    schedulerPayload,
    toast,
    updatePresetMutation,
  ]);

  // -- Week label -------------------------------------------------------------
  const viewLabel = useMemo(
    () => scheduleDateLabelText(viewMode, {
      day: weekDays[dayOffset],
      weekStart: weekDays[0],
      weekEnd: weekDays[6],
      month: monthDate,
    }),
    [viewMode, weekDays, dayOffset, monthDate],
  );

  // -- Appointments bucketed per day ------------------------------------------
  // Bucket on the user's local date (not the UTC-Z prefix from the wire), so
  // an appointment at 11pm user-TZ that crosses UTC midnight still appears in
  // the user's day instead of the next day's column.
  const apptsByDay: Record<string, CalendarAppointment[]> = {};
  for (const appt of appointments) {
    const day = dateInTz(appt.start, userTz);
    if (!apptsByDay[day]) apptsByDay[day] = [];
    apptsByDay[day].push(appt);
  }

  // -- Overview status groups -------------------------------------------------
  const overviewGroups = useMemo(() => {
    if (viewMode !== 'overview') return null;
    const dayStr = toDateStr(weekDays[dayOffset] ?? weekDays[0]);
    const dayAppts = apptsByDay[dayStr] ?? [];

    // One bucket per label getApptStatus() can return — a status with no
    // bucket is silently dropped by the `groups[status]` guard below, which is
    // how Contact Attempted appointments went missing from the overview.
    const groups: Record<string, CalendarAppointment[]> = {
      'Scheduled': [],
      'Contact Attempted': [],
      'Confirmed': [],
      'Checked In': [],
      'Checked Back': [],
      'Checked Out': [],
      'Cancelled': [],
      'No Show': [],
      'Waiting List': [],
    };

    for (const appt of dayAppts) {
      // Blocked time is not a patient appointment — no patient, no status, and
      // none of the workflow actions the overview rows offer. It was landing in
      // "Scheduled" because getApptStatus() derives the bucket from status flags
      // (confirmed / checkInTime / noShow / …) that a block never carries, so
      // every block fell through to the default. The grid keeps blocks; the
      // status buckets must not.
      if (appt.isBlock) continue;

      const status = getApptStatus(appt);
      // Wait-list entries come from the dedicated (not day-scoped) query
      // below, so skip them here — otherwise a wait-list appointment booked
      // for the viewed day would be listed twice.
      if (status === 'Waiting List') continue;
      if (groups[status]) {
        groups[status].push(appt);
      }
    }

    // Every still-upcoming wait-list entry, regardless of the viewed day.
    groups['Waiting List'] = waitlistAppointments.filter((appt) => !appt.isBlock);

    return groups;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, appointments, dayOffset, weekDays, waitlistAppointments]);

  // Convert detailAppt (CalendarAppointment) into an Appointment shape for the modal.
  const [fullDetailAppt, setFullDetailAppt] = useState<Appointment | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  React.useEffect(() => {
    if (!detailAppt) {
      setFullDetailAppt(null);
      return;
    }
    setLoadingDetail(true);
    appointmentsService.getAppointment(detailAppt.id)
      .then((a) => setFullDetailAppt(a))
      .catch(() => {
        toast.error('Failed to load appointment details.');
        setDetailAppt(null);
      })
      .finally(() => setLoadingDetail(false));
  }, [detailAppt]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Active filter count and items (for the toolbar popover) ----------------
  const activeFilterCount = selectedFacilities.size + (showCancelled ? 1 : 0) + (showNoShows ? 1 : 0);

  const activeFilterItems = useMemo(() => {
    const items: { category: string; id: string; label: string; onRemove: () => void }[] = [];
    for (const opt of facilityOptions) {
      if (selectedFacilities.has(opt.id)) {
        items.push({ category: 'Facilities', id: `fac-${opt.id}`, label: opt.label, onRemove: () => handleToggleFacility(opt.id) });
      }
    }
    if (showCancelled) {
      items.push({ category: 'Options', id: 'show-cancelled', label: 'Show Cancelled', onRemove: () => setShowCancelled(false) });
    }
    if (showNoShows) {
      items.push({ category: 'Options', id: 'show-no-shows', label: 'Show No Shows', onRemove: () => setShowNoShows(false) });
    }
    return items;
  }, [facilityOptions, selectedFacilities, handleToggleFacility, showCancelled, showNoShows]);

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const filterPopoverRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterPopoverOpen) return;
    function onOutside(e: MouseEvent) {
      if (!filterPopoverRef.current?.contains(e.target as Node)) {
        setFilterPopoverOpen(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [filterPopoverOpen]);

  const handleClearAllFilters = useCallback(() => {
    handleClearAllFacilities();
    setShowCancelled(false);
    setFilterPopoverOpen(false);
  }, [handleClearAllFacilities]);

  // -- Month grid data --------------------------------------------------------
  const monthGrid = useMemo(() => {
    if (viewMode !== 'month') return [];
    return getMonthGrid(monthDate.getFullYear(), monthDate.getMonth());
  }, [viewMode, monthDate]);

  // -- Overview status color map ----------------------------------------------
  const statusColorMap: Record<string, string> = {
    'Scheduled': 'bg-blue-50 text-blue-700',
    'Contact Attempted': 'bg-amber-50 text-amber-700',
    'Confirmed': 'bg-green-50 text-green-700',
    'Checked In': 'bg-yellow-50 text-yellow-700',
    'Checked Back': 'bg-blue-100 text-blue-700',
    'Checked Out': 'bg-slate-100 text-slate-600',
    'Cancelled': 'bg-slate-100 text-slate-500',
    'No Show': 'bg-red-50 text-red-700',
    'Waiting List': 'bg-purple-50 text-purple-700',
  };

  // Flat resourceId -> label lookup used by the calendar column-header logic
  // below. Built from the same active-resources list that feeds the per-type
  // Schedules sections in the sidebar, so labels stay in sync.
  const scheduleLabelLookup = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of resources) {
      if (r.active) map.set(r.resourceId, r.resourceName);
    }
    return map;
  }, [resources]);

  const dayScheduleColumns = useMemo(() => {
    if (viewMode !== 'day') return [];

    const selected = visibleSelectedSchedules
      .map((scheduleId) => {
        const label = scheduleLabelLookup.get(scheduleId);
        return label ? { id: scheduleId, label } : null;
      })
      .filter((entry): entry is { id: number; label: string } => entry !== null);

    return selected.length > 0 ? selected : [{ id: 0, label: 'All Schedules' }];
  }, [viewMode, visibleSelectedSchedules, scheduleLabelLookup]);

  // Legacy parity (scheduler-display-week-cell-tz.php 963-971): in week view
  // when multiple schedules are selected, each day is split into N sub-columns
  // — one per resource. When 0 or 1 resource is selected, each day is a single
  // column as before.
  const weekScheduleColumns = useMemo(() => {
    if (viewMode !== 'week') return [];
    const selected = visibleSelectedSchedules
      .map((scheduleId) => {
        const label = scheduleLabelLookup.get(scheduleId);
        return label ? { id: scheduleId, label } : null;
      })
      .filter((entry): entry is { id: number; label: string } => entry !== null);
    return selected;
  }, [viewMode, visibleSelectedSchedules, scheduleLabelLookup]);

  // -- Render -----------------------------------------------------------------

  /**
   * SCRUM-1448. Unconditional and at the top level — `useIsBelow` is a useSyncExternalStore hook
   * and a call behind a condition or an early return changes the render-to-render hook count,
   * which is what threw React #310 and blanked every page in this app.
   */
  const filtersInDrawer = useIsBelow('lg');
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  /**
   * The drawer does not survive the desktop crossing — the same policy the app nav follows
   * (`Sidebar.mobileClose.test.tsx` names a case for it). Unmounting `OffCanvasPanel` already
   * removes the dialog when the rail goes inline, but `filtersOpen` would stay `true` and the
   * drawer would spring back on the way down. Resetting it makes the return trip unsurprising.
   */
  React.useEffect(() => {
    if (!filtersInDrawer) setFiltersOpen(false);
  }, [filtersInDrawer]);

  /**
   * One element, rendered in one of two places. Hoisting it is what keeps the drawer and the
   * inline rail from drifting apart — forty props copied twice is forty chances for one branch to
   * lose a filter.
   */
  const sidebarFilters = (
    <SidebarFilters
      facilityOptions={facilityOptions}
      selectedFacilities={effectiveSelectedFacilities}
      onToggleFacility={handleToggleFacility}
      onSelectAllFacilities={handleSelectAllFacilities}
      onClearAllFacilities={handleClearAllFacilities}
      isFacilityLocked={isFacilityLocked}
      scheduleSectionsByType={scheduleSectionsByType}
      scheduleSectionTitle={scheduleSectionTitle}
      selectedSchedules={selectedSchedules}
      onToggleSchedule={handleToggleSchedule}
      onSelectAllSchedulesOfType={handleSelectAllSchedulesOfType}
      onClearSchedulesOfType={handleClearSchedulesOfType}
      showCancelled={showCancelled}
      onToggleCancelled={() => setShowCancelled((v) => !v)}
      showNoShows={showNoShows}
      onToggleNoShows={() => setShowNoShows((v) => !v)}
      showWeekends={showWeekends}
      onToggleWeekends={() => setShowWeekends((v) => !v)}
      collapsed={filtersInDrawer ? false : sidebarCollapsed}
      onToggleCollapse={
      filtersInDrawer ? () => setFiltersOpen(false) : () => setSidebarCollapsed((v) => !v)
      }
      displayMode={displayMode}
      onDisplayModeChange={(v) => setDisplayMode(v ?? 'both')}
      blockDisplayStyle={blockDisplayStyle}
      onBlockDisplayStyleChange={(v) => setBlockDisplayStyle(v ?? 'overlay')}
      showHours={showHours}
      onShowHoursChange={(v) => setShowHours(v ?? 12)}
      startHour={startHour}
      onStartHourChange={(v) => setStartHour(v ?? 7)}
      slotMinutes={slotMinutes}
      onSlotMinutesChange={(v) => setSlotMinutes(v ?? 30)}
      showNoResource={showNoResource}
      onToggleNoResource={() => setShowNoResource((v) => !v)}
      showInfoPopups={showInfoPopups}
      onToggleInfoPopups={() => setShowInfoPopups((v) => !v)}
      showBlockInfoPopups={showBlockInfoPopups}
      onToggleBlockInfoPopups={() => setShowBlockInfoPopups((v) => !v)}
      apptCountToggle={apptCountToggle}
      onToggleApptCount={() => setApptCountToggle((v) => !v)}
      apptHoursToggle={apptHoursToggle}
      onToggleApptHours={() => setApptHoursToggle((v) => !v)}
      apptQuoteToggle={apptQuoteToggle}
      onToggleApptQuote={() => setApptQuoteToggle((v) => !v)}
      canSeeQuoteTotals={isAdmin}
      privacyMode={privacyMode}
      onTogglePrivacyMode={() => setPrivacyMode((v) => !v)}
      currentDate={currentDate}
      onMiniCalendarDateClick={handleMiniCalendarDateClick}
    />
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* -- Previous-day check banner (legacy performYesterdayCheck parity) --- */}
      {previousDayVisible && previousDayBanner && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 text-sm text-amber-900">
            <span className="font-semibold">Previous day check:</span>{' '}
            {previousDayBanner.totalCount} item{previousDayBanner.totalCount !== 1 ? 's' : ''} need attention from {previousDayBanner.date}.
            {previousDayBanner.confirmedNotCheckedIn.length > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {previousDayBanner.confirmedNotCheckedIn.length} not checked in
              </span>
            )}
            {previousDayBanner.checkedInNotCheckedBack.length > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {previousDayBanner.checkedInNotCheckedBack.length} not checked back
              </span>
            )}
            {previousDayBanner.checkedInNotCheckedOut.length > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {previousDayBanner.checkedInNotCheckedOut.length} not checked out
              </span>
            )}
            {previousDayBanner.waitlistStranded.length > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                {previousDayBanner.waitlistStranded.length} waitlist stranded
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              // P1-6: persist dismiss via yesterdayCheckDate preference.
              updatePreferenceMutation.mutate({ yesterdayCheckDate: todayKey });
            }}
            aria-label="Dismiss previous day check"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {/* -- Toolbar ---------------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        {/* The only way back to the filters once the rail is in the drawer (SCRUM-1448).
            Rendered only in that tier, so the desktop toolbar gains no button. */}
        {filtersInDrawer && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setFiltersOpen(true)}
            leftIcon={<SlidersHorizontal className="h-4 w-4" />}
          >
            Filters
          </Button>
        )}
        {/* Navigation */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrev}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToday}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Week / day / month label */}
        {/* The loading spinner is drawn inside the label, after the date: as its own toolbar
            item it took width from the label and swapped it to the short form (SCRUM-2136). */}
        <ScheduleDateLabel full={viewLabel.full} short={viewLabel.short} samples={viewLabel.samples} busy={isFetching} />

        {/* Active filter indicator + popover */}
        {activeFilterCount > 0 && (
          <div className="relative" ref={filterPopoverRef}>
            <button
              type="button"
              onClick={() => setFilterPopoverOpen((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors',
                filterPopoverOpen
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200',
              )}
            >
              <Filter className="h-3 w-3" />
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
            </button>
            {filterPopoverOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 w-64 rounded-md border border-slate-200 bg-white shadow-lg">
                <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                  <span className="text-xs font-semibold text-slate-700">Active Filters</span>
                  <button
                    type="button"
                    onClick={handleClearAllFilters}
                    className="text-[11px] font-medium text-rose-600 hover:text-rose-700"
                  >
                    Clear All
                  </button>
                </div>
                <div className="max-h-[260px] overflow-y-auto py-1">
                  {(['Facilities', 'Resources', 'Appt Types', 'Options'] as const).map((cat) => {
                    const catItems = activeFilterItems.filter((f) => f.category === cat);
                    if (catItems.length === 0) return null;
                    return (
                      <div key={cat}>
                        <p className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          {cat}
                        </p>
                        {catItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-1 px-3 py-1 hover:bg-slate-50"
                          >
                            <span className="flex-1 truncate text-xs text-slate-700">{item.label}</span>
                            <button
                              type="button"
                              onClick={item.onRemove}
                              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600 flex-shrink-0"
                              aria-label={`Remove ${item.label}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* The toolbar above IS `flex-wrap`, but this group was one unbreakable item: a
            `min-w-[14rem]` nowrap container holding a `min-w-[12rem]` Select plus four nowrap
            buttons, whose min-content width is well past 375px. A parent cannot wrap inside a
            child, which is why "Delete" was cut off at the right edge. Below `lg` the group takes
            the full row and wraps internally; the two minimums stay for desktop. */}
        <div className="flex min-w-[14rem] items-center gap-1 max-lg:w-full max-lg:min-w-0 max-lg:flex-wrap">
          <div className="min-w-[12rem] max-lg:min-w-0 max-lg:flex-1">
            <Select<number>
              options={presetOptions}
              value={selectedPresetId ?? undefined}
              onChange={(value) => setSelectedPresetId(value ?? null)}
              placeholder={presetsQuery.isLoading ? 'Loading presets...' : 'Select preset'}
            />
          </div>
          <Button variant="ghost" size="sm" onClick={handleApplyPreset} disabled={!selectedPresetId || applyPresetMutation.isPending}>
            Apply
          </Button>
          <Button variant="ghost" size="sm" onClick={openCreatePresetModal}>
            Save As
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={openEditPresetModal}
            disabled={!selectedPreset || !selectedPreset.ownedByCurrentUser}
          >
            Update
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeletePreset}
            disabled={!selectedPreset || !selectedPreset.ownedByCurrentUser || deletePresetMutation.isPending}
          >
            Delete
          </Button>
        </div>

        {/* Toolbar action buttons */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Seed BlockTimeModal with naive wall-clock strings (no `T`,
              // no `Z`). The modal interprets them as facility/account
              // timezone wall-clock (same contract as the appointment
              // modal). The previous fake-`Z` form misled the modal into
              // a UTC->Pacific projection that produced the wrong time
              // for non-Pacific accounts.
              const dateStr = toDateStr(currentDate);
              const startHh = String(startHour).padStart(2, '0');
              const endHh = String(Math.min(startHour + 1, 23)).padStart(2, '0');
              const defaultStart = `${dateStr} ${startHh}:00:00`;
              const defaultEnd = `${dateStr} ${endHh}:00:00`;
              setBlockRange({ start: defaultStart, end: defaultEnd });
              setBlockOpen(true);
            }}
            title="Block Time"
          >
            <Plus className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline text-xs">Block Time</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFindAvailableOpen(true)}
            title="Find Available Time"
          >
            <SearchIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline text-xs">Find Available</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPrintOpen(true)}
            title="Print Schedule"
          >
            <Printer className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline text-xs">Print</span>
          </Button>
        </div>

        {/* View toggle */}
        <div className="flex rounded-md border border-slate-200 overflow-hidden">
          <button
            type="button"
            onClick={() => selectViewMode('week')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium transition-colors',
              viewMode === 'week'
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            Week
          </button>
          <button
            type="button"
            onClick={() => selectViewMode('day')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors',
              viewMode === 'day'
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            Day
          </button>
          <button
            type="button"
            onClick={() => selectViewMode('month')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors',
              viewMode === 'month'
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            Month
          </button>
          <button
            type="button"
            onClick={() => selectViewMode('overview')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors',
              viewMode === 'overview'
                ? 'bg-primary text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50',
            )}
          >
            Overview
          </button>
        </div>
      </div>

      {/* -- Error state ------------------------------------------------------- */}
      {isError && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 text-red-700 text-sm flex-shrink-0">
          Failed to load appointments. Please refresh the page.
        </div>
      )}

      {/* -- Main content: sidebar + calendar grid ----------------------------- */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar filters.

            SCRUM-1448: below `lg` the 224px rail is moved into the shared OffCanvasPanel drawer
            so the calendar gets the full width. `lg`, not `md`, because the app's own 256px rail
            is still in flow above 768px: at 768 the content column is ~512px and this rail would
            leave the day grid ~288px — narrower than the same grid on a phone. Subtract the shell
            before choosing a breakpoint.

            A JS branch rather than CSS because the DOM genuinely differs (an aside vs a dialog),
            and that has a print benefit too: JS branches do not fire for print, so a printed
            schedule keeps the inline rail even though print lays out near 816px. */}
        {!filtersInDrawer && sidebarFilters}
        {filtersInDrawer && (
          <OffCanvasPanel
            id="schedule-filters"
            title="Filters"
            side="left"
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
          >
            {sidebarFilters}
          </OffCanvasPanel>
        )}

        {/* Calendar grid - Week view */}
        {viewMode === 'week' && (
          <div className="flex-1 overflow-auto">
            <div
              className="grid min-w-0"
              style={{
                gridTemplateColumns: `4rem repeat(${displayDays.length}, minmax(0, 1fr))`,
              }}
            >
              {/* Header row */}
              {/* Top-left corner */}
              <div className="sticky top-0 z-20 bg-white border-b border-r border-slate-400 py-2" />

              {/* Day header cells. When multiple schedules are selected we
                  render a second-level header row with each resource's label,
                  mirroring scheduler_display_tz.php 896-924. */}
              {displayDays.map((day) => {
                const ds = toDateStr(day);
                const isToday = ds === todayStr;
                const dayIdx = day.getDay(); // 0=Sun, 1=Mon, ...
                const dayLabel = DAY_LABELS[dayIdx === 0 ? 6 : dayIdx - 1];
                const dayTotals = computeColumnTotals(apptsByDay[ds] ?? []);
                return (
                  <div
                    key={ds}
                    className={cn(
                      'sticky top-0 z-20 bg-white border-b border-r border-slate-400 pt-2 text-center',
                      isToday && 'bg-blue-50',
                    )}
                  >
                    <p className={cn(
                      'text-xs font-semibold uppercase tracking-wide',
                      isToday ? 'text-blue-600' : 'text-slate-500',
                    )}>
                      {dayLabel}
                    </p>
                    <p className={cn(
                      'text-lg font-bold mt-0.5',
                      isToday ? 'text-blue-600' : 'text-slate-800',
                    )}>
                      {day.getDate()}
                    </p>
                    <TotalsStrip
                      totals={dayTotals}
                      showCount={apptCountToggle}
                      showHours={apptHoursToggle}
                      showQuote={apptQuoteToggle && isAdmin}
                    />
                    {weekScheduleColumns.length > 1 && (
                      <div className="mt-1 flex border-t border-slate-400">
                        {weekScheduleColumns.map((col) => {
                          const colTotals = computeColumnTotals(
                            (apptsByDay[ds] ?? []).filter((event) => isBlockVisibleInColumn(event, col.id) || eventHasResource(event, col.id)),
                          );
                          return (
                            <div
                              key={`${ds}-hdr-${col.id}`}
                              className={cn(
                                'flex-1 min-w-0 border-r border-slate-300 last:border-r-0 px-1 py-0.5 text-[10px] font-medium text-slate-500 group/wcol',
                                weekScheduleColumns.length > 1 && 'cursor-grab active:cursor-grabbing',
                              )}
                              title={col.label}
                              draggable
                              onDragStart={(e) => {
                                dragColumnIdRef.current = col.id;
                                e.dataTransfer.effectAllowed = 'move';
                                (e.currentTarget as HTMLElement).style.opacity = '0.4';
                              }}
                              onDragEnd={(e) => {
                                (e.currentTarget as HTMLElement).style.opacity = '';
                                (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                                (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                                dragColumnIdRef.current = null;
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                (e.currentTarget as HTMLElement).style.borderLeftColor = '#3b82f6';
                                (e.currentTarget as HTMLElement).style.borderLeftWidth = '3px';
                              }}
                              onDragLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                                (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                                (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                                handleColumnDrop(col.id);
                              }}
                            >
                              <div className="flex items-center gap-0.5">
                                <div className="truncate flex-1">{col.label}</div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); handleToggleSchedule(col.id); }}
                                  className="opacity-0 group-hover/wcol:opacity-100 transition-opacity rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 flex-shrink-0"
                                  aria-label={`Remove ${col.label}`}
                                  title="Remove schedule"
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </div>
                              <TotalsStrip
                                totals={colTotals}
                                showCount={apptCountToggle}
                                showHours={apptHoursToggle}
                                showQuote={apptQuoteToggle && isAdmin}
                                compact
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Time axis + day columns */}
              <div className="relative border-r border-slate-300" style={{ height: `${Math.max(timeSlots.length, 1) * SLOT_ROW_HEIGHT_REM}rem` }}>
                {timeSlots.map((slot, slotIdx) => {
                  const isHourBoundary = slot.endsWith(':00');
                  return (
                    <div
                      key={slot}
                      className={cn(
                        'absolute left-0 right-0 border-t px-1 pr-2 flex justify-end',
                        isHourBoundary ? 'border-slate-400' : 'border-slate-300',
                      )}
                      style={{ top: `${(slotIdx / Math.max(timeSlots.length, 1)) * 100}%` }}
                    >
                      <span className={cn(
                        'text-[10px] -translate-y-1/2 whitespace-nowrap bg-white px-1',
                        isHourBoundary ? 'text-slate-500 font-medium' : 'text-slate-400',
                      )}>
                        {formatTimeLabel(slot)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {displayDays.map((day) => {
                const ds = toDateStr(day);
                const isToday = ds === todayStr;
                const dayEvents = (apptsByDay[ds] ?? []).filter((event) => {
                  // Visible-window check uses minutes-since-midnight in the
                  // user's TZ so day-boundary events (late-evening / very
                  // early morning) are placed correctly.
                  const visibleStart = startHour * 60;
                  const visibleEnd = hourEnd * 60;
                  const startMinutes = minutesSinceMidnightInTz(event.start, userTz);
                  const endMinutes = minutesSinceMidnightInTz(event.end, userTz);
                  return endMinutes > visibleStart && startMinutes < visibleEnd;
                });

                // Legacy parity: when >1 schedule selected, split the day into
                // N resource sub-columns; otherwise keep the single-column
                // rendering.
                if (weekScheduleColumns.length > 1) {
                  return (
                    <div
                      key={ds}
                      className="flex"
                      style={{ height: `${Math.max(timeSlots.length, 1) * SLOT_ROW_HEIGHT_REM}rem` }}
                    >
                      {weekScheduleColumns.map((col) => {
                        const subEvents = dayEvents.filter((event) => isBlockVisibleInColumn(event, col.id) || eventHasResource(event, col.id));
                        return (
                          <div
                            key={`${ds}-${col.id}`}
                            className="flex-1 min-w-0 border-r border-slate-300 last:border-r-0"
                          >
                            <OverlayDayColumn
                              dateStr={ds}
                              events={subEvents}
                              timeSlots={timeSlots}
                              slotMinutes={slotMinutes}
                              startHour={startHour}
                              hourEnd={hourEnd}
                              isToday={isToday}
                              scheduleResourceId={col.id}
                              onOpenAppt={handleOpenAppt}
                              onOpenCreate={handleOpenCreate}
                              onCheckIn={handleCheckIn}
                              checkingInId={checkingInId}
                              privacyMode={privacyMode}
                              showInfoPopups={showInfoPopups}
                              showBlockInfoPopups={showBlockInfoPopups}
                              blockDisplayStyle={blockDisplayStyle}
                              nowTick={nowTick}
                              hideNowLine={hideNowLine}
                              userTz={userTz}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Single-schedule case: filter dayEvents to that one resource
                // so global/foreign blocks don't bleed through (mirrors the
                // Day view filter at the column level and the multi-schedule
                // sub-column filter above).
                const selectedCol = weekScheduleColumns.length === 1 ? weekScheduleColumns[0] : null;
                const columnEvents = selectedCol
                  ? dayEvents.filter((event) => isBlockVisibleInColumn(event, selectedCol.id) || eventHasResource(event, selectedCol.id))
                  : dayEvents;
                return (
                  <div key={ds} style={{ height: `${Math.max(timeSlots.length, 1) * SLOT_ROW_HEIGHT_REM}rem` }}>
                    <OverlayDayColumn
                      dateStr={ds}
                      events={columnEvents}
                      timeSlots={timeSlots}
                      slotMinutes={slotMinutes}
                      startHour={startHour}
                      hourEnd={hourEnd}
                      isToday={isToday}
                      scheduleResourceId={selectedCol ? selectedCol.id : undefined}
                      onOpenAppt={handleOpenAppt}
                      onOpenCreate={handleOpenCreate}
                      onCheckIn={handleCheckIn}
                      checkingInId={checkingInId}
                      privacyMode={privacyMode}
                      showInfoPopups={showInfoPopups}
                      showBlockInfoPopups={showBlockInfoPopups}
                      blockDisplayStyle={blockDisplayStyle}
                      nowTick={nowTick}
                      hideNowLine={hideNowLine}
                      userTz={userTz}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Calendar grid - Day view with schedules */}
        {viewMode === 'day' && (
          <div className="flex-1 overflow-auto">
            <div
              className="grid min-w-0"
              style={{
                // `compact` only when the filters have moved into the drawer, i.e. below `lg`.
                // At `compact === false` the string is byte-identical to the template this
                // replaced — see dayGridTemplate's docblock for why that matters.
                gridTemplateColumns: dayGridTemplate(dayScheduleColumns.length, filtersInDrawer),
              }}
            >
              <div className="sticky top-0 z-20 bg-white border-b border-r border-slate-400 py-2" />

              {dayScheduleColumns.map((column) => {
                const dayDs = toDateStr(displayDays[0]);
                const rawDayAppts = apptsByDay[dayDs] ?? [];
                const colAppts = column.id === 0
                  ? rawDayAppts
                  : rawDayAppts.filter((event) => isBlockVisibleInColumn(event, column.id) || eventHasResource(event, column.id));
                const colTotals = computeColumnTotals(colAppts);
                const isDraggable = column.id !== 0 && dayScheduleColumns.length > 1;
                return (
                  <div
                    key={`schedule-header-${column.id}`}
                    className={cn(
                      'sticky top-0 z-20 bg-white border-b border-r border-slate-400 py-2 px-2 group/col',
                      isDraggable && 'cursor-grab active:cursor-grabbing',
                    )}
                    title={column.label}
                    draggable={isDraggable}
                    onDragStart={isDraggable ? (e) => {
                      dragColumnIdRef.current = column.id;
                      e.dataTransfer.effectAllowed = 'move';
                      (e.currentTarget as HTMLElement).style.opacity = '0.4';
                    } : undefined}
                    onDragEnd={isDraggable ? (e) => {
                      (e.currentTarget as HTMLElement).style.opacity = '';
                      (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                      (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                      dragColumnIdRef.current = null;
                    } : undefined}
                    onDragOver={isDraggable ? (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      (e.currentTarget as HTMLElement).style.borderLeftColor = '#3b82f6';
                      (e.currentTarget as HTMLElement).style.borderLeftWidth = '3px';
                    } : undefined}
                    onDragLeave={isDraggable ? (e) => {
                      (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                      (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                    } : undefined}
                    onDrop={isDraggable ? (e) => {
                      e.preventDefault();
                      (e.currentTarget as HTMLElement).style.borderLeftColor = '';
                      (e.currentTarget as HTMLElement).style.borderLeftWidth = '';
                      handleColumnDrop(column.id);
                    } : undefined}
                  >
                    <div className="flex items-start gap-1">
                      <p
                        className="text-[11px] font-semibold leading-tight text-slate-800 break-words flex-1"
                        title={column.label}
                      >
                        {column.label}
                      </p>
                      {isDraggable && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleToggleSchedule(column.id); }}
                          className="opacity-0 group-hover/col:opacity-100 transition-opacity rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 flex-shrink-0"
                          aria-label={`Remove ${column.label}`}
                          title="Remove schedule"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    <TotalsStrip
                      totals={colTotals}
                      showCount={apptCountToggle}
                      showHours={apptHoursToggle}
                      showQuote={apptQuoteToggle && isAdmin}
                    />
                  </div>
                );
              })}

              <div className="relative border-r border-slate-300" style={{ height: `${Math.max(timeSlots.length, 1) * SLOT_ROW_HEIGHT_REM}rem` }}>
                {timeSlots.map((slot, slotIdx) => {
                  const isHourBoundary = slot.endsWith(':00');
                  return (
                    <div
                      key={slot}
                      className={cn(
                        'absolute left-0 right-0 border-t px-1 pr-2 flex justify-end',
                        isHourBoundary ? 'border-slate-400' : 'border-slate-300',
                      )}
                      style={{ top: `${(slotIdx / Math.max(timeSlots.length, 1)) * 100}%` }}
                    >
                      <span className={cn(
                        'text-[10px] -translate-y-1/2 whitespace-nowrap bg-white px-1',
                        isHourBoundary ? 'text-slate-500 font-medium' : 'text-slate-400',
                      )}>
                        {formatTimeLabel(slot)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {dayScheduleColumns.map((column) => {
                const day = displayDays[0];
                const ds = toDateStr(day);
                const isToday = ds === todayStr;
                const dayEvents = (apptsByDay[ds] ?? []).filter((event) => {
                  const visibleStart = startHour * 60;
                  const visibleEnd = hourEnd * 60;
                  const startMinutes = minutesSinceMidnightInTz(event.start, userTz);
                  const endMinutes = minutesSinceMidnightInTz(event.end, userTz);
                  if (!(endMinutes > visibleStart && startMinutes < visibleEnd)) {
                    return false;
                  }

                  if (column.id === 0) {
                    return true;
                  }

                  return isBlockVisibleInColumn(event, column.id) || eventHasResource(event, column.id);
                });

                return (
                  <div key={`schedule-column-${column.id}`} style={{ height: `${Math.max(timeSlots.length, 1) * SLOT_ROW_HEIGHT_REM}rem` }}>
                    <OverlayDayColumn
                      dateStr={ds}
                      events={dayEvents}
                      timeSlots={timeSlots}
                      slotMinutes={slotMinutes}
                      startHour={startHour}
                      hourEnd={hourEnd}
                      isToday={isToday}
                      scheduleResourceId={column.id === 0 ? undefined : column.id}
                      onOpenAppt={handleOpenAppt}
                      onOpenCreate={handleOpenCreate}
                      onCheckIn={handleCheckIn}
                      checkingInId={checkingInId}
                      privacyMode={privacyMode}
                      showInfoPopups={showInfoPopups}
                      showBlockInfoPopups={showBlockInfoPopups}
                      blockDisplayStyle={blockDisplayStyle}
                      nowTick={nowTick}
                      hideNowLine={hideNowLine}
                      userTz={userTz}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Calendar grid - Month view */}
        {viewMode === 'month' && (
          <div className="flex-1 overflow-auto p-4">
            {/* Month grid header */}
            <div className="grid grid-cols-7 gap-px bg-slate-400 border border-slate-400 rounded-t-lg">
              {DAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  {label}
                </div>
              ))}
            </div>
            {/* Month grid body */}
            <div className="grid grid-cols-7 gap-px bg-slate-400 border-x border-b border-slate-400 rounded-b-lg">
              {monthGrid.map((day, idx) => {
                if (!day) {
                  return (
                    <div key={`empty-${idx}`} className="bg-slate-50 min-h-[9rem]" />
                  );
                }

                const ds = toDateStr(day);
                const isToday = ds === todayStr;
                const dayAppts = apptsByDay[ds] ?? [];
                const isCurrentMonth = day.getMonth() === monthDate.getMonth();
                const dayTotals = computeColumnTotals(dayAppts);

                return (
                  <div
                    key={ds}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleMonthDayClick(day)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleMonthDayClick(day); }}
                    className={cn(
                      'bg-white min-h-[9rem] p-1.5 cursor-pointer hover:bg-slate-50 transition-colors',
                      !isCurrentMonth && 'bg-slate-50/50',
                      isToday && 'bg-blue-50',
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          isToday
                            ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs'
                            : isCurrentMonth
                              ? 'text-slate-700'
                              : 'text-slate-400',
                        )}
                      >
                        {day.getDate()}
                      </span>
                      {dayAppts.length > 0 && (
                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                          {dayAppts.length}
                        </span>
                      )}
                    </div>
                    <TotalsStrip
                      totals={dayTotals}
                      showCount={apptCountToggle}
                      showHours={apptHoursToggle}
                      showQuote={apptQuoteToggle && isAdmin}
                      compact
                    />
                    {/* Abbreviated appointment list */}
                    <div className="space-y-0.5">
                      {dayAppts.slice(0, 3).map((appt) => {
                        const time = toLocaleTimeStringInTz(appt.start, undefined, {
                          hour: '2-digit', minute: '2-digit', timeZone: userTz,
                        });
                        return (
                          <div
                            // Recurring blocks share `id`; suffix with `start`
                            // so each occurrence has a unique React key.
                            key={`${appt.id}-${appt.start}`}
                            className="text-[10px] leading-tight truncate rounded px-1 py-0.5"
                            style={{
                              backgroundColor: appt.appointmentTypeColor
                                ? `${appt.appointmentTypeColor}20`
                                : '#e2e8f020',
                              borderLeft: `2px solid ${appt.appointmentTypeColor ?? '#94a3b8'}`,
                            }}
                          >
                            <span className="text-slate-500">{time}</span>{' '}
                            <span className="text-slate-700 font-medium">
                              {privacyMode
                                ? 'Patient'
                                : (appt.patientName?.trim()?.split(' ')[0] || appt.title || '')}
                            </span>
                          </div>
                        );
                      })}
                      {dayAppts.length > 3 && (
                        <p className="text-[10px] text-slate-400 pl-1">
                          +{dayAppts.length - 3} more
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Overview view */}
        {viewMode === 'overview' && overviewGroups && (
          <div className="flex-1 overflow-auto p-4">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              Day Overview — {weekDays[dayOffset]?.toLocaleDateString(undefined, {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}
            </h2>
            {Object.entries(overviewGroups).map(([status, appts]) => {
              // Legacy ov*Open key mapping for the persisted collapse state.
              const openKey: keyof typeof overviewOpenState = (
                status === 'Scheduled' ? 'scheduled'
                  : status === 'Contact Attempted' ? 'contactAttempted'
                  : status === 'Confirmed' ? 'confirmed'
                  : status === 'Checked In' ? 'checkedIn'
                  : status === 'Checked Back' ? 'checkedBack'
                  : status === 'Checked Out' ? 'checkedOut'
                  : status === 'Cancelled' ? 'cancelled'
                  : status === 'No Show' ? 'noShow'
                  : 'waitList'
              );
              const isOpen = overviewOpenState[openKey];
              return (
              <OverviewSection
                key={status}
                title={status}
                appointments={appts}
                colorClass={statusColorMap[status] ?? 'bg-slate-50 text-slate-700'}
                onOpenAppt={handleOpenAppt}
                privacyMode={privacyMode}
                collapsed={!isOpen}
                onToggleCollapsed={() =>
                  setOverviewOpenState((prev) => ({ ...prev, [openKey]: !prev[openKey] }))
                }
                onConfirm={(id) => confirmMutation.mutate(id)}
                onCheckIn={(id) => checkInMutation.mutate(id)}
                onCheckBack={(id) => checkBackMutation.mutate(id)}
                onCheckOut={(id) => checkOutMutation.mutate(id)}
                pendingId={
                  confirmMutation.isPending
                    ? (confirmMutation.variables ?? null)
                    : checkInMutation.isPending
                      ? (checkInMutation.variables ?? null)
                      : checkBackMutation.isPending
                        ? (checkBackMutation.variables ?? null)
                        : checkOutMutation.isPending
                          ? (checkOutMutation.variables ?? null)
                          : null
                }
                cardSettings={cardSettings}
                userTz={userTz}
                showStartDate={status === 'Waiting List'}
              />
              );
            })}
          </div>
        )}
      </div>

      <Modal
        isOpen={presetModal !== null}
        onClose={() => {
          setPresetModal(null);
          setPresetDraftName('');
          setPresetDraftShared(false);
        }}
        title={presetModal?.mode === 'edit' ? 'Update Scheduler Preset' : 'Save Scheduler Preset'}
        size="sm"
        footer={(
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setPresetModal(null);
                setPresetDraftName('');
                setPresetDraftShared(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePreset}
              disabled={createPresetMutation.isPending || updatePresetMutation.isPending}
            >
              {presetModal?.mode === 'edit' ? 'Update Preset' : 'Save Preset'}
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="Preset Name"
            value={presetDraftName}
            onChange={(event) => setPresetDraftName(event.target.value)}
            placeholder="Scheduler preset name"
            autoFocus
          />
          {canCreateSharedPreset && (
            <Checkbox
              checked={presetDraftShared}
              onChange={(event) => setPresetDraftShared(event.currentTarget.checked)}
              label="Shared preset"
              description="Shared presets are visible to other users on this account, like the legacy scheduler."
            />
          )}
        </div>
      </Modal>

      {/* -- Find Available Time modal --------------------------------------- */}
      <Modal
        isOpen={findAvailableOpen}
        onClose={() => setFindAvailableOpen(false)}
        title="Find Available Time"
        size="lg"
      >
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Select<number>
              label="Appointment Type"
              options={typeOptions.map((option) => ({ value: Number(option.id), label: option.label }))}
              value={findAvailableTypeId}
              onChange={(value) => setFindAvailableTypeId(value ?? undefined)}
              placeholder="Any type"
            />
            <Select<number>
              label="Provider / Resource"
              options={resourceOptions.map((option) => ({ value: Number(option.id), label: option.label }))}
              value={findAvailableResourceId}
              onChange={(value) => setFindAvailableResourceId(value ?? undefined)}
              placeholder="Any resource"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Additional resources for the availability search. The component
                takes a Set<number> + per-id toggle (not a controlled value
                array), so we adapt the underlying findAvailableResourceIds
                array on each interaction. The `label` is rendered above the
                dropdown because MultiSelectDropdown has no `label` prop. */}
            <div>
              <p className="mb-1 text-xs font-medium text-slate-700">Additional Resources (Multi)</p>
              <MultiSelectDropdown
                options={resourceOptions}
                selected={new Set(findAvailableResourceIds)}
                onToggle={(id) => setFindAvailableResourceIds((prev) =>
                  prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
                )}
                onSelectAll={(visibleIds) =>
                  setFindAvailableResourceIds((prev) =>
                    // While the dropdown is being searched it hands up only the
                    // matching ids, which merge into the current picks.
                    visibleIds
                      ? [...new Set([...prev, ...visibleIds])]
                      : resourceOptions.map((opt) => opt.id),
                  )
                }
                onClearAll={() => setFindAvailableResourceIds([])}
                placeholder="Any / add resources"
                searchable={resourceOptions.length > 10}
              />
            </div>
            <Select<number>
              label={isFacilityLocked ? 'Facility (Global override)' : 'Facility'}
              options={facilityOptions.map((option) => ({ value: Number(option.id), label: option.label }))}
              value={isFacilityLocked ? globalFacilityId : findAvailableFacilityId}
              onChange={(value) => setFindAvailableFacilityId(value ?? undefined)}
              placeholder="Any facility"
              disabled={isFacilityLocked}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              id="find-available-date-from"
              label="Date From"
              value={findAvailableDateFrom}
              onChange={setFindAvailableDateFrom}
            />
            <DatePicker
              id="find-available-date-to"
              label="Date To"
              value={findAvailableDateTo}
              onChange={setFindAvailableDateTo}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Daily Time Start"
              type="time"
              value={findAvailableTimeStart}
              onChange={(e) => setFindAvailableTimeStart(e.target.value)}
            />
            <Input
              label="Daily Time End"
              type="time"
              value={findAvailableTimeEnd}
              onChange={(e) => setFindAvailableTimeEnd(e.target.value)}
            />
            <Select<number>
              label="Duration"
              options={[
                { value: 15, label: '15 minutes' },
                { value: 30, label: '30 minutes' },
                { value: 45, label: '45 minutes' },
                { value: 60, label: '60 minutes' },
                { value: 90, label: '90 minutes' },
              ]}
              value={findAvailableDuration}
              onChange={(value) => setFindAvailableDuration(value ?? 30)}
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <Checkbox
              label="Include Weekends"
              checked={findAvailableIncludeWeekends}
              onChange={(e) => setFindAvailableIncludeWeekends(e.target.checked)}
            />
            {/* Legacy blocksOnlySelect: when off, availability search also returns
                open gaps outside blockTime windows. */}
            <Checkbox
              label="Block-time Only"
              description="Restrict results to configured block-time windows (legacy blocksOnlySelect)."
              checked={findAvailableBlocksOnly}
              onChange={(e) => setFindAvailableBlocksOnly(e.target.checked)}
            />
            <Input
              label="Type Group ID"
              type="number"
              value={findAvailableApptTypeGroupId ?? ''}
              onChange={(e) => setFindAvailableApptTypeGroupId(e.target.value ? Number(e.target.value) : undefined)}
              placeholder="Optional"
              className="w-32"
            />
          </div>

          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {findingAvailability && (
              <div className="p-4 flex items-center gap-2 text-sm text-slate-500">
                <Spinner size="sm" />
                Searching open slots...
              </div>
            )}

            {!findingAvailability && availableSlots.filter((slot) => slot.available).length === 0 && (
              <div className="p-4 text-sm text-slate-500">
                No block-compliant availability was found for this day.
              </div>
            )}

            {!findingAvailability && availableSlots.filter((slot) => slot.available).slice(0, 50).map((slot) => {
              const startDateObj = new Date(slot.startTime);
              const dateLabel = toLocaleDateStringInTz(startDateObj, [], { weekday: 'short', month: 'short', day: 'numeric', timeZone: userTz });
              const timeLabel = toLocaleTimeStringInTz(startDateObj, [], { hour: '2-digit', minute: '2-digit', timeZone: userTz });
              return (
                <button
                  key={slot.startTime}
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => {
                    setFindAvailableOpen(false);
                    // Seed the create modal with the slot's wall-clock time
                    // in the user's TZ; the modal then re-encodes using the
                    // facility TZ when the user submits.
                    setCreateDate(dateInTz(slot.startTime, userTz));
                    setCreateTime(timeInTz(slot.startTime, userTz));
                    setCreateOpen(true);
                  }}
                >
                  <div className="text-sm font-medium text-slate-800">
                    {dateLabel} &middot; {timeLabel}
                  </div>
                  <div className="text-xs text-slate-500">
                    {slot.warnings && slot.warnings.length > 0
                      ? `${slot.warnings.length} optional block warning${slot.warnings.length > 1 ? 's' : ''}`
                      : 'Within configured availability blocks'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      <BlockTimeModal
        open={blockOpen}
        onClose={() => {
          setBlockOpen(false);
          setEditBlockId(null);
          setEditBlockOccurrenceDate(undefined);
        }}
        onSaved={() => {
          setBlockOpen(false);
          setEditBlockId(null);
          setEditBlockOccurrenceDate(undefined);
          void qc.invalidateQueries({ queryKey: ['schedule'] });
        }}
        defaultStart={editBlockId == null ? blockRange.start : undefined}
        defaultEnd={editBlockId == null ? blockRange.end : undefined}
        editBlockId={editBlockId ?? undefined}
        occurrenceDate={editBlockOccurrenceDate}
        onConvertToAppointment={(seed) => {
          // Block modal closes itself; seed the create modal with the
          // legacy `convertBlockToAppt` payload.
          setBlockOpen(false);
          setEditBlockId(null);
          setEditBlockOccurrenceDate(undefined);
          setConvertSeed(seed);
          setCreateOpen(true);
        }}
      />

      {/* -- Print Schedule modal (legacy scheduler_print_options_* parity) -- */}
      <Modal
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        title="Print Schedule"
        size="lg"
      >
        <div className="p-4 space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={printScope === 'range'}
                onChange={() => setPrintScope('range')}
              />
              Date Range
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={printScope === 'waitlist'}
                onChange={() => setPrintScope('waitlist')}
              />
              Waitlist
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={printScope === 'documents'}
                onChange={() => setPrintScope('documents')}
              />
              Document Packet
            </label>
          </div>

          {/* P1-12: legacy scheduler_document_list.php — packets + individual
              documents grouped by category. */}
          {printScope === 'documents' && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Document</label>
              <select
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                value={printDocumentValue}
                onChange={(e) => setPrintDocumentValue(e.target.value)}
              >
                <option value="">— Select a document —</option>
                {printDocumentOptionsQuery.data?.packets.map((p) => (
                  <option key={`packet-${p.id}`} value={`${p.id}|packet`}>{p.name}</option>
                ))}
                {printDocumentOptionsQuery.data?.documents.map((d) => (
                  <option key={`document-${d.id}`} value={`${d.id}|document`}>
                    {d.categoryName ? `${d.categoryName} > ${d.title}` : d.title}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500">
                Generates the selected packet/document for every appointment currently
                visible on the calendar (legacy scheduler_document_converter.php).
              </p>
            </div>
          )}

          {printScope === 'range' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <DatePicker
                  id="print-start-date"
                  label="Start Date"
                  value={printStartDate}
                  onChange={setPrintStartDate}
                />
                <DatePicker
                  id="print-end-date"
                  label="End Date"
                  value={printEndDate}
                  onChange={setPrintEndDate}
                />
              </div>

              {/* Legacy parity: 21 independent printOptionN toggles grouped for legibility.
                  Mapping to legacy field names (DOM ids) is captured in PRINT_OPTION_META. */}
              {(['identity', 'appointment', 'medical', 'layout'] as const).map((group) => (
                <fieldset key={group} className="rounded border border-slate-200 p-2">
                  <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {PRINT_OPTION_GROUP_LABELS[group]}
                  </legend>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    {PRINT_OPTION_META.filter((meta) => meta.group === group).map((meta) => (
                      <Checkbox
                        key={meta.index}
                        label={`${meta.label} (printOption${meta.index})`}
                        description={meta.hint}
                        checked={!!printFlags[meta.index]}
                        onChange={(e) => setPrintFlags((prev) => ({ ...prev, [meta.index]: e.currentTarget.checked }))}
                      />
                    ))}
                  </div>
                </fieldset>
              ))}

              <div className="grid grid-cols-2 gap-2">
                <Checkbox
                  label="Include Cancelled"
                  checked={printIncludeCancelled}
                  onChange={(e) => setPrintIncludeCancelled(e.target.checked)}
                />
                <Checkbox
                  label="Include Blocks"
                  checked={printIncludeBlocks}
                  onChange={(e) => setPrintIncludeBlocks(e.target.checked)}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setPrintOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                try {
                  const fac = Array.from(effectiveSelectedFacilities);
                  const printOptions = PRINT_OPTION_META.reduce<SchedulerPrintOptions>((acc, meta) => {
                    acc[meta.key] = !!printFlags[meta.index];
                    return acc;
                  }, {} as SchedulerPrintOptions);
                  let result: { html: string } | null = null;
                  if (printScope === 'range') {
                    result = await printCalendarRange({
                      startDate: printStartDate,
                      endDate: printEndDate,
                      printOptions,
                      facilityIds: fac.length ? fac : undefined,
                      appointmentTypeIds: undefined,
                      resourceIds: undefined,
                      includeCancelled: printIncludeCancelled,
                      includeBlocks: printIncludeBlocks,
                    });
                  } else if (printScope === 'waitlist') {
                    result = await printWaitlist({
                      date: printStartDate,
                      facilityIds: fac.length ? fac : undefined,
                      appointmentTypeIds: undefined,
                      resourceIds: undefined,
                    });
                  } else {
                    // P1-12: legacy documentIDSelect value is `{id}|packet` or `{id}|document`.
                    const [idRaw, kindRaw] = printDocumentValue.split('|');
                    const docId = Number(idRaw);
                    const kind = kindRaw === 'packet' ? 'packet' : 'document';
                    if (!Number.isFinite(docId) || docId <= 0) {
                      toast.error('Please select a document.');
                      return;
                    }
                    // Scope: every appointment currently loaded on the calendar
                    // (legacy converter POSTed the same filter state as schedule print).
                    const appointmentIds = (appointments ?? [])
                      .filter((a) => !a.isBlock)
                      .map((a) => a.id);
                    if (appointmentIds.length === 0) {
                      toast.error('No appointments in current view to print for.');
                      return;
                    }
                    result = await printDocumentPacket({
                      kind,
                      id: docId,
                      appointmentIds,
                    });
                  }

                  const html = result?.html ?? '';
                  const win = window.open('', '_blank', 'width=900,height=700');
                  if (!win) {
                    toast.error('Unable to open print window. Pop-ups may be blocked.');
                    return;
                  }
                  win.document.open();
                  win.document.write(html);
                  win.document.close();
                  win.focus();
                  setTimeout(() => win.print(), 250);
                  setPrintOpen(false);
                } catch (err) {
                  toast.error(
                    'Failed to generate print',
                    err instanceof Error ? err.message : 'Please try again.',
                  );
                }
              }}
            >
              Print
            </Button>
          </div>
        </div>
      </Modal>

      {/* -- Detail modal ------------------------------------------------------ */}
      {detailAppt && (
        loadingDetail ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
            <div className="bg-white rounded-lg p-6 shadow-xl">
              <Spinner size="lg" />
            </div>
          </div>
        ) : fullDetailAppt ? (
          <AppointmentDetailModal
            isOpen
            onClose={() => { setDetailAppt(null); setFullDetailAppt(null); }}
            appointment={fullDetailAppt}
            privacyMode={privacyMode}
            onUpdated={(updated) => {
              setFullDetailAppt(updated);
              handleDetailUpdated();
            }}
            // P8 — Lazy-Copy parity with legacy `convertApptToAppt`. The
            // P8 — "Find Available Time" toolbar action. Closes the
            // detail modal, seeds the next-available form with the
            // current type/group/facility/duration, and opens the
            // legacy "Find Available" picker (`loadNextAvailPage`,
            // `scheduler-appt-details.php` L760).
            onFindAvailableTime={(seed) => {
              setDetailAppt(null);
              setFullDetailAppt(null);
              setFindAvailableTypeId(seed.appointmentTypeId);
              setFindAvailableApptTypeGroupId(seed.appointmentTypeGroupId);
              setFindAvailableFacilityId(seed.facilityId);
              setFindAvailableDuration(seed.duration && seed.duration > 0 ? seed.duration : 30);
              setFindAvailableResourceIds(seed.resourceIds);
              setFindAvailableOpen(true);
            }}
          />
        ) : null
      )}

      {/* -- Create modal ------------------------------------------------------ */}
      {createOpen && (
        <AppointmentCreateModal
          isOpen
          onClose={() => {
            setCreateOpen(false);
            setCreateDate(null);
            setCreateTime(null);
            setCreateResourceIds([]);
            setConvertSeed(null);
          }}
          onSuccess={handleCreateSuccess}
          defaultDate={
            convertSeed?.startTime
              ? dateInTz(convertSeed.startTime, userTz)
              : (createDate ?? undefined)
          }
          defaultStartTime={
            convertSeed?.startTime
              ? timeInTz(convertSeed.startTime, userTz)
              : (createTime ?? undefined)
          }
          defaultEndTime={convertSeed?.endTime ? timeInTz(convertSeed.endTime, userTz) : undefined}
          defaultResourceIds={convertSeed?.resourceIds ?? createResourceIds}
          defaultName={convertSeed?.title || undefined}
          defaultReason={convertSeed?.notes || undefined}
          defaultDuration={convertSeed?.duration && convertSeed.duration > 0 ? convertSeed.duration : undefined}
          defaultFacilityId={convertSeed?.facilityId && convertSeed.facilityId > 0 ? convertSeed.facilityId : undefined}
          defaultAppointmentTypeId={
            convertSeed?.appointmentTypeId && convertSeed.appointmentTypeId > 0
              ? convertSeed.appointmentTypeId
              : undefined
          }
          defaultAppointmentTypeGroupId={
            convertSeed?.appointmentTypeGroupId && convertSeed.appointmentTypeGroupId > 0
              ? convertSeed.appointmentTypeGroupId
              : undefined
          }
          defaultPatientId={
            convertSeed?.patientId && convertSeed.patientId > 0 ? convertSeed.patientId : undefined
          }
          defaultPatientPlanId={
            convertSeed?.planId && convertSeed.planId > 0 ? convertSeed.planId : undefined
          }
          privacyMode={privacyMode}
          onFindAvailableTime={(seed) => {
            setCreateOpen(false);
            setConvertSeed(null);
            setFindAvailableTypeId(seed.appointmentTypeId);
            setFindAvailableApptTypeGroupId(seed.appointmentTypeGroupId);
            setFindAvailableFacilityId(seed.facilityId);
            setFindAvailableDuration(seed.duration && seed.duration > 0 ? seed.duration : 30);
            setFindAvailableResourceIds(seed.resourceIds);
            setFindAvailableOpen(true);
          }}
        />
      )}
    </div>
  );
}
